/**
 * BOQEditPage - Standalone full-page BOQ editor (opens in new tab)
 * Features: Resizable columns, expandable descriptions, Estimated + Actual columns.
 * Table fills 100% page width. First (#) and last (actions) columns are fixed.
 */
import { useEffect, useState, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { VirtualRowGroup } from '../components/boq/VirtualRowGroup';
import { useExportGate } from '@/hooks/useExportGate';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { FeatureLocked } from '@/components/FeatureLocked';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
// Popover removed - GST section uses inline form now
import { Save, ArrowLeft, Plus, Trash2, ArrowUpDown, ArrowDownAZ, Percent, ChevronDown, ChevronUp, GripVertical, LayoutDashboard, Sparkles, Layers, X, ArrowRight, RefreshCw, Share2, Users, Mail, Link as LinkIcon, Clock, Copy, History, Check, FileText, FileSpreadsheet, Lock, Download, Pin, Edit3, TrendingUp, Settings2, ClipboardCheck, Expand, Search, Replace, Receipt } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CategoryCombobox } from '@/components/boq/CategoryCombobox';
import { PricingModelPreviewDialog } from '@/components/boq/PricingModelPreviewDialog';
import { CreditNotesDialog } from '@/components/boq/CreditNotesDialog';
import { ApplyPaymentsDialog } from '@/components/boq/ApplyPaymentsDialog';
import { RecordPaymentDialog } from '@/components/boq/RecordPaymentDialog';
import { CreditBadge } from '@/components/CreditBadge';
import { useAICredits } from '@/hooks/useAICredits';
import { ProfitabilityDialog } from '@/components/boq/ProfitabilityDialog';
import BOQTaskDateInputs from '@/components/boq/BOQTaskDateInputs';
import SnapshotDialog from '@/components/boq/SnapshotDialog';
import VendorQuoteDialog from '@/components/boq/VendorQuoteDialog';
import PublishValidityDialog from '@/components/boq/PublishValidityDialog';
import MergeItemsDialog from '@/components/boq/MergeItemsDialog';
// Fix 10: AI Task Creator (2k+ lines, presets, library chips) is code-split and mounted only when opened.
const AITaskCreatorDialog = lazy(() => import('@/components/boq/AITaskCreatorDialog'));
import { segmentFromProjectType } from '@/lib/project_types';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { promptUpgrade } from '@/components/billing/PlanUpgradeModal';
import ProjectDocsPanel from '@/components/boq/ProjectDocsPanel';
import AnnexureTable from '@/components/boq/AnnexureTable';
import QualityTierBadge from '@/components/QualityTierBadge';
import OTPConfigDialog from '@/components/boq/OTPConfigDialog';
import FindReplaceBar from '@/components/boq/FindReplaceBar';
import { ConsultancyInvoiceDialog } from '@/components/boq/ConsultancyInvoiceDialog';
import { highlightHtml } from '@/components/boq/HighlightedText';
import AIChatWidget from '@/components/AIChatWidget';
// iter-364 — Featured Brand Partner placement inside the Edit BOQ page.
import SponsorBadge from '@/components/SponsorBadge';

const UNIT_OPTIONS = ['No.s', 'Sq.ft', 'R.ft', 'L.ft', 'Kg', 'Ltr', 'Set', 'Pair', 'Box', 'Bag', 'Lot'];

// Resizable column keys — Description merged with Title (like Published BOQ)
const RESIZABLE_COLS = ['title', 'estQty', 'unit', 'vendorRate', 'estAmt', 'actQty', 'actRate', 'actAmt', 'billableSoFar', 'variance', 'gst', 'remarks'];

const DEFAULT_COL_WIDTHS = {
  title: 440,
  estQty: 80,           // iter-338z11: widened from 58 to fit 5-digit qty
  unit: 68,
  vendorRate: 82,
  estAmt: 92,
  actQty: 80,           // iter-338z11: widened from 58
  actRate: 82,
  actAmt: 92,
  billableSoFar: 100,
  variance: 85,
  gst: 52,
  remarks: 260,         // iter-338z11: widened from 200
};

function loadColWidths(worksheetId) {
  try {
    const saved = localStorage.getItem(`boq_col_widths_v2_${worksheetId}`);
    if (saved) return { ...DEFAULT_COL_WIDTHS, ...JSON.parse(saved) };
  } catch {}
  return { ...DEFAULT_COL_WIDTHS };
}

function saveColWidths(worksheetId, widths) {
  try { localStorage.setItem(`boq_col_widths_v2_${worksheetId}`, JSON.stringify(widths)); } catch {}
}

import { useTouchDragDrop } from '@/hooks/useTouchDragDrop';
import { computeConsultancyMilestone, applyMilestoneToBillableGrand, computeBoqTotals, pickRate, pickActualRate } from '@/utils/boqTotals';
import TaskProgressSlider from '@/components/TaskProgressSlider';

export default function BOQEditPage() {
  const { worksheetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { costFor: creditCostFor } = useAICredits();
  const [worksheet, setWorksheet] = useState(null);
  const [project, setProject] = useState(null);
  const { exportLocked, guardExport } = useExportGate();
  // Plan gating: returns true when allowed, else opens the upgrade prompt.
  const { can: planCan, loaded: planLoaded, required_plans: planReq } = usePlanPermissions();
  const planGate = (cap, message) => {
    if (!planLoaded || planCan(cap)) return true;
    promptUpgrade(cap, planReq?.[cap], message);
    return false;
  };
  const [editItems, setEditItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [discountType, setDiscountType] = useState('value');
  const [discountValue, setDiscountValue] = useState(0);
  // iter-315e: discount apply mode — 'before_tax' or 'after_tax' (per-BOQ override).
  const [discountApplyMode, setDiscountApplyMode] = useState('before_tax');
  const [freightPct, setFreightPct] = useState(0);
  const [labourHandlingPct, setLabourHandlingPct] = useState(0);
  const [consultancyPct, setConsultancyPct] = useState(0);
  // iter-285: Consultancy can be Fixed amount OR Percentage (mirror Discount UX).
  const [consultancyType, setConsultancyType] = useState('percentage');
  const [consultancyValue, setConsultancyValue] = useState(0);
  // iter-312p: Actual-side deductions. null = auto from project ledger, number = override.
  const [clientPaymentsOverride, setClientPaymentsOverride] = useState(null);
  const [creditNotesOverride, setCreditNotesOverride] = useState(null);
  const [creditNotesDialogOpen, setCreditNotesDialogOpen] = useState(false);
  // iter-312q: per-BOQ applied payments (canonical when non-empty).
  const [appliedPayments, setAppliedPayments] = useState([]); // [{payment_id, applied_amount, date, payment_mode, is_advance, ...}]
  const [applyPaymentsDialogOpen, setApplyPaymentsDialogOpen] = useState(false);
  // iter-312t: inline payment recording
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [recordPaymentMode, setRecordPaymentMode] = useState('client'); // 'client' | 'vendor'
  // iter-338i: Profitability dialog (Owner/Admin/Staff only)
  const [profitabilityOpen, setProfitabilityOpen] = useState(false);
  // iter-338k: shared task-dates map for per-row pickers — keyed by task_id
  const [taskDatesMap, setTaskDatesMap] = useState({});
  // iter-338m: AI Arrange busy flag for the Edit-BOQ toolbar button
  const [aiArranging, setAiArranging] = useState(false);
  // iter-338z16: AI Arrange date-range dialog state
  const [aiArrangeDialogOpen, setAiArrangeDialogOpen] = useState(false);
  const [aiArrangeStart, setAiArrangeStart] = useState('');
  const [aiArrangeEnd, setAiArrangeEnd] = useState('');
  // iter-386u-bw — Timeline anchors: pin a specific BOQ item as first/last task.
  const [aiArrangeFirstTaskId, setAiArrangeFirstTaskId] = useState('');
  const [aiArrangeLastTaskId, setAiArrangeLastTaskId] = useState('');
  const fetchTaskDates = useCallback(async () => {
    if (!worksheetId) return;
    try {
      const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/task-dates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setTaskDatesMap(r.data || {});
    } catch { /* silent — pickers just show empty */ }
  }, [worksheetId]);
  useEffect(() => { if (worksheetId) fetchTaskDates(); }, [worksheetId, fetchTaskDates]);
  // iter-338i++: auto-open via ?openProfitability=1 deep-link (from dashboard chart)
  useEffect(() => {
    if (new URLSearchParams(location.search).get('openProfitability') === '1') {
      setProfitabilityOpen(true);
    }
  }, [location.search]);

  // iter-338l+ / iter-386u-bq — scroll + flash-highlight a specific row when
  // arriving via ?focusItem={item_id | task_title}. Deep-links from the
  // Dashboard tiles pass the task title (URI-encoded) when we don't have a
  // stable item_id; we then match by data-task-title on the row.
  useEffect(() => {
    const focusItem = new URLSearchParams(location.search).get('focusItem');
    if (!focusItem || !worksheet?.items?.length) return;
    const t = setTimeout(() => {
      const decoded = decodeURIComponent(focusItem).trim().toLowerCase();
      let el =
        document.querySelector(`[data-row-id="${focusItem}"]`) ||
        document.querySelector(`[data-item-id="${focusItem}"]`);
      // iter-386u-bq — Fallback: match by task title (case-insensitive)
      if (!el) {
        const candidates = document.querySelectorAll('[data-task-title]');
        for (const c of candidates) {
          if ((c.getAttribute('data-task-title') || '').trim().toLowerCase() === decoded) {
            el = c;
            break;
          }
        }
      }
      // iter-386u-bq — Fallback: also scan visible row text
      if (!el) {
        const rows = document.querySelectorAll('[data-row-id]');
        for (const r of rows) {
          if ((r.textContent || '').trim().toLowerCase().includes(decoded)) {
            el = r;
            break;
          }
        }
      }
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('boq-row-focus-flash');
        setTimeout(() => el.classList.remove('boq-row-focus-flash'), 2400);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [location.search, worksheet?.items?.length]);
  const [termsConditions, setTermsConditions] = useState('');
  const [generatingTerms, setGeneratingTerms] = useState(false);
  // iter-372 — expandable full-size AI T&C editor
  const [termsExpandedOpen, setTermsExpandedOpen] = useState(false);
  // iter-372 — Excel-style Find & Replace bar (Ctrl+F / Ctrl+H)
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [consultancyInvoiceOpen, setConsultancyInvoiceOpen] = useState(false);
  // iter-375 — Snapshot for last Replace All (undo)
  const [findReplaceUndoSnapshot, setFindReplaceUndoSnapshot] = useState(null);
  // iter-380 — Live find-highlight state. Broadcast by FindReplaceBar
  // via `onQueryChange`; consumed by BOQ table row rendering and
  // AnnexureTable to wrap matched text in <mark>.
  const [findQuery, setFindQuery] = useState('');
  const [findCaseSensitive, setFindCaseSensitive] = useState(false);
  // iter-386u — Bulk Quality Tier update on the MAIN Edit BOQ table.
  // Reuses backend POST /api/boq-worksheets/{ws_id}/ai-bulk-tier-update.
  const [tierSelectedIds, setTierSelectedIds] = useState(new Set());
  const [tierRewriting, setTierRewriting] = useState(false);

  // iter-386u-bq — Rate History Diff (Ghost Lines).
  // When Primary Rate is flipped, snapshot every row's vendor/client rate so
  // we can show "was ₹X → now ₹Y" under the auto-recalculated cell.
  // Cleared when the user leaves the page or clicks a row's dismiss X.
  // iter-386u-bw — Rolling 3-step ghost history. Each entry is a rolling
  // series so the cell renders "Was X → Then Y → Now Z" per side.
  // Rate history is persisted server-side on Save only (item.rate_history, last 3).
  const [dismissedRateGhosts, setDismissedRateGhosts] = useState({});
  const dismissRateGhost = (editId) => setDismissedRateGhosts((prev) => ({ ...prev, [editId]: true }));
  const handleFindQueryChange = useCallback((q, cs) => {
    setFindQuery(q || '');
    setFindCaseSensitive(!!cs);
  }, []);

  // iter-372 — global keyboard shortcut for the find-replace bar. Bind on
  // window so users can Ctrl+F from anywhere on the Edit BOQ page. We
  // deliberately preventDefault to override the browser's native find bar
  // — users expect the in-BOQ search to take over.
  useEffect(() => {
    const handler = (e) => {
      const isCmd = e.ctrlKey || e.metaKey;
      if (isCmd && (e.key === 'f' || e.key === 'F' || e.key === 'h' || e.key === 'H')) {
        // Ignore when focus is in a text field that's part of an unrelated
        // dialog (e.g., AI Task Creator textarea) — otherwise we'd hijack
        // typing. We check the active element's data-testid prefix.
        const active = document.activeElement;
        const inDialog = active?.closest?.('[role="dialog"]');
        if (inDialog) return;
        e.preventDefault();
        if (planGate('can_find_replace_boq', 'Find & Replace is a Pro-Intelligence feature.')) setFindReplaceOpen(true);
      } else if (e.key === 'Escape' && findReplaceOpen) {
        setFindReplaceOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [findReplaceOpen]);
  const [boqCategories, setBoqCategories] = useState([]);
  const [editSort, setEditSort] = useState({ field: 'none', direction: 'asc' });
  const [bulkGst, setBulkGst] = useState('');
  const [bulkGstOpen, setBulkGstOpen] = useState(false);
  const [bulkGstType, setBulkGstType] = useState('percentage');
  // Merge from another estimate
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  // iter-338z: AI Task Creator dialog state
  const [aiTaskCreatorOpen, setAiTaskCreatorOpen] = useState(false);
  // iter-370 — per-line AI Refine dialog state
  const [aiRefineOpen, setAiRefineOpen] = useState(false);
  const [aiRefineTargetIdx, setAiRefineTargetIdx] = useState(null);
  // iter-371 — OTP Gate + view limit admin config dialog
  const [otpConfigOpen, setOtpConfigOpen] = useState(false);
  const [pricingPreviewOpen, setPricingPreviewOpen] = useState(false);
  const [availableEstimates, setAvailableEstimates] = useState([]);
  const [mergeEstimateId, setMergeEstimateId] = useState('');
  const [mergeCreateTasks, setMergeCreateTasks] = useState(true);
  const [merging, setMerging] = useState(false);

  // Pre-fill bulk GST from items if they share a common GST percentage
  useEffect(() => {
    if (editItems.length > 0 && bulkGst === '') {
      const gstValues = editItems.filter(i => i.item_type !== 'header' && !i.is_discount).map(i => i.gst_percentage || 0);
      const uniqueGst = [...new Set(gstValues)];
      if (uniqueGst.length === 1 && uniqueGst[0] > 0) {
        setBulkGst(String(uniqueGst[0]));
      }
    }
    // eslint-disable-next-line
  }, [editItems.length]); // only run when items count changes
  const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS);
  const [expandedDescs, setExpandedDescs] = useState(new Set());
  const [expandedEditCats, setExpandedEditCats] = useState({}); // track collapsed categories
  const [activeLegendFilter, setActiveLegendFilter] = useState(null); // null = show all, or category code
  const resizingRef = useRef(null);
  const [dragRowIdx, setDragRowIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // iter-313: Auto-hide controls panel — slim bar always visible (Project · BOQ · Save · Toggle);
  // full toolbar shows on hover near top OR when pinned. Persists pin choice per-user.
  const [controlsHover, setControlsHover] = useState(false);
  const [controlsPinned, setControlsPinned] = useState(() => {
    try { return localStorage.getItem('boq:edit:controlsPinned') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('boq:edit:controlsPinned', controlsPinned ? '1' : '0'); } catch {}
  }, [controlsPinned]);

  useEffect(() => { fetchWorksheet(); fetchBoqCategories(); }, [worksheetId]);
  useEffect(() => { if (worksheetId) setColWidths(loadColWidths(worksheetId)); }, [worksheetId]);

  // Column resize (mouse + touch support)
  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    resizingRef.current = { colKey, startX: clientX, startWidth: colWidths[colKey] };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [colWidths]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      if (e.cancelable) e.preventDefault(); // Prevent scroll during resize on touch
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const { colKey, startX, startWidth } = resizingRef.current;
      setColWidths(prev => ({ ...prev, [colKey]: Math.max(20, startWidth + clientX - startX) }));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths(prev => { saveColWidths(worksheetId, prev); return prev; });
      resizingRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [worksheetId]);

  const toggleDescExpanded = (editId) => {
    setExpandedDescs(prev => {
      const next = new Set(prev);
      next.has(editId) ? next.delete(editId) : next.add(editId);
      return next;
    });
  };

  // Drag-to-reorder row handlers (mouse)
  const handleDragStart = (e, idx) => {
    setDragRowIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx);
    e.target.closest('tr').style.opacity = '0.5';
  };
  const handleDragEnd = (e) => {
    e.target.closest('tr').style.opacity = '1';
    setDragRowIdx(null);
    setDragOverIdx(null);
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragRowIdx === null || dragRowIdx === dropIdx) { setDragOverIdx(null); return; }
    reorderItems(dragRowIdx, dropIdx);
    setDragRowIdx(null);
    setDragOverIdx(null);
  };

  // Shared reorder logic for both mouse and touch
  const reorderItems = useCallback((fromIdx, toIdx) => {
    setEditItems(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  }, []);

  // iter-370 — Insert-at-Position: user types a target row number in the
  // small input beside the drag handle and hits Enter → the item is moved
  // to that 1-based position (clamped to [1, N]). Complements drag+drop
  // for long BOQs where dragging from the bottom to row #3 is tedious.
  const handleInsertAtPosition = useCallback((fromIdx, targetOneBased) => {
    setEditItems(prev => {
      const N = prev.length;
      const to = Math.max(0, Math.min(N - 1, Number(targetOneBased) - 1));
      if (fromIdx === to || Number.isNaN(to)) return prev;
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
    toast.success(`Moved to row ${targetOneBased}`);
  }, []);

  // Touch drag-and-drop for iPad/tablet
  const { touchHandlers, dragIdx: touchDragIdx, overIdx: touchOverIdx } = useTouchDragDrop({ onReorder: reorderItems });

  const fetchWorksheet = async () => {
    try {
      setLoading(true);
      // Fix 10: fetch only this worksheet (was: every BOQ in the org, then .find()).
      const response = await axios.get(`${API}/boq-worksheets/${worksheetId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const ws = response.data;
      if (ws) {
        setWorksheet(ws);
        // iter-288: Fetch project details so we can surface the project
        // name in the Edit BOQ header (parity with Published BOQ views).
        if (ws.project_id) {
          axios.get(`${API}/projects/${ws.project_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }).then(r => setProject(r.data)).catch(() => setProject(null));
        }
        setEditItems((ws.items || []).map((item, idx) => ({
          ...item,
          // iter-386u-ac — always assign a stable, RANDOM _editId (no Date.now
          // ties in same-ms bulk adds). Preserve backend id/task_id when the
          // row is already persisted so save reconciliation still matches.
          task_title: item.task_title ?? item.title ?? (item.description || 'Unnamed Item').slice(0, 100),
          _editId: item.id || item.task_id || `row_${idx}_${Math.random().toString(36).slice(2, 10)}`,
          gst_percentage: item.gst_percentage !== undefined ? item.gst_percentage : 0,
        })));
        setDiscountType(ws.discount_type || 'value');
        setDiscountValue(ws.discount_value || 0);
        setDiscountApplyMode(ws.discount_apply_mode || 'before_tax');
        // Backend's canonical fields are `*_pct`; older rows may have `*_percentage`.
        setFreightPct(ws.freight_pct ?? ws.freight_percentage ?? 0);
        setLabourHandlingPct(ws.labour_handling_pct ?? ws.labour_handling_percentage ?? 0);
        setConsultancyPct(ws.consultancy_pct ?? ws.consultancy_percentage ?? 0);
        setConsultancyType(ws.consultancy_type || 'percentage');
        setConsultancyValue(ws.consultancy_value || 0);
        // iter-312p: hydrate Actual-side deduction overrides (null = auto)
        setClientPaymentsOverride(
          ws.client_payments_applied_override != null ? +ws.client_payments_applied_override : null
        );
        setCreditNotesOverride(
          ws.credit_notes_applied_override != null ? +ws.credit_notes_applied_override : null
        );
        // iter-312q: hydrate per-BOQ applied payments (canonical when non-empty)
        setAppliedPayments(ws.applied_payments_detail || ws.applied_payments || []);
        // iter-286: hydrate visibility / disable toggles
        setPackageGloballyDisabled(!!ws.package_savings_disabled);
        setShowConsultancyToVendor(!!ws.show_consultancy_to_vendor);
        // iter-304: hydrate Client PDF variance toggle
        setShowActualVarianceInClientPdf(!!ws.show_actual_variance_in_client_pdf);
        // iter-377: hydrate Annexure visibility toggles (client + vendor).
        setAnnexureShowToClient(!!ws.annexure_show_to_client);
        setAnnexureShowToVendor(!!ws.annexure_show_to_vendor);
        setTermsConditions(ws.terms_conditions || '');
      } else {
        toast.error('BOQ worksheet not found');
      }
    } catch { toast.error('Failed to load BOQ worksheet'); }
    finally { setLoading(false); }
  };

  const fetchBoqCategories = async () => {
    try {
      const resp = await axios.get(`${API}/boq-categories/for-tasks`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setBoqCategories(resp.data || []);
    } catch {}
  };

  // Build category color map from boqCategories
  const categoryColorMap = useMemo(() => {
    const map = {};
    (boqCategories || []).forEach(cat => {
      if (cat.color) {
        map[cat.group || cat.code] = cat.color;
        if (cat.main_category_id) map[cat.main_category_id] = cat.color;
      }
    });
    return map;
  }, [boqCategories]);

  const mainCategoryOptions = useMemo(() => boqCategories.map(cat => ({
    value: cat.main_category_id || cat.id,
    label: `${cat.group} - ${cat.group_name}`,
    code: cat.group,
    name: cat.group_name,
  })), [boqCategories]);

  const getSubCategoryOptions = (mainCategoryId) => {
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === mainCategoryId);
    if (!mainCat) return [];
    return (mainCat.categories || mainCat.sub_categories || []).map(sub => ({
      value: sub.id || sub.code, label: `${sub.code} - ${sub.name}`, code: sub.code, name: sub.name,
    }));
  };

  // Pricing model helpers
  // V→C: vendor_rate is editable, forward multiply
  // C→V: client_rate is editable, reverse DIVIDE (not subtract)
  const isCtV = worksheet?.pricing_model === 'client_to_vendor';
  const getBaseRate = (item) => {
    if (isCtV) return parseFloat(item.client_rate || item.rate || 0);
    return parseFloat(item.vendor_rate || item.rate || 0);
  };

  const handleVendorRateChange = (index, rateValue) => {
    const pct1 = worksheet?.admin_markup_percentage || 20;
    const pct2 = worksheet?.client_markup_percentage || 20;
    let vendorRate, adminRate, clientRate;
    if (isCtV) {
      // C→V: client_rate is the input. DIVIDE to get admin, then vendor.
      clientRate = rateValue;
      adminRate = Math.round((clientRate / (1 + pct2 / 100)) * 100) / 100;
      vendorRate = Math.round((adminRate / (1 + pct1 / 100)) * 100) / 100;
    } else {
      // V→C: vendor_rate is the input. MULTIPLY to get admin, then client.
      vendorRate = rateValue;
      adminRate = Math.round(vendorRate * (1 + pct1 / 100) * 100) / 100;
      clientRate = Math.round(adminRate * (1 + pct2 / 100) * 100) / 100;
    }
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      return {
        ...item, rate: rateValue, vendor_rate: vendorRate,
        admin_rate: adminRate, client_rate: clientRate,
        amount: (item.quantity || 0) * rateValue,
      };
    }));
  };

  // iter-321 / iter-337: Per-row task progress update.
  // • If the item already has a persisted `id`, call the dedicated PATCH endpoint
  //   so the change syncs to tasks + draft RA bills + vendor submissions instantly.
  // • If the item is BRAND NEW (only `_editId`, no `id` yet — e.g. Additional Work
  //   items added post-RA-confirm before the first BOQ save), update local
  //   editItems state IMMEDIATELY so the next "Save BOQ" click persists the
  //   correct task_progress as part of the items array. Without this, the slider
  //   value was silently discarded on save → reverted to 0% on reload (Bug 2).
  const handleItemProgressUpdate = async (itemId, index, next) => {
    if (!worksheet?.id) return;
    const nextProgress = (next && typeof next === 'object') ? next.progress : Number(next);
    const nextStatus = (next && typeof next === 'object') ? next.status : undefined;
    // Optimistically merge so the slider reflects the new value either way.
    setEditItems(prev => prev.map((it, i) => i === index ? {
      ...it,
      task_progress: nextProgress != null ? nextProgress : it.task_progress,
      task_progress_status: nextStatus || it.task_progress_status,
      task_progress_source: 'admin',
      task_progress_updated_at: new Date().toISOString(),
    } : it));
    if (!itemId) {
      // New item — no server PATCH yet. Local state holds the value; it will be
      // persisted on the next BOQ save (PUT preserves task_progress).
      toast.success('Progress staged — click Save to persist', { duration: 2500 });
      return;
    }
    try {
      const res = await axios.patch(`${API}/boq-worksheets/${worksheet.id}/items/${itemId}/progress`, next);
      const data = res.data || {};
      setEditItems(prev => prev.map((it, i) => i === index ? {
        ...it,
        task_progress: data.task_progress ?? it.task_progress,
        task_progress_status: data.task_progress_status ?? it.task_progress_status,
        task_progress_source: data.task_progress_source || 'admin',
        task_progress_updated_by: data.task_progress_updated_by,
        task_progress_updated_at: data.task_progress_updated_at,
      } : it));
      toast.success('Progress updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update progress');
    }
  };

  const handleItemChange = (index, field, value) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const n = { ...item, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const pct1 = worksheet?.admin_markup_percentage || 20;
        const pct2 = worksheet?.client_markup_percentage || 20;
        // iter-386u-bh — Lump-sum (from mismatched-unit specs) is added on top
        const lumpV = Number(n.lump_sum_vendor || 0);
        const lumpC = Number(n.lump_sum_client || 0);
        if (isCtV) {
          const cr = n.rate || 0;
          n.client_rate = cr;
          n.admin_rate = Math.round((cr / (1 + pct2 / 100)) * 100) / 100;
          n.vendor_rate = Math.round((n.admin_rate / (1 + pct1 / 100)) * 100) / 100;
          n.amount = (n.quantity || 0) * cr + lumpC;
        } else {
          const vr = n.rate || 0;
          n.vendor_rate = vr;
          n.admin_rate = Math.round(vr * (1 + pct1 / 100) * 100) / 100;
          n.client_rate = Math.round(n.admin_rate * (1 + pct2 / 100) * 100) / 100;
          n.amount = (n.quantity || 0) * vr + lumpV;
        }
      }
      // iter-306b: cascade actual_rate the same way as estimated rate AND
      // keep `actual_rate` mirroring the CURRENT pricing-model tier (so a
      // re-load in the same model shows the exact value the user typed).
      // V→C: typed value = vendor; C→V: typed value = client.
      if (field === 'actual_rate') {
        const pct1 = worksheet?.admin_markup_percentage || 20;
        const pct2 = worksheet?.client_markup_percentage || 20;
        const ar = value || 0;
        if (isCtV) {
          n.actual_client_rate = ar;
          n.actual_admin_rate = Math.round((ar / (1 + pct2 / 100)) * 100) / 100;
          n.actual_vendor_rate = Math.round((n.actual_admin_rate / (1 + pct1 / 100)) * 100) / 100;
        } else {
          n.actual_vendor_rate = ar;
          n.actual_admin_rate = Math.round(ar * (1 + pct1 / 100) * 100) / 100;
          n.actual_client_rate = Math.round(n.actual_admin_rate * (1 + pct2 / 100) * 100) / 100;
        }
        // n.actual_rate already set to the typed value above (field === 'actual_rate').
      }
      if (['gst_percentage', 'quantity', 'rate'].includes(field)) {
        const br = isCtV ? (n.client_rate ?? n.rate ?? 0) : (n.vendor_rate ?? n.rate ?? 0);
        // iter-386u-bh — Include lump-sum in the GST-eligible subtotal.
        const lump = isCtV ? Number(n.lump_sum_client || 0) : Number(n.lump_sum_vendor || 0);
        const amt = (n.quantity || 0) * br + lump;
        n.gst_amount = amt * (n.gst_percentage || 0) / 100;
        n.amount_with_gst = amt + n.gst_amount;
      }
      return n;
    }));
  };

  const handleMainCategoryChange = (index, mainCatId) => {
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === mainCatId);
    setEditItems(prev => prev.map((item, i) => i !== index ? item : {
      ...item, main_category_id: mainCatId,
      main_category_code: mainCat?.group || '', main_category_name: mainCat?.group_name || '',
      sub_category_id: null, sub_category_code: '', sub_category_name: '',
    }));
  };

  const handleSubCategoryChange = (index, subCatId) => {
    const item = editItems[index];
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === item.main_category_id);
    const subCat = (mainCat?.categories || mainCat?.sub_categories || []).find(s => (s.id || s.code) === subCatId);
    setEditItems(prev => prev.map((it, i) => i !== index ? it : {
      ...it, sub_category_id: subCatId, sub_category_code: subCat?.code || '', sub_category_name: subCat?.name || '',
    }));
  };

  const handleDeleteItem = (index) => setEditItems(prev => prev.filter((_, i) => i !== index));

  // === Merge from another estimate (MK + MW full-home BOQ) ===
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  // iter-315e: who is editing? — used to gate the "Revert RA Bill" button to Owner only.
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const isRaConfirmed = !!worksheet?.ra_bill_confirmed_at;

  // iter-334: After RA confirm, "Additional Work" items (added AFTER the
  // confirm date) stay FULLY editable. Existing items only have their
  // Estimated columns locked; Actual side + task_progress remain editable.
  // This helper is used by every <Input readOnly>/disabled binding below.
  const isItemFrozen = (item) => {
    if (!isRaConfirmed) return false;
    if (item?.added_after_ra_confirm) return false;
    return true;
  };
  const openMergeModal = async () => {
    setMergeModalOpen(true);
    try {
      const res = await axios.get(`${API}/estimates?project_id=${worksheet.project_id}`, { headers: authHeaders });
      const list = Array.isArray(res.data) ? res.data : [];
      // Exclude estimates already merged into this worksheet
      const alreadyIn = new Set((editItems || []).map(i => i.estimate_id).filter(Boolean));
      setAvailableEstimates(list.filter(e => e.id && !alreadyIn.has(e.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load estimates');
    }
  };

  const performMerge = async () => {
    if (!mergeEstimateId) { toast.error('Pick an estimate to merge'); return; }
    try {
      setMerging(true);
      const res = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/merge-estimate`,
        { estimate_id: mergeEstimateId, create_tasks: mergeCreateTasks },
        { headers: authHeaders }
      );
      toast.success(res.data.message || 'Merged');
      setMergeModalOpen(false);
      setMergeEstimateId('');
      // Reload worksheet
      const w = await axios.get(`${API}/boq-worksheets/${worksheetId}`, { headers: authHeaders });
      setWorksheet(w.data);
      setEditItems((w.data.items || []).map(it => ({ ...it, _editId: it.id || `row_${Math.random()}` })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge estimate');
    } finally { setMerging(false); }
  };

  // iter-338e (Msg-680 round-5, opt 1b): Append the new row at the END of the
  // last section (so it sits right below the most recently opened amber
  // header), reset any active sort that would hide the new row, auto-scroll
  // to it and briefly highlight it. Saves the user from scrolling to the top
  // to find the new row on long BOQs.
  const _flashRow = (rowId) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${rowId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('bg-amber-100', 'transition-colors', 'duration-700');
        setTimeout(() => el.classList.remove('bg-amber-100'), 1800);
      }
    }, 80);
  };
  // Insert at end of the LAST section (i.e., immediately after the final
  // item under the bottom-most header). If no headers exist, append at the
  // end. Returns the new array AND the index where the new row landed.
  const _insertAtEndOfLastSection = (prev, newRow) => {
    // Find the index of the LAST header. New row inserts after it.
    let lastHeaderIdx = -1;
    for (let i = prev.length - 1; i >= 0; i--) {
      if (prev[i].item_type === 'header') { lastHeaderIdx = i; break; }
    }
    if (lastHeaderIdx === -1) {
      // No headers — append at end.
      return [...prev, newRow];
    }
    // Append at very end (after all items under the last header — they're
    // all already below `lastHeaderIdx` so appending = end of last section).
    return [...prev, newRow];
  };

  const handleAddItem = () => {
    // iter-338e: reset sort so the new row is visible at its insertion point
    if (editSort.field !== 'none' || editSort.direction !== 'asc') {
      setEditSort({ field: 'none', direction: 'asc' });
    }
    const newId = `new_${Date.now()}`;
    setEditItems(prev => _insertAtEndOfLastSection(prev, {
      _editId: newId, task_title: '', description: '', quantity: 1, unit: 'Sq.ft',
      rate: 0, vendor_rate: 0, admin_rate: 0, client_rate: 0, amount: 0,
      gst_percentage: 0, actual_quantity: null, actual_rate: null,
      main_category_id: null, sub_category_id: null,
      // iter-334: tag items added AFTER RA Bill is confirmed so they remain editable
      added_after_ra_confirm: !!worksheet?.ra_bill_confirmed_at,
    }));
    _flashRow(newId);
  };

  const handleAddHeaderRow = () => {
    const headerName = prompt('Enter header name (e.g., "Master Bedroom", "Living Room"):');
    if (!headerName?.trim()) return;
    if (editSort.field !== 'none' || editSort.direction !== 'asc') {
      setEditSort({ field: 'none', direction: 'asc' });
    }
    const newId = `header_${Date.now()}_${Math.random().toString(36).substr(2,5)}`;
    // iter-386u-ac — Alpha-Header sequencing (A, B, ... Z, AA, AB, ...).
    const nextCode = _nextHeaderAlphaCode(editItems);
    setEditItems(prev => _insertAtEndOfLastSection(prev, {
      _editId: newId,
      item_type: 'header',
      task_title: headerName.trim(),
      header_code: nextCode,
      boq_header_code: nextCode,
      description: '', quantity: 0, unit: '', rate: 0, amount: 0,
      gst_percentage: 0, vendor_rate: 0, admin_rate: 0, client_rate: 0,
      main_category_id: null, sub_category_id: null,
      // iter-334: tag headers added AFTER RA Bill is confirmed so they're not frozen
      added_after_ra_confirm: !!worksheet?.ra_bill_confirmed_at,
    }));
    _flashRow(newId);
  };

  // iter-386u-ac — Alpha-Header helpers: A, B, ... Z, AA, AB, ..., ZZ.
  const _alphaFromIndex = (idx) => {
    // 0 → 'A', 25 → 'Z', 26 → 'AA', 27 → 'AB', ...
    let s = '';
    let n = idx;
    while (n >= 0) {
      s = String.fromCharCode(65 + (n % 26)) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  };
  const _nextHeaderAlphaCode = (items) => {
    const usedCodes = new Set(
      (items || [])
        .filter((it) => it && (it.item_type === 'header' || it.is_header))
        .map((it) => String(it.header_code || it.boq_header_code || '').toUpperCase())
        .filter(Boolean)
    );
    for (let i = 0; i < 26 * 27; i++) {
      const code = _alphaFromIndex(i);
      if (!usedCodes.has(code)) return code;
    }
    return String(Date.now()); // fallback — shouldn't happen
  };

  // iter-386u-am — invert _alphaFromIndex so we can compare positions.
  // Accepts up to 3-letter codes: 'A'→0, 'Z'→25, 'AA'→26, 'AB'→27, 'AAA'→702.
  const _indexFromAlpha = (code) => {
    const c = String(code || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (!c) return -1;
    let n = 0;
    for (let i = 0; i < c.length; i++) {
      n = n * 26 + (c.charCodeAt(i) - 64); // 'A' = 1 in this base-26
    }
    return n - 1; // shift back so 'A' → 0
  };

  // iter-386u-am — Smart header re-sequencing.
  // When a user rewrites a header's alpha-code (F → C), we don't just rename:
  // we INSERT this header at position `newCode` and push every subsequent
  // header down by exactly one alpha slot (C→D, D→E, ..., Z→AA, AA→AB). Tasks
  // tagged to any bumped header are remapped too so [Header]→[Tasks] grouping
  // survives the shift. Finally we run sortByHeader() so the UI reflects the
  // new order instantly.
  const resequenceHeaderCode = (headerEditId, oldCodeRaw, newCodeRaw) => {
    const oldCode = String(oldCodeRaw || '').toUpperCase().replace(/[^A-Z]/g, '');
    const newCode = String(newCodeRaw || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (!newCode || newCode === oldCode) return;

    const newIdx = _indexFromAlpha(newCode);
    if (newIdx < 0) return;
    const oldIdx = _indexFromAlpha(oldCode); // may be -1 if header had no code

    // Reset display sort so title/date sorts don't fight the shift.
    setEditSort({ field: 'none', direction: 'asc' });

    // Build the remap: for every other header whose current index is between
    // newIdx and (oldIdx - 1 if oldIdx > newIdx, else Infinity), bump by +1.
    // Simpler & bullet-proof: if a different header currently occupies newIdx
    // (or any higher slot without a gap), shift the whole contiguous block up.
    setEditItems((prev) => {
      const items = [...prev];
      // Snapshot original codes for every header (except the one being edited).
      const otherHeaders = items
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => it && (it.item_type === 'header' || it.is_header) && it._editId !== headerEditId);

      // Step 1: figure out which headers need to shift.
      //  – Any header currently at slot >= newIdx AND (if oldIdx>=0) < oldIdx shifts +1.
      //  – If oldIdx < 0 or oldIdx < newIdx (moving down), every header at >= newIdx shifts +1.
      const shiftMap = new Map(); // idx → +delta
      const isMoveUp = oldIdx > newIdx; // e.g. F(5) → C(2) is a move-UP the list.
      for (const { it, idx } of otherHeaders) {
        const currIdx = _indexFromAlpha(String(it.header_code || it.boq_header_code || ''));
        if (currIdx < 0) continue;
        if (isMoveUp) {
          // Moving F → C: any header that currently sits between C and F-1 gets bumped +1.
          if (currIdx >= newIdx && currIdx < oldIdx) shiftMap.set(idx, +1);
        } else {
          // Moving C → F (or fresh assign): any header at >= newIdx shifts +1 to
          // make room. (Existing headers beyond newIdx are pushed down.)
          if (currIdx >= newIdx) shiftMap.set(idx, +1);
        }
      }

      // If nothing to shift, this is a simple rename.
      const remap = {}; // oldHeaderCode → newHeaderCode (for row remap)
      for (const [idx, delta] of shiftMap.entries()) {
        const hdr = items[idx];
        const prevCode = String(hdr.header_code || hdr.boq_header_code || '').toUpperCase();
        const nextCode = _alphaFromIndex(_indexFromAlpha(prevCode) + delta);
        remap[prevCode] = nextCode;
        items[idx] = { ...hdr, header_code: nextCode, boq_header_code: nextCode };
      }

      // Set the edited header's new code.
      const editedIdx = items.findIndex((it) => it._editId === headerEditId);
      if (editedIdx >= 0) {
        items[editedIdx] = { ...items[editedIdx], header_code: newCode, boq_header_code: newCode };
        // If the OLD code was legitimate and different, ensure any row still
        // tagged with the old code follows to the new code. (Rows tagged to a
        // bumped header code are already handled by `remap` above.)
        if (oldCode) remap[oldCode] = newCode;
      }

      // Step 2: remap non-header rows that carried a shifted (or edited) code.
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it || it.item_type === 'header' || it.is_header) continue;
        const tag = String(it.boq_header_code || it.header_code || '').toUpperCase();
        if (tag && remap[tag]) {
          items[i] = { ...it, boq_header_code: remap[tag], header_code: remap[tag] };
        }
      }

      return items;
    });

    // Step 3: after state has settled, run the alpha group-sort so the DOM
    // reflects the new order.
    setTimeout(() => {
      try { sortByHeader(); } catch { /* ignore */ }
      // iter-386u-an — persist the new header_code map to Mongo so a reload
      // no longer wipes the alphabetical order. Fire-and-forget; a Save button
      // click will still overwrite this anyway.
      try {
        // Read state fresh via functional setter, then POST the delta.
        setEditItems((current) => {
          const updates = current
            .filter((it) => it && (it.item_type === 'header' || it.is_header) && it.id)
            .map((it) => ({ item_id: it.id, header_code: String(it.header_code || it.boq_header_code || '') }))
            .filter((u) => u.header_code);
          if (updates.length) {
            axios.patch(
              `${API}/boq-worksheets/${worksheetId}/header-codes`,
              { updates },
              { headers: authHeaders },
            ).catch(() => { /* silent — Save still works */ });
          }
          return current;
        });
      } catch { /* ignore */ }
    }, 0);
  };

  // Backfill alpha codes on legacy headers that don't have one yet, then
  // reorder the whole worksheet so every line item follows its header.
  //
  // iter-386u-ak: Group tasks by their STORED `header_code` (not by physical
  // position). This is what makes rename-B-to-A actually reorder the section:
  // every task is tagged the moment it lands under a header, so tasks travel
  // with their header even after alphabetical reshuffling.
  const sortByHeader = () => {
    if (!editItems?.length) return;
    // Reset display sort so the reshuffle isn't overridden by title/date sorting.
    setEditSort({ field: 'none', direction: 'asc' });

    // 1) Assign alpha codes to any header row missing one.
    const backfilled = [];
    let nextIdx = 0;
    const takenCodes = new Set(
      editItems
        .filter((it) => it && (it.item_type === 'header' || it.is_header))
        .map((it) => String(it.header_code || it.boq_header_code || '').toUpperCase())
        .filter(Boolean)
    );
    for (const it of editItems) {
      if (it && (it.item_type === 'header' || it.is_header)) {
        const existing = String(it.header_code || it.boq_header_code || '').toUpperCase();
        if (!existing) {
          while (takenCodes.has(_alphaFromIndex(nextIdx))) nextIdx += 1;
          const code = _alphaFromIndex(nextIdx);
          takenCodes.add(code);
          backfilled.push({ ...it, header_code: code, boq_header_code: code });
          nextIdx += 1;
        } else {
          backfilled.push(it);
        }
      } else {
        backfilled.push(it);
      }
    }

    // 2) Stamp every non-header row with its "current" header_code so the
    //    row's tag is authoritative even if we later reorder headers.
    //    Rule: a row's assigned code = it.boq_header_code / it.header_code if
    //    present; otherwise fall back to the nearest preceding header's code.
    const stamped = [];
    let currentCode = '';
    for (const it of backfilled) {
      if (it && (it.item_type === 'header' || it.is_header)) {
        currentCode = String(it.header_code || it.boq_header_code || '').toUpperCase();
        stamped.push(it);
      } else {
        const tagged = String(it.boq_header_code || it.header_code || '').toUpperCase();
        const code = tagged || currentCode || '';
        stamped.push({ ...it, boq_header_code: code, header_code: code });
      }
    }

    // 3) Group by stamped header_code so tasks TRAVEL with their tag.
    const headersByCode = new Map();   // CODE → header row (last wins if dup)
    const groupsByCode = new Map();    // CODE → items[]
    const noHeaderGroup = [];
    for (const it of stamped) {
      if (it && (it.item_type === 'header' || it.is_header)) {
        const code = String(it.header_code || it.boq_header_code || '').toUpperCase();
        headersByCode.set(code, it);
        if (!groupsByCode.has(code)) groupsByCode.set(code, []);
      } else {
        const code = String(it.boq_header_code || it.header_code || '').toUpperCase();
        if (!code || !headersByCode.has(code)) {
          noHeaderGroup.push(it);
        } else {
          if (!groupsByCode.has(code)) groupsByCode.set(code, []);
          groupsByCode.get(code).push(it);
        }
      }
    }

    // 4) Emit groups in alphabetical order of their code (A, B, …, AA, AB, …).
    const codes = [...groupsByCode.keys()].sort((a, b) => {
      if (a.length !== b.length) return a.length - b.length;
      return a.localeCompare(b);
    });
    const rebuilt = [];
    for (const code of codes) {
      const hdr = headersByCode.get(code);
      if (hdr) rebuilt.push(hdr);
      for (const li of groupsByCode.get(code)) rebuilt.push(li);
    }
    // 5) Items with no header stay at the end.
    for (const li of noHeaderGroup) rebuilt.push(li);
    setEditItems(rebuilt);
    toast.success('BOQ sorted by header sequence');
  };

  // Reassign a line item to a specific header (used by the Header dropdown).
  const assignItemToHeader = (rowEditId, targetHeaderEditId) => {
    setEditItems((prev) => {
      const row = prev.find((it) => it._editId === rowEditId);
      if (!row) return prev;
      if (targetHeaderEditId === '__none__') {
        // Move to the "no header" section at the very end.
        const remaining = prev.filter((it) => it._editId !== rowEditId);
        return [...remaining, { ...row, boq_header_code: '', header_code: '' }];
      }
      const targetHeader = prev.find((it) => it._editId === targetHeaderEditId);
      if (!targetHeader) return prev;
      const code = String(targetHeader.header_code || targetHeader.boq_header_code || '').toUpperCase();
      const movedRow = { ...row, boq_header_code: code, header_code: code };
      // Insert just after the target header's last existing member row.
      const withoutRow = prev.filter((it) => it._editId !== rowEditId);
      const hIdx = withoutRow.findIndex((it) => it._editId === targetHeaderEditId);
      if (hIdx < 0) return prev;
      // Walk forward until next header or end.
      let insertAt = hIdx + 1;
      while (insertAt < withoutRow.length &&
             !(withoutRow[insertAt].item_type === 'header' || withoutRow[insertAt].is_header)) {
        insertAt += 1;
      }
      return [...withoutRow.slice(0, insertAt), movedRow, ...withoutRow.slice(insertAt)];
    });
  };

  const applyBulkGst = () => {
    const gstVal = parseFloat(bulkGst);
    if (isNaN(gstVal) || gstVal < 0) { toast.error('Enter a valid GST value'); return; }
    
    if (bulkGstType === 'value') {
      // Fixed amount: distribute across items proportionally
      const nonHeaderItems = editItems.filter(i => i.item_type !== 'header');
      const totalBase = nonHeaderItems.reduce((s, i) => {
        const br = isCtV ? (i.client_rate ?? i.rate ?? 0) : (i.vendor_rate ?? i.rate ?? 0);
        return s + (i.quantity || 0) * br;
      }, 0);
      if (totalBase === 0) { toast.error('No items to apply GST'); return; }
      // Calculate equivalent percentage
      const equivPct = (gstVal / totalBase) * 100;
      setEditItems(prev => prev.map(item => {
        if (item.item_type === 'header') return item;
        return { ...item, gst_percentage: Math.round(equivPct * 100) / 100 };
      }));
      toast.success(`GST ₹${gstVal.toLocaleString()} applied (≈${equivPct.toFixed(1)}%)`);
    } else {
      // Percentage: apply directly
      setEditItems(prev => prev.map(item => {
        if (item.item_type === 'header') return item;
        return { ...item, gst_percentage: gstVal };
      }));
      toast.success(`GST ${gstVal}% applied to all items`);
    }
    // Keep the value visible (don't reset)
  };

  // iter-386u — Bulk Quality Tier update handlers for the MAIN Edit BOQ table.
  const rowIdFor = (it) => it?._editId || it?.id || null;
  const toggleTierSelect = (id) => {
    if (!id) return;
    setTierSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearTierSelection = () => setTierSelectedIds(new Set());
  const runBoqTierUpdate = async (tier) => {
    if (!worksheetId) { toast.error('Worksheet id missing'); return; }
    if (!tierSelectedIds.size) { toast.info('Select at least one row first'); return; }
    if (!['budget', 'standard', 'premium', 'luxury'].includes(tier)) return;
    const pool = editItems
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it && it.item_type !== 'header' && tierSelectedIds.has(rowIdFor(it)));
    if (!pool.length) { toast.info('Selected rows are no longer present'); return; }
    // iter-386u-b — Skip rows already at the target tier locally so the
    // toast honestly reports what changed (backend also enforces it).
    const changed = pool.filter(({ it }) => (it.quality_tier || 'standard').toLowerCase() !== tier);
    if (!changed.length) {
      toast.info(`All selected rows are already ${tier.charAt(0).toUpperCase() + tier.slice(1)}`);
      clearTierSelection();
      return;
    }
    setTierRewriting(true);
    const CHUNK = 8;
    let touched = 0;
    try {
      for (let start = 0; start < changed.length; start += CHUNK) {
        const slice = changed.slice(start, start + CHUNK);
        const r = await axios.post(
          `${API}/boq-worksheets/${worksheetId}/ai-bulk-tier-update`,
          {
            items: slice.map(({ it }) => ({
              id: rowIdFor(it),
              task_title: it.task_title || '',
              description: it.description || '',
              main_category_name: it.main_category_name || '',
              sub_category_name: it.sub_category_name || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'NOS',
              vendor_rate: Number(it.vendor_rate) || 0,
              client_rate: Number(it.client_rate) || 0,
              // iter-386u-b — Baseline locking payload.
              quality_tier: (it.quality_tier || 'standard').toLowerCase(),
              original_standard_vendor_rate: Number(it.original_standard_vendor_rate) || null,
              original_standard_client_rate: Number(it.original_standard_client_rate) || null,
            })),
            tier,
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 90000 },
        );
        const updated = r.data?.updated || [];
        updated.forEach((res, i) => {
          const target = slice[i];
          if (!target || !res) return;
          if (res.no_change) return; // server confirmed no-op
          const patch = {};
          if (res.description) patch.description = res.description;
          if (res.vendor_rate != null) patch.vendor_rate = res.vendor_rate;
          if (res.client_rate != null) patch.client_rate = res.client_rate;
          if (res.remarks) patch.remarks = res.remarks;
          if (res.quality_tier) patch.quality_tier = res.quality_tier;
          if (res.original_standard_vendor_rate != null) patch.original_standard_vendor_rate = res.original_standard_vendor_rate;
          if (res.original_standard_client_rate != null) patch.original_standard_client_rate = res.original_standard_client_rate;
          if (Object.keys(patch).length) {
            setEditItems((prev) => prev.map((it, k) => (k === target.idx ? { ...it, ...patch } : it)));
            touched += 1;
          }
        });
      }
      const label = tier.charAt(0).toUpperCase() + tier.slice(1);
      toast.success(`Applied ${label} tier to ${touched} row${touched !== 1 ? 's' : ''} — remember to Save`);
      clearTierSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tier update failed');
    } finally {
      setTierRewriting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/boq-worksheets/${worksheetId}`, {
        items: editItems, discount_type: discountType, discount_value: discountValue,
        discount_percentage: discountType === 'percentage' ? discountValue : 0,
        discount_apply_mode: discountApplyMode,
        freight_pct: freightPct, labour_handling_pct: labourHandlingPct, consultancy_pct: consultancyPct,
        consultancy_type: consultancyType, consultancy_value: consultancyValue,
        // iter-286: persist admin toggles
        package_savings_disabled: packageGloballyDisabled,
        show_consultancy_to_vendor: showConsultancyToVendor,
        // iter-304: persist Client PDF variance toggle
        show_actual_variance_in_client_pdf: showActualVarianceInClientPdf,
        // iter-377: persist Annexure client/vendor visibility toggles
        annexure_show_to_client: annexureShowToClient,
        annexure_show_to_vendor: annexureShowToVendor,
        // iter-312p: persist Actual-side deduction overrides. null → backend keeps auto behavior.
        client_payments_applied_override: clientPaymentsOverride,
        credit_notes_applied_override: creditNotesOverride,
        // iter-312q: persist per-BOQ payment application list (strip hydrated detail keys)
        applied_payments: (appliedPayments || [])
          .filter(ap => ap && ap.payment_id && +ap.applied_amount > 0)
          .map(ap => ({ payment_id: ap.payment_id, applied_amount: +ap.applied_amount })),
        terms_conditions: termsConditions,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('BOQ saved successfully');
      // iter-386u-ac — High-Speed Editor: instead of a full re-fetch (which
      // scrolls to the top and destroys focus/scroll state), reconcile the
      // server-returned items back into local state IN-PLACE. We keep every
      // row's stable `_editId` so React never remounts the row.
      const serverItems = resp?.data?.items;
      if (Array.isArray(serverItems)) {
        setEditItems((prev) => {
          const byId = new Map(prev.map((it) => [it._editId, it]));
          return serverItems.map((si, idx) => {
            // Match server row → prev row by id (new rows come back with a
            // fresh id from the server that wasn't in the prev _editId map).
            const existing = byId.get(si.id) || byId.get(si._editId) || null;
            const stableEditId = existing?._editId || si.id || `row_${idx}_${Math.random().toString(36).slice(2, 10)}`;
            return { ...si, _editId: stableEditId };
          });
        });
      }
      try { const bc = new BroadcastChannel('boq_updates'); bc.postMessage({ type: 'boq_saved', worksheetId }); bc.close(); } catch {}
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save BOQ'); }
    finally { setSaving(false); }
  };

  // ============================================================================
  // iter-291: Publish / Vendor / Email controls — replicated UX from
  // BOQWorksheets.js. We REUSE the existing backend endpoints rather than
  // writing fresh ones, per user spec ("do not write a separate code").
  // ============================================================================
  const [publishing, setPublishing] = useState(false);
  const [publishingVendor, setPublishingVendor] = useState(false);
  // iter-313e: replace blocking prompt() with a proper inline dialog
  // featuring 3 / 7 / 14 / 20 / 30 / 60 / 90 day quick-picks plus a custom input.
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  // iter-386u-f — task #6: Display in Dashboards toggle default ON.
  const [publishDisplayInDashboards, setPublishDisplayInDashboards] = useState(true);
  const [publishDialogTarget, setPublishDialogTarget] = useState('client'); // 'client' | 'vendor'
  const [publishValidityDays, setPublishValidityDays] = useState(15);

  const openPublishDialog = (target) => {
    setPublishDialogTarget(target);
    setPublishValidityDays(15);
    setPublishDialogOpen(true);
  };

  const handlePublishToClient = async () => {
    // iter-386u-as — plan-gated: block Free users with upgrade toast.
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const plan = (u.plan_code || '').toLowerCase();
      if (u.role !== 'owner' && !u.grandfathered_pro && (plan === 'free' || !u.plan_status || u.plan_status === 'inactive')) {
        toast.error('Publishing to client requires Essential+ — visit /billing to upgrade.', {
          action: { label: 'Upgrade', onClick: () => window.location.href = '/billing' },
        });
        return;
      }
    } catch { /* ignore */ }
    if (!worksheet) return;
    const days = parseInt(publishValidityDays, 10);
    if (!days || days <= 0) {
      toast.error('Please pick a valid expiry');
      return;
    }
    const validity = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setPublishing(true);
    setPublishDialogOpen(false);
    try {
      const r = await axios.post(`${API}/boq-worksheets/${worksheetId}/publish-to-client`, {
        visible: true,
        validity_date: validity,
        generate_public_link: true,
        display_in_dashboards: publishDisplayInDashboards,
      }, { headers: authHeaders });
      const url = `${window.location.origin}/public/boq/${r.data.public_token}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success(`Published to client (${days} days). Link copied to clipboard.`);
      await fetchWorksheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish to client');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishFromClient = async () => {
    if (!worksheet) return;
    if (!window.confirm('Unpublish this BOQ from the client? The public link will be revoked.')) return;
    try {
      await axios.post(`${API}/boq-worksheets/${worksheetId}/publish-to-client`, {
        visible: false, generate_public_link: false,
      }, { headers: authHeaders });
      toast.success('Unpublished from client');
      await fetchWorksheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    }
  };

  // iter-315e: BOQ → RA Bill confirm / un-confirm
  const [raBillBusy, setRaBillBusy] = useState(false);
  const handleConfirmRaBill = async () => {
    if (!worksheet) return;
    if (!window.confirm('Confirm this BOQ as a Running Account (RA) Bill?\n\nAfter confirmation:\n• Estimated (Design) Qty/Unit/Rate/Amount become read-only.\n• Actual (Site) figures stay editable.\n• You can only add new line items under a new "Additional Work" section.')) return;
    // iter-338c (Msg-680): "Generate Running Bill" / "Confirm as RA Bill"
    // must NOT auto-send an email to the client. Surface a second prompt so
    // the user explicitly opts in. Default = no email.
    const sendEmail = window.confirm(
      'Send a confirmation email with the RA-Bill PDF to the client now?\n\n' +
      'OK   → Send email immediately\n' +
      'Cancel → Confirm only; you can email later from the Edit BOQ / Public BOQ page.'
    );
    setRaBillBusy(true);
    try {
      await axios.post(
        `${API}/boq-worksheets/${worksheetId}/confirm-ra-bill?send_email=${sendEmail ? 'true' : 'false'}`,
        {},
        { headers: authHeaders }
      );
      toast.success(sendEmail ? 'Confirmed as RA Bill — email sent to client.' : 'Confirmed as RA Bill (no email sent).');
      await fetchWorksheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm');
    } finally {
      setRaBillBusy(false);
    }
  };
  const handleUnconfirmRaBill = async () => {
    if (!worksheet) return;
    if (!window.confirm('Revert RA Bill confirmation?\n\nThe BOQ will become fully editable again.')) return;
    setRaBillBusy(true);
    try {
      await axios.post(`${API}/boq-worksheets/${worksheetId}/unconfirm-ra-bill`, {}, { headers: authHeaders });
      toast.success('Reverted');
      await fetchWorksheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally {
      setRaBillBusy(false);
    }
  };

  // iter-326c: Generate Running Bill from current Billable So Far → opens as Draft for review
  const [runningBillBusy, setRunningBillBusy] = useState(false);
  const handleGenerateRunningBill = async () => {
    if (!worksheet) return;
    if (!window.confirm('Generate a Running Bill from the current Billable So Far?\n\nA Draft Running Bill (RA-N) will be created using each item\'s current task progress × Actual amount. You can review, edit and send it from the Running Bills page.')) return;
    setRunningBillBusy(true);
    try {
      const res = await axios.post(`${API}/boq-worksheets/${worksheetId}/generate-running-bill`, {}, { headers: authHeaders });
      const bill = res.data?.bill;
      toast.success(res.data?.message || `Running Bill ${bill?.bill_number || ''} created`);
      if (bill?.id) {
        navigate(`/running-bills/${bill.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate Running Bill');
    } finally {
      setRunningBillBusy(false);
    }
  };

  // ============================================================================
  // iter-329: Lock & Snapshot — immutable BOQ versions (PDF + Excel in GridFS)
  // ============================================================================
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [snapshotNotes, setSnapshotNotes] = useState('');
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  const fetchSnapshots = useCallback(async () => {
    if (!worksheetId) return;
    setSnapshotsLoading(true);
    try {
      const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/snapshots`, { headers: authHeaders });
      setSnapshots(Array.isArray(r.data) ? r.data : []);
    } catch {
      setSnapshots([]);
    } finally {
      setSnapshotsLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    if (worksheetId) fetchSnapshots();
  }, [worksheetId, fetchSnapshots]);

  const handleCreateSnapshot = async () => {
    if (!worksheet) return;
    setSnapshotBusy(true);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/snapshot`,
        { version_label: snapshotLabel.trim(), notes: snapshotNotes.trim() },
        { headers: authHeaders }
      );
      toast.success(`Snapshot "${r.data?.version_label}" locked.`);
      setSnapshotDialogOpen(false);
      setSnapshotLabel('');
      setSnapshotNotes('');
      await fetchSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create snapshot');
    } finally {
      setSnapshotBusy(false);
    }
  };

  const handleDownloadSnapshot = async (snap, kind) => {
    try {
      const r = await axios.get(
        `${API}/boq-worksheets/${worksheetId}/snapshots/${snap.id}/download?kind=${kind}`,
        { headers: authHeaders, responseType: 'blob' }
      );
      const fname = kind === 'pdf' ? (snap.pdf_filename || `${snap.version_label}.pdf`) : (snap.xlsx_filename || `${snap.version_label}.xlsx`);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to download ${kind.toUpperCase()}`);
    }
  };

  const handleDeleteSnapshot = async (snap) => {
    const reason = window.prompt(`Delete locked snapshot "${snap.version_label}"?\n\nProvide a reason (min 3 chars). The reason will be logged in the audit trail:`);
    if (reason == null) return;
    if (reason.trim().length < 3) { toast.error('Reason must be at least 3 characters'); return; }
    try {
      await axios.delete(
        `${API}/boq-worksheets/${worksheetId}/snapshots/${snap.id}`,
        { headers: authHeaders, data: { reason: reason.trim() } }
      );
      toast.success('Snapshot deleted');
      await fetchSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete snapshot');
    }
  };

  // iter-386u-ak — Snapshot → Edit in New Tab. Prompts for a name prefix,
  // clones the snapshot into a fresh worksheet, and opens it in a new tab.
  const handleCloneSnapshot = async (snap) => {
    const prefix = window.prompt(
      `Clone "${snap.version_label}" into a new editable BOQ.\n\nName prefix (e.g. "Revision 1"):`,
      `Revision ${(snapshots.length || 0) + 1}`,
    );
    if (prefix == null) return;
    if (prefix.trim().length < 1) { toast.error('Name prefix is required'); return; }
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/snapshots/${snap.id}/clone`,
        { name_prefix: prefix.trim() },
        { headers: authHeaders },
      );
      const newId = r.data?.id;
      if (!newId) { toast.error('Clone did not return an ID'); return; }
      toast.success(`Opened "${r.data?.name}" in a new tab`);
      window.open(`/boq-worksheets/${newId}/edit`, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clone snapshot');
    }
  };


  const handlePublishToVendor = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const plan = (u.plan_code || '').toLowerCase();
      if (u.role !== 'owner' && !u.grandfathered_pro && (plan === 'free' || !u.plan_status || u.plan_status === 'inactive')) {
        toast.error('Publishing to vendor requires Essential+ — visit /billing to upgrade.', {
          action: { label: 'Upgrade', onClick: () => window.location.href = '/billing' },
        });
        return;
      }
    } catch { /* ignore */ }
    if (!worksheet) return;
    const days = parseInt(publishValidityDays, 10);
    if (!days || days <= 0) {
      toast.error('Please pick a valid expiry');
      return;
    }
    const validity = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setPublishingVendor(true);
    setPublishDialogOpen(false);
    try {
      const r = await axios.post(`${API}/boq-worksheets/${worksheetId}/publish-to-vendor`, {
        visible: true,
        validity_date: validity,
        generate_public_link: true,
      }, { headers: authHeaders });
      const url = `${window.location.origin}/public/boq/${r.data.vendor_public_token}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success(`Published to vendors (${days} days). Link copied to clipboard.`);
      await fetchWorksheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish to vendors');
    } finally {
      setPublishingVendor(false);
    }
  };

  const handleUnpublishFromVendor = async () => {
    if (!worksheet) return;
    if (!window.confirm('Unpublish this BOQ from vendors? The vendor link will be revoked.')) return;
    try {
      await axios.post(`${API}/boq-worksheets/${worksheetId}/publish-to-vendor`, {
        visible: false, generate_public_link: false,
      }, { headers: authHeaders });
      toast.success('Unpublished from vendors');
      await fetchWorksheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    }
  };

  const copyPublicLink = async (token) => {
    if (!token) return;
    const url = `${window.location.origin}/public/boq/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const openEmailPage = () => {
    if (!worksheet) return;
    const params = worksheet.project_id ? `?projectId=${worksheet.project_id}` : '';
    window.open(`/boq-worksheets/${worksheetId}/email${params}`, '_blank');
  };

  // iter-292: "Last published" badge formatter — full date + publisher name.
  const formatLastPublished = (iso, name) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      return name ? `${dateStr}, ${timeStr} • ${name}` : `${dateStr}, ${timeStr}`;
    } catch {
      return null;
    }
  };

  // iter-292: Vendor Quote Link dialog — replicated UX from BOQ Worksheets list.
  // Lets admins generate a per-vendor public quote-submission link directly
  // from the Edit BOQ header. Reuses the existing /api/vendor-quote-requests
  // endpoint (no new backend code).
  const [vendorQuoteDialogOpen, setVendorQuoteDialogOpen] = useState(false);
  const [vendorQuoteData, setVendorQuoteData] = useState({
    vendor_name: '', vendor_email: '', instructions: '', expiry_days: 14,
    category_codes: [], // iter-364: per-category scope filter (empty = all)
  });
  const [vendorQuoteLoading, setVendorQuoteLoading] = useState(false);
  // iter-364: hold the generated link + copy state so admin can copy again
  // from inside the dialog without re-generating.
  const [vendorQuoteResult, setVendorQuoteResult] = useState(null);

  const openVendorQuoteDialog = () => {
    if (!worksheet?.project_id) {
      toast.error('This BOQ is not linked to a project.');
      return;
    }
    setVendorQuoteData({ vendor_name: '', vendor_email: '', instructions: '', expiry_days: 14, category_codes: [] });
    setVendorQuoteResult(null);
    setVendorQuoteDialogOpen(true);
  };

  // iter-293: Activity timeline popover — pulls merged events from
  // /boq-worksheets/{id}/activity-timeline and surfaces them as a chronological
  // audit trail directly inside the editor.
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const r = await axios.get(
        `${API}/boq-worksheets/${worksheetId}/activity-timeline?limit=80`,
        { headers: authHeaders }
      );
      setTimelineEvents(r.data?.events || []);
    } catch (err) {
      console.error('Failed to load activity timeline', err);
      toast.error(err.response?.data?.detail || 'Failed to load activity timeline');
    } finally {
      setTimelineLoading(false);
    }
  };
  const onTimelineOpenChange = (open) => {
    setTimelineOpen(open);
    if (open) fetchTimeline();
  };
  const formatTimelineEvent = (e) => {
    if (!e?.at) return { dateStr: '—', timeStr: '—' };
    try {
      const d = new Date(e.at);
      return {
        dateStr: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        timeStr: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
      };
    } catch {
      return { dateStr: '—', timeStr: '—' };
    }
  };
  const ACTION_LABEL = {
    publish: 'Published to client',
    unpublish: 'Unpublished from client',
    publish_to_vendor: 'Published to vendor',
    unpublish_from_vendor: 'Unpublished from vendor',
    publish_snapshot: 'Snapshot captured',
    confirm: 'Confirmed',
    reject: 'Rejected',
    pending: 'Marked pending',
    delete: 'Deleted',
    merge: 'Merged from sources',
    merged_into: 'Merged into another BOQ',
    generate_client_link: 'Client public link generated',
    generate_vendor_link: 'Vendor public link generated',
  };
  const ACTION_COLOR = {
    publish: 'text-green-700 bg-green-50 border-green-200',
    publish_snapshot: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    publish_to_vendor: 'text-purple-700 bg-purple-50 border-purple-200',
    unpublish: 'text-red-700 bg-red-50 border-red-200',
    unpublish_from_vendor: 'text-red-700 bg-red-50 border-red-200',
    confirm: 'text-blue-700 bg-blue-50 border-blue-200',
    reject: 'text-red-700 bg-red-50 border-red-200',
    pending: 'text-amber-700 bg-amber-50 border-amber-200',
    merge: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    merged_into: 'text-stone-700 bg-stone-50 border-stone-200',
    delete: 'text-red-800 bg-red-100 border-red-300',
  };

  const handleCreateVendorQuoteLink = async (e) => {
    e.preventDefault();
    if (!vendorQuoteData.vendor_name.trim()) {
      toast.error('Vendor name is required'); return;
    }
    setVendorQuoteLoading(true);
    try {
      const r = await axios.post(`${API}/vendor-quote-requests`, {
        project_id: worksheet.project_id,
        vendor_name: vendorQuoteData.vendor_name.trim(),
        vendor_email: vendorQuoteData.vendor_email.trim() || undefined,
        instructions: vendorQuoteData.instructions.trim() || undefined,
        expiry_days: vendorQuoteData.expiry_days || 14,
        // iter-364 — per-category scope filter (empty = all categories)
        category_codes: (vendorQuoteData.category_codes || []).length
          ? vendorQuoteData.category_codes
          : undefined,
      }, { headers: authHeaders });
      const url = `${window.location.origin}/vendor-quote/${r.data.token}`;
      // iter-364 — keep dialog open in success state so user can copy again.
      setVendorQuoteResult({
        url,
        token: r.data.token,
        expiry_date: r.data.expiry_date,
        vendor_name: r.data.vendor_name,
      });
      try { await navigator.clipboard.writeText(url); toast.success('Vendor quote link created & copied!'); }
      catch { toast.success('Vendor quote link created. Use Copy below.'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vendor quote link');
    } finally {
      setVendorQuoteLoading(false);
    }
  };

  // iter-364 — small helper: copy the generated URL from the success state.
  const copyGeneratedVendorQuoteUrl = async () => {
    if (!vendorQuoteResult?.url) return;
    try {
      await navigator.clipboard.writeText(vendorQuoteResult.url);
      toast.success('Vendor quote link copied to clipboard');
    } catch {
      window.prompt('Copy this vendor quote link:', vendorQuoteResult.url);
    }
  };

  // iter-380: Legend filter is now the SINGLE source of truth for
  // category filtering — the Annexure legend chips write to
  // `activeLegendFilter` too, so both filter widgets stay in sync and
  // BOTH the BOQ table and the Annexure grid narrow to the same
  // category with a single click. `annexureCategoryFilter` is kept as a
  // no-op alias to avoid touching every downstream reference.
  const annexureCategoryFilter = activeLegendFilter || '';
  // eslint-disable-next-line no-unused-vars
  const setAnnexureCategoryFilter = (code) => setActiveLegendFilter(code || null);

  const sortedAllItems = useMemo(() => {
    const items = [...editItems];
    if (editSort.field === 'category') items.sort((a, b) => (a.main_category_code || 'Z').localeCompare(b.main_category_code || 'Z'));
    else if (editSort.field === 'title') items.sort((a, b) => (a.task_title || '').localeCompare(b.task_title || ''));
    else if (editSort.field === 'start_date') {
      // iter-338m: sort by linked task start_date (items with no date → end of list)
      const dateOf = (it) => {
        const tid = it.task_id || it.source_task_id;
        const rec = tid ? taskDatesMap[tid] : null;
        return rec?.start_date || '9999-99-99';
      };
      items.sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
    }
    if (editSort.direction === 'desc') items.reverse();
    return items;
  }, [editItems, editSort, taskDatesMap]);

  // SR number always reflects the row's position in the full, unfiltered BOQ.
  const fullRowPosById = useMemo(() => {
    const m = new Map();
    sortedAllItems.forEach((it, i) => m.set(it._editId, i + 1));
    return m;
  }, [sortedAllItems]);

  // Legend filter keeps matching rows plus the section header above each matching group.
  const sortedItems = useMemo(() => {
    if (!annexureCategoryFilter) return sortedAllItems;
    const target = String(annexureCategoryFilter).toUpperCase();
    const codeOf = (it) => String(it.main_category_code
      || (it.category_code || '').toString().charAt(0)
      || (it.sub_category_code || '').toString().charAt(0)
      || (it.boq_header_code || '').toString().charAt(0)
      || '').toUpperCase();
    const out = [];
    let pendingHeader = null;
    for (const it of sortedAllItems) {
      if (it.item_type === 'header') { pendingHeader = it; continue; }
      const cc = codeOf(it);
      if (!cc || !(cc === target || cc.startsWith(target))) continue;
      if (pendingHeader) { out.push(pendingHeader); pendingHeader = null; }
      out.push(it);
    }
    return out;
  }, [sortedAllItems, annexureCategoryFilter]);

  // iter-386u-an — Section totals by header. For each header row, sum the
  // amounts of all non-header rows between it and the NEXT header (or end).
  // Computed on the current sorted array so re-sequencing updates totals live.
  const sectionTotalsByEditId = useMemo(() => {
    const totals = {};
    let currentHeaderId = null;
    let running = 0;
    for (const it of sortedItems) {
      if (!it) continue;
      if (it.item_type === 'header' || it.is_header) {
        if (currentHeaderId) totals[currentHeaderId] = running;
        currentHeaderId = it._editId;
        totals[currentHeaderId] = 0;
        running = 0;
      } else if (currentHeaderId) {
        // iter-386u-bh — Include lump_sum_client on header totals so the summary math matches the line breakdown.
        const rate = Number(it.quantity || 0) * Number(it.rate || 0);
        const lump = Number(it.lump_sum_client || 0);
        const amt = Number(it.amount || 0) || (rate + lump);
        running += amt;
      }
    }
    if (currentHeaderId) totals[currentHeaderId] = running;
    return totals;
  }, [sortedItems]);

  // === Discount Rules Engine — auto-evaluate against main categories present ===
  const [discountRule, setDiscountRule] = useState(null);  // {name, discount_pct, valid_till}
  // iter-285: Package Savings can be disabled OR overridden per-BOQ.
  // When `packageDismissed` is true, the package savings row is hidden even
  // if the discount rule engine returns a match.
  // When `packageOverride` is set, it overrides the auto-rule's name/pct.
  const [packageDismissed, setPackageDismissed] = useState(false);
  // iter-286: this is the PERSISTED admin toggle (saved on the worksheet)
  // distinct from `packageDismissed` which is per-session UI dismiss.
  const [packageGloballyDisabled, setPackageGloballyDisabled] = useState(false);
  // iter-286: admin toggle to expose Consultancy in the Vendor public view.
  const [showConsultancyToVendor, setShowConsultancyToVendor] = useState(false);
  // iter-304: admin toggle to expose Actual+Variance columns in Client PDF/Excel
  const [showActualVarianceInClientPdf, setShowActualVarianceInClientPdf] = useState(false);
  // iter-377: Annexure visibility on public views — two independent
  // toggles so admin can share brand/material specs with the client only,
  // vendor only, both, or neither.
  const [annexureShowToClient, setAnnexureShowToClient] = useState(false);
  const [annexureShowToVendor, setAnnexureShowToVendor] = useState(false);
  // iter-377: idempotent AI-rewrite tracking. When the user runs the AI
  // rewrite (bulk or selective), we record the item's `id`/`_editId` here
  // so a future "Rewrite Legacy" sweep skips it unless the user turns on
  // the Force toggle. Stored in a Set for O(1) lookup; not persisted to
  // the DB — the rewritten description itself IS the persistent artefact.
  const [aiRewrittenIds, setAiRewrittenIds] = useState(new Set());
  // iter-304b: forecast (live what-if) chip state for pricing-model panel
  const [forecastAdminPct, setForecastAdminPct] = useState(null);
  const [forecastClientPct, setForecastClientPct] = useState(null);
  const [forecastPricingModel, setForecastPricingModel] = useState(null);
  const [packageOverride, setPackageOverride] = useState(null); // { name, pct }
  const [packageEditOpen, setPackageEditOpen] = useState(false);
  const [packageEditName, setPackageEditName] = useState('');
  const [packageEditPct, setPackageEditPct] = useState(0);
  const catsKey = useMemo(() => {
    const cats = new Set();
    (editItems || []).forEach(it => { if (it.main_category_code) cats.add(String(it.main_category_code).toUpperCase()); });
    return Array.from(cats).sort().join(',');
  }, [editItems]);
  useEffect(() => {
    if (!catsKey) { setDiscountRule(null); return; }
    const token = localStorage.getItem('token');
    axios.post(`${API}/discount-rules/evaluate`, { categories: catsKey.split(',') }, { headers: authHeaders })
      .then(r => setDiscountRule(r.data?.match || null))
      .catch(() => setDiscountRule(null));
  }, [catsKey]);

  // Non-critical cells (rate history, remarks editor, progress) mount after first paint.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    if (loading || deferredReady) return undefined;
    const ric = window.requestIdleCallback || ((f) => setTimeout(f, 150));
    const cic = window.cancelIdleCallback || clearTimeout;
    const h = ric(() => setDeferredReady(true), { timeout: 600 });
    return () => cic(h);
  }, [loading, deferredReady]);

  // Fix 5/10: lazy-render line items — first 50 rows, +50 as the user scrolls.
  const ROW_BATCH = 50;
  const [visibleRows, setVisibleRows] = useState(ROW_BATCH);
  const rowSentinelRef = useRef(null);
  const prevItemCountRef = useRef(0);
  useEffect(() => {
    // Rows added after load (Add Item / AI commit / paste) → render everything so new rows are visible.
    if (prevItemCountRef.current && editItems.length > prevItemCountRef.current) setVisibleRows(Infinity);
    prevItemCountRef.current = editItems.length;
  }, [editItems.length]);
  useEffect(() => { if (findReplaceOpen || findQuery) setVisibleRows(Infinity); }, [findReplaceOpen, findQuery]);
  useEffect(() => {
    const el = rowSentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) setVisibleRows(v => v + ROW_BATCH);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  });
  const editIndexById = useMemo(() => {
    const m = new Map();
    editItems.forEach((ei, i) => m.set(ei._editId, i));
    return m;
  }, [editItems]);

  // Fix 10: debounce summary recalculation — 800ms after the last edit (first load is immediate).
  const [summaryItems, setSummaryItems] = useState([]);
  const summaryPrimedRef = useRef(false);
  useEffect(() => {
    if (!summaryPrimedRef.current) {
      if (editItems.length) { summaryPrimedRef.current = true; setSummaryItems(editItems); }
      return undefined;
    }
    const t = setTimeout(() => setSummaryItems(editItems), 800);
    return () => clearTimeout(t);
  }, [editItems]);

  const totals = useMemo(() => {
    // iter-312p: SINGLE SOURCE OF TRUTH — same helper as Email + PDF + Excel.
    // Below local variables (estDiscount/estOverheads/estPreGst/etc.) are kept
    // so existing inline UI references that reach into `totals.x` continue working.
    const layer = isCtV ? 'client' : 'vendor';

    // Auto-from-ledger values come from the GET worksheet response; they are now
    // ADVISORY ONLY — used to render the "₹X available" hint, NOT to populate the row.
    const autoPayments = +(worksheet?.client_payments_received || 0);
    const autoCreditNotes = +(worksheet?.credit_notes_total || 0);
    // iter-312t: NO auto-pull from ledger. Admin must Apply Payments or set an
    // explicit override for the deduction to take effect.
    const appliedSum = (appliedPayments || []).reduce((s, ap) => s + (+ap?.applied_amount || 0), 0);
    const usingAppliedList = appliedSum > 0;
    const effectivePayments = usingAppliedList
      ? appliedSum
      : (clientPaymentsOverride != null ? +clientPaymentsOverride : 0);
    const effectiveCreditNotes = creditNotesOverride != null ? +creditNotesOverride : 0;

    // Vendor primary rate = vendor scope only (no consultancy / milestone / client deductions)
    const vendorScope = !isCtV;
    const helper = computeBoqTotals(summaryItems, {
      layer,
      pricingModel: worksheet?.pricing_model,
      discountType,
      discountValue,
      discountApplyMode,
      freightPct,
      labourHandlingPct,
      consultancyType,
      consultancyPct: vendorScope ? 0 : consultancyPct,
      consultancyValue: vendorScope ? 0 : consultancyValue,
      clientPaymentsApplied: vendorScope ? 0 : effectivePayments,
      creditNotesApplied: vendorScope ? 0 : effectiveCreditNotes,
    });
    const milestone = vendorScope
      ? { stage: 0, milestonePct: 0, sitePct: 0, effectivePct: 0, est: 0, act: 0 }
      : computeConsultancyMilestone(worksheet?.consultancy_stage, summaryItems, helper.consultancy, helper.actConsultancy);

    // === PACKAGE DEAL DISCOUNT (kept disabled — iter-303 user request) ===
    let activeRulePct = discountRule ? +(discountRule.discount_pct || 0) : 0;
    let activeRuleName = discountRule?.name || 'MK + MW Bundle';
    if (packageOverride) {
      activeRulePct = +(packageOverride.pct || 0);
      activeRuleName = packageOverride.name || activeRuleName;
    }
    const packageDiscountPct = 0;
    const packageEligible = false;
    const packageDiscount = 0;
    const actPackageDiscount = 0;

    // === iter-329: BILLABLE SO FAR (Σ actual_amount × task_progress%) ===
    // Mirrors public_full.py — per-line Σ(act_qty × act_rate × progress/100), then
    // overheads/discount/GST cascade scaled proportionally via billable_ratio.
    const layerForBillable = helper.layer;
    let billableBasic = 0;
    (summaryItems || []).forEach((item) => {
      if (!item || item.is_discount || item.item_type === 'header') return;
      const rate = pickRate(item, layerForBillable);
      const ar = pickActualRate(item, layerForBillable, rate);
      const aq = item.actual_quantity != null ? (parseFloat(item.actual_quantity) || 0) : (parseFloat(item.quantity ?? 0) || 0);
      const pct = parseFloat(item.task_progress) || 0;
      billableBasic += aq * ar * pct / 100;
    });
    const billableRatio = helper.actBasic > 0 ? (billableBasic / helper.actBasic) : 0;
    const billableDiscount = (helper.actDiscount || 0) * billableRatio;
    const billableFreight = (helper.actFreight || 0) * billableRatio;
    const billableLabour = (helper.actLabour || 0) * billableRatio;
    const billableConsultancy = (helper.actConsultancy || 0) * billableRatio;
    const billableGst = (helper.actGst || 0) * billableRatio;
    // Billable Grand = Basic − Discount + max(Consultancy%, Milestone) + GST — never both.
    const billableGrand = applyMilestoneToBillableGrand((helper.actGrand || 0) * billableRatio, billableConsultancy, milestone.est);
    const billableNetReceivable = billableGrand - (vendorScope ? 0 : effectivePayments) - (vendorScope ? 0 : effectiveCreditNotes);

    return {
      // Estimated
      estBasic: helper.estBasic,
      gst: helper.gst,
      discount: helper.discount,
      overheads: helper.overheads,
      freight: helper.freight,
      labourHandling: helper.labourHandling,
      consultancy: helper.consultancy,
      estGrand: helper.estGrand,
      estNetReceivable: helper.estNetReceivable,
      // Actual
      actBasic: helper.actBasic,
      actGst: helper.actGst,
      actDiscount: helper.actDiscount,
      actOverheads: helper.actOverheads,
      actFreight: helper.actFreight,
      actLabour: helper.actLabour,
      actConsultancy: helper.actConsultancy,
      actGrand: helper.actGrand,
      actNetReceivable: helper.actNetReceivable,
      // iter-312p: Actual-side deductions surfaced for the summary UI
      clientPaymentsApplied: vendorScope ? 0 : effectivePayments,
      creditNotesApplied: vendorScope ? 0 : effectiveCreditNotes,
      vendorScope,
      milestone,
      clientPaymentsAuto: autoPayments,
      creditNotesAuto: autoCreditNotes,
      clientPaymentsOverridden: clientPaymentsOverride != null,
      creditNotesOverridden: creditNotesOverride != null,
      // iter-312q: surfaces for the summary UI
      usingAppliedList,
      appliedPaymentsCount: (appliedPayments || []).filter(ap => +ap?.applied_amount > 0).length,
      // Package savings (disabled)
      packageDiscount, packageDiscountPct, packageEligible,
      packageRuleName: activeRuleName,
      actPackageDiscount,
      // iter-329: Billable So Far cascade
      billableBasic,
      billableDiscount,
      billableFreight,
      billableLabour,
      billableConsultancy,
      billableGst,
      billableGrand,
      billableRatio,
      billableNetReceivable,
    };
  }, [summaryItems, worksheet, discountType, discountValue, discountApplyMode, freightPct, labourHandlingPct, consultancyPct, consultancyType, consultancyValue, discountRule, packageDismissed, packageOverride, packageGloballyDisabled, isCtV, clientPaymentsOverride, creditNotesOverride, appliedPayments]);

  // iter-304b: Live "what-if" forecast — recompute the visible rate per item
  // using the staged pricing % values that the user is typing into the
  // pricing-model panel. Skipped when the staged values match the saved ones.
  const forecast = useMemo(() => {
    if (!worksheet) return null;
    const stagedAdmin = forecastAdminPct;
    const stagedClient = forecastClientPct;
    const stagedModel = forecastPricingModel;
    const savedAdmin = worksheet.admin_markup_percentage ?? 20;
    const savedClient = worksheet.client_markup_percentage ?? 20;
    const savedModel = worksheet.pricing_model || 'vendor_to_client';
    if (
      (stagedAdmin == null || +stagedAdmin === +savedAdmin) &&
      (stagedClient == null || +stagedClient === +savedClient) &&
      (stagedModel == null || stagedModel === savedModel)
    ) return null; // nothing staged → no forecast
    const aPct = +(stagedAdmin ?? savedAdmin) / 100;
    const cPct = +(stagedClient ?? savedClient) / 100;
    const model = stagedModel || savedModel;
    let basic = 0;
    (editItems || []).forEach((it) => {
      if (it.item_type === 'header' || it.is_discount) return;
      const qty = +(it.quantity || 1);
      let adminRate = 0;
      if (model === 'client_to_vendor') {
        const cRate = +(it.client_rate || it.rate || 0);
        // reverse: adminRate = clientRate / (1+cPct), vendorRate = adminRate / (1+aPct)
        adminRate = cPct > 0 ? cRate / (1 + cPct) : cRate;
      } else {
        const vRate = +(it.vendor_rate || 0) || +(it.rate || 0);
        adminRate = vRate * (1 + aPct);
      }
      basic += adminRate * qty;
    });
    let disc = 0;
    if (discountType === 'percentage' && discountValue > 0) disc = basic * discountValue / 100;
    else if (discountValue > 0) disc = +discountValue;
    const post = basic - disc;
    const freight = post * (freightPct || 0) / 100;
    const labour = post * (labourHandlingPct || 0) / 100;
    const consult = (consultancyType === 'amount' || consultancyType === 'fixed')
      ? +(consultancyValue || 0) : post * (consultancyPct || 0) / 100;
    const overheads = freight + labour + consult;
    const gstRate = totals.estBasic > 0 ? totals.gst / totals.estBasic : 0;
    const pre = post + overheads;
    const gstFinal = pre * gstRate;
    const grand = pre + gstFinal;
    const cur = totals.estGrand;
    const delta = grand - cur;
    const pct = cur > 0 ? (delta / cur) * 100 : 0;
    return { grand, delta, pct };
  }, [worksheet, editItems, forecastAdminPct, forecastClientPct, forecastPricingModel, discountType, discountValue, freightPct, labourHandlingPct, consultancyPct, consultancyType, consultancyValue, totals]);

  // Fixed column widths for # and actions
  const FIXED_NUM_W = 36;
  const FIXED_ACT_W = 36;
  const resizableTotal = RESIZABLE_COLS.reduce((s, k) => s + colWidths[k], 0);

  const ResizeHandle = ({ colKey }) => {
    const ref = useRef(null);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const onTouch = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleResizeStart(e, colKey);
      };
      el.addEventListener('touchstart', onTouch, { passive: false });
      return () => el.removeEventListener('touchstart', onTouch);
    });
    return (
      <div
        ref={ref}
        className="absolute right-[-3px] top-0 bottom-0 w-[6px] cursor-col-resize z-20 group/resize flex items-center justify-center hover:bg-blue-400/20"
        onMouseDown={(e) => handleResizeStart(e, colKey)}
        data-testid={`resize-handle-${colKey}`}
      >
        <div className="w-[2px] h-5 bg-stone-300 group-hover/resize:bg-blue-500 rounded-full transition-colors" />
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-stone-50"><div className="text-lg text-stone-400">Loading BOQ...</div></div>;
  if (!worksheet) return <div className="flex items-center justify-center h-screen bg-stone-50"><div className="text-lg text-red-500">BOQ worksheet not found</div></div>;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Sticky Header — iter-313: slim always-visible bar + auto-hide controls panel */}
      <div
        className="sticky top-0 z-50 bg-white border-b border-stone-200 shadow-sm"
        onMouseLeave={() => setControlsHover(false)}
        data-testid="boq-edit-sticky-header"
      >
        {/* SLIM ALWAYS-VISIBLE BAR: Close · Project: X · BOQ: Y · Save · Toggle */}
        <div
          className="px-4 py-1.5 flex items-center justify-between gap-3 border-b border-stone-100"
          onMouseEnter={() => setControlsHover(true)}
          data-testid="boq-edit-slim-bar"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="edit-back-btn-slim" className="shrink-0" title="Go to Dashboard">
              <LayoutDashboard size={16} className="mr-1" /> Dashboard
            </Button>
            <div className="text-sm font-semibold text-stone-800 truncate" data-testid="edit-boq-slim-title">
              {isRaConfirmed && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 mr-2"
                  data-testid="ra-bill-confirmed-badge"
                  title={`Confirmed as RA Bill on ${new Date(worksheet.ra_bill_confirmed_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}${worksheet.ra_bill_confirmed_by_name ? ' by ' + worksheet.ra_bill_confirmed_by_name : ''}`}
                >
                  <Check size={10} />RA Bill · {new Date(worksheet.ra_bill_confirmed_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                </span>
              )}
              {project?.name && (
                <>
                  <span className="text-stone-500 font-medium">Project:</span>{' '}
                  <span className="text-stone-900">{project.name}</span>
                  {project.project_number && (
                    <span className="ml-1 text-stone-400 font-normal">({project.project_number})</span>
                  )}
                </>
              )}
              {worksheet?.worksheet_number && (
                <>
                  <span className="mx-2 text-stone-300">·</span>
                  <span className="text-stone-500 font-medium">BOQ:</span>{' '}
                  <span className="text-stone-900">{worksheet.worksheet_number}</span>
                </>
              )}
              <span className="ml-3 text-xs text-stone-500 font-normal hidden lg:inline">
                {editItems.length} items · Est: ₹{Math.round(totals.estGrand).toLocaleString()}
                {(() => {
                  const delta = Math.round(totals.actGrand - totals.estGrand);
                  if (delta === 0 || !totals.estGrand) return null;
                  const pct = ((totals.actGrand - totals.estGrand) / totals.estGrand) * 100;
                  const isOver = delta > 0;
                  return (
                    <span
                      className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        isOver
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                      data-testid="edit-boq-site-delta-pill"
                      title={`Actual (Site) ₹${Math.round(totals.actGrand).toLocaleString()} vs Estimated (Design) ₹${Math.round(totals.estGrand).toLocaleString()}`}
                    >
                      Δ {isOver ? '+' : '-'}₹{Math.abs(delta).toLocaleString()} ({isOver ? '+' : ''}{pct.toFixed(1)}%)
                    </span>
                  );
                })()}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              size="sm"
              data-testid="save-edit-btn-slim"
            >
              <Check size={14} className="mr-1" />{saving ? 'Saving...' : 'Save'}
            </Button>
            {/* iter-380: Always-visible Find button — the original Find lives
                inside the auto-hide controls panel below, so admins couldn't
                launch Find/Replace unless the panel was pinned open. Cmd/Ctrl+F
                still works too. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => planGate('can_find_replace_boq', 'Find & Replace is a Pro-Intelligence feature.') && setFindReplaceOpen(true)}
              data-testid="open-find-replace-btn-slim"
              title="Find & Replace (Ctrl/Cmd+F)"
              className="h-8"
            >
              <Search size={13} className="mr-1" /> Find
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setControlsPinned(p => !p)}
              data-testid="toggle-controls-btn"
              title={controlsPinned ? 'Hide controls panel' : 'Pin controls panel open'}
              className={controlsPinned ? 'border-stone-400 bg-stone-100' : ''}
            >
              {controlsPinned ? <ChevronUp size={14} className="mr-1" /> : <ChevronDown size={14} className="mr-1" />}
              {controlsPinned ? 'Hide Controls' : 'Show Controls'}
            </Button>
          </div>
        </div>
        {/* AUTO-HIDE CONTROLS PANEL — slides down when hovering slim bar or pinned */}
        <div
          onMouseEnter={() => setControlsHover(true)}
          onMouseLeave={() => setControlsHover(false)}
          className={`transition-all duration-300 ease-in-out ${(controlsPinned || controlsHover) ? 'max-h-[1200px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`}
          data-testid="boq-edit-controls-panel"
        >
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="edit-back-btn" title="Go to Dashboard">
              <LayoutDashboard size={16} className="mr-1" /> Dashboard
            </Button>
            <div>
              <h1 className="font-bold text-base text-stone-900 flex items-center flex-wrap gap-2 min-w-0" data-testid="edit-boq-title">
                <span>Edit: {worksheet.name || worksheet.id}</span>
                {/* iter-386u-al — Revision Chain chip */}
                <RevisionChainChip worksheetId={worksheetId} />
              </h1>
              {(project?.name || worksheet?.worksheet_number) && (
                <p className="text-sm font-semibold text-stone-700 mt-0.5" data-testid="edit-boq-project-name">
                  {project?.name && (
                    <>
                      <span className="text-stone-500 font-medium">Project:</span>{' '}
                      <span className="text-stone-900">{project.name}</span>
                      {project.project_number && (
                        <span className="ml-1 text-stone-400 font-normal">({project.project_number})</span>
                      )}
                    </>
                  )}
                  {worksheet?.worksheet_number && (
                    <>
                      <span className="mx-2 text-stone-300">·</span>
                      <span className="text-stone-500 font-medium">BOQ:</span>{' '}
                      <span className="text-stone-900" data-testid="edit-boq-number">{worksheet.worksheet_number}</span>
                      {/* iter-338h (Msg-680 round-8): BOQ display_name — user-defined
                         alias. Inline click-to-edit prompt so the user can quickly
                         rename without leaving Edit BOQ. */}
                      <button
                        type="button"
                        onClick={async () => {
                          // iter-338i: fetch suggestion to pre-fill the prompt
                          let suggestion = '';
                          try {
                            const sr = await axios.get(
                              `${API}/boq-worksheets/${worksheetId}/suggest-display-name`,
                              { headers: authHeaders }
                            );
                            suggestion = (sr.data?.suggestion || '').trim();
                          } catch { /* non-fatal */ }
                          const seed = worksheet.display_name || suggestion || '';
                          const promptMsg = suggestion && !worksheet.display_name
                            ? `BOQ display name (auto-suggested from items — edit or clear):`
                            : 'BOQ display name (leave blank to clear):';
                          const next = window.prompt(promptMsg, seed);
                          if (next === null) return;
                          try {
                            await axios.put(`${API}/boq-worksheets/${worksheetId}`, { display_name: next.trim() }, { headers: authHeaders });
                            toast.success(next.trim() ? `Renamed to "${next.trim()}"` : 'Display name cleared');
                            await fetchWorksheet();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Rename failed');
                          }
                        }}
                        className="ml-2 text-stone-700 hover:text-emerald-700 inline-flex items-center gap-1 underline decoration-dotted"
                        data-testid="edit-boq-display-name"
                        title="Click to rename this BOQ (e.g. 'Master Bedroom + Living Room')"
                      >
                        <Edit3 size={12} />
                        {worksheet.display_name || <span className="italic text-stone-400">add name</span>}
                      </button>
                    </>
                  )}
                </p>
              )}
              <p className="text-[10px] text-stone-500 mt-0.5">
                {editItems.length} items | Est: ₹{totals.estBasic.toLocaleString()} | Actual: ₹{totals.actBasic.toLocaleString()} | Grand: ₹{Math.round(totals.estGrand).toLocaleString()}
                <span className="ml-3 text-stone-400"><GripVertical size={9} className="inline" /> Drag column borders to resize. Click description to expand/collapse.</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0" data-testid="boq-edit-action-cluster">
            {/* iter-304b: Pricing Model inline editor — toggle + 2 editable percentages.
                iter-386u-bp — Rebranded as "Primary Rate" per Msg. Toggling flips
                which side (Vendor or Client) is the input; the other auto-recalculates
                via admin_markup ↔ client_markup. */}
            <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-xs flex-wrap" data-testid="boq-pricing-model-panel">
              <span className="text-stone-500 font-semibold" title="Which side drives the math. The other side auto-recalculates in real time.">Primary Rate:</span>
              {/* Toggle between Vendor→Client and Client→Vendor */}
              <div className="inline-flex rounded-md overflow-hidden border border-stone-300" data-testid="primary-rate-toggle">
                <button
                  type="button"
                  onClick={async () => {
                    if ((worksheet.pricing_model || 'vendor_to_client') === 'vendor_to_client') return;
                    try {
                      await axios.put(`${API}/boq-worksheets/${worksheetId}`,
                        { pricing_model: 'vendor_to_client' },
                        { headers: authHeaders });
                      toast.success('Primary rate: Vendor drives · Client auto-recalculates');
                      await fetchWorksheet();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || err.message || 'Failed to update pricing model');
                    }
                  }}
                  className={`px-2 py-0.5 ${worksheet.pricing_model !== 'client_to_vendor' ? 'bg-purple-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                  title="Vendor rate is the input · Client rate = Vendor × (1 + admin markup) × (1 + client markup)"
                  data-testid="boq-pricing-vtc-btn"
                >Vendor</button>
                <button
                  type="button"
                  onClick={async () => {
                    if (worksheet.pricing_model === 'client_to_vendor') return;
                    try {
                      await axios.put(`${API}/boq-worksheets/${worksheetId}`,
                        { pricing_model: 'client_to_vendor' },
                        { headers: authHeaders });
                      toast.success('Primary rate: Client drives · Vendor auto-recalculates');
                      await fetchWorksheet();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || err.message || 'Failed to update pricing model');
                    }
                  }}
                  className={`px-2 py-0.5 border-l border-stone-300 ${worksheet.pricing_model === 'client_to_vendor' ? 'bg-blue-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                  title="Client rate is the input · Vendor rate = Client ÷ (1 + client markup) ÷ (1 + admin markup)"
                  data-testid="boq-pricing-ctv-btn"
                >Client</button>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => setPricingPreviewOpen(true)}
                data-testid="open-pricing-model-preview-btn"
              >
                <Layers size={12} className="mr-1" />Pricing-Model Preview
              </Button>
              {/* iter-386u-bx — Compact "locked stage" indicator. The full
                  interactive 5-pill toggle now lives in the Commercial Snapshot
                  section below Consultancy. Clicking this chip scrolls to it. */}
              <button
                type="button"
                onClick={() => {
                  const el = document.querySelector('[data-testid="commercial-stage-toggle-row"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition"
                data-testid="consultancy-stage-toggle"
                title="Jump to the Consultancy Milestone toggle in the Commercial Snapshot"
              >
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Milestone:</span>
                {worksheet?.consultancy_stage ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-[11px] font-extrabold flex items-center justify-center" data-testid={`consultancy-stage-btn-${worksheet.consultancy_stage}`}>
                      {worksheet.consultancy_stage}
                    </span>
                    <span className="text-[10px] text-emerald-700/80">·</span>
                    <span className="text-[10px] font-bold text-emerald-800" data-testid="consultancy-stage-pct">
                      {({1:'10',2:'25',3:'50',4:'80',5:'100'})[String(worksheet.consultancy_stage)]}%
                    </span>
                  </span>
                ) : (
                  <span className="text-[10px] text-emerald-700/80 italic">not set · click to lock</span>
                )}
              </button>
              {/* iter-338i: Profitability tab — Owner/Admin/Staff only.
                 Surfaces per-Legend-Category margin + profit breakdown. */}
              {['owner', 'admin', 'staff'].includes((user?.role || '').toLowerCase()) && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold"
                  onClick={() => setProfitabilityOpen(true)}
                  data-testid="open-profitability-btn"
                  title="Per-Legend-Category profit margin breakdown"
                >
                  <TrendingUp size={12} className="mr-1" />Profitability
                </Button>
              )}
              <span className="text-stone-400">|</span>
              <Label htmlFor="bep-admin-pct" className="text-stone-600 font-normal m-0">{worksheet.pricing_model === 'client_to_vendor' ? 'Admin Markdown' : 'Admin Markup'} %</Label>
              <Input
                id="bep-admin-pct"
                type="number"
                step="0.1"
                min="0"
                max="200"
                className="h-7 w-16 px-2 text-xs"
                defaultValue={worksheet.admin_markup_percentage ?? 20}
                key={`admin-${worksheet.id}-${worksheet.admin_markup_percentage ?? 20}`}
                onChange={(e) => {
                  // iter-304b: live what-if forecast as user types
                  const v = parseFloat(e.target.value);
                  setForecastAdminPct(Number.isNaN(v) ? null : v);
                }}
                onBlur={async (e) => {
                  const raw = e.target.value;
                  const v = parseFloat(raw);
                  if (Number.isNaN(v) || v < 0 || v > 200) {
                    toast.error('Enter a valid percentage between 0 and 200');
                    e.target.value = String(worksheet.admin_markup_percentage ?? 20);
                    setForecastAdminPct(null);
                    return;
                  }
                  if (v === (worksheet.admin_markup_percentage ?? 20)) { setForecastAdminPct(null); return; }
                  try {
                    await axios.put(`${API}/boq-worksheets/${worksheetId}`,
                      { admin_markup_percentage: v },
                      { headers: authHeaders });
                    toast.success(`Admin ${worksheet.pricing_model === 'client_to_vendor' ? 'Markdown' : 'Markup'} updated to ${v}%`);
                    setForecastAdminPct(null);
                    await fetchWorksheet();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || err.message || 'Failed to update %');
                    e.target.value = String(worksheet.admin_markup_percentage ?? 20);
                    setForecastAdminPct(null);
                  }
                }}
                data-testid="boq-admin-pct-input"
              />
              <Label htmlFor="bep-client-pct" className="text-stone-600 font-normal m-0">{worksheet.pricing_model === 'client_to_vendor' ? 'Vendor Markdown' : 'Client Markup'} %</Label>
              <Input
                id="bep-client-pct"
                type="number"
                step="0.1"
                min="0"
                max="200"
                className="h-7 w-16 px-2 text-xs"
                defaultValue={worksheet.client_markup_percentage ?? 20}
                key={`client-${worksheet.id}-${worksheet.client_markup_percentage ?? 20}`}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setForecastClientPct(Number.isNaN(v) ? null : v);
                }}
                onBlur={async (e) => {
                  const raw = e.target.value;
                  const v = parseFloat(raw);
                  if (Number.isNaN(v) || v < 0 || v > 200) {
                    toast.error('Enter a valid percentage between 0 and 200');
                    e.target.value = String(worksheet.client_markup_percentage ?? 20);
                    setForecastClientPct(null);
                    return;
                  }
                  if (v === (worksheet.client_markup_percentage ?? 20)) { setForecastClientPct(null); return; }
                  try {
                    await axios.put(`${API}/boq-worksheets/${worksheetId}`,
                      { client_markup_percentage: v },
                      { headers: authHeaders });
                    toast.success(`${worksheet.pricing_model === 'client_to_vendor' ? 'Vendor Markdown' : 'Client Markup'} updated to ${v}%`);
                    setForecastClientPct(null);
                    await fetchWorksheet();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || err.message || 'Failed to update %');
                    e.target.value = String(worksheet.client_markup_percentage ?? 20);
                    setForecastClientPct(null);
                  }
                }}
                data-testid="boq-client-pct-input"
              />
              {/* iter-304b: Live "what-if" forecast chip — appears only when staged
                  values differ from the saved values. Shows projected Grand Total
                  delta in real time before commit. */}
              {forecast && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-stone-900 text-white rounded-md text-[11px] font-mono ml-1 animate-in fade-in" data-testid="boq-forecast-chip" title="Projected Grand Total if you save now">
                  <span className="text-stone-300">→</span>
                  <span className="text-amber-300 font-bold">₹{Math.round(forecast.grand).toLocaleString()}</span>
                  <span className={forecast.delta > 0 ? 'text-rose-400' : forecast.delta < 0 ? 'text-emerald-400' : 'text-stone-400'}>
                    {forecast.delta > 0 ? '+' : ''}₹{Math.round(forecast.delta).toLocaleString()}
                    <span className="text-stone-400 ml-1">({forecast.pct >= 0 ? '+' : ''}{forecast.pct.toFixed(1)}%)</span>
                  </span>
                </div>
              )}
            </div>
            {/* iter-359: Consolidated single-row action toolbar.
                Groups every action into logical dropdowns so the toolbar
                reads as one clean line: [Save] Edit ▾  Export ▾  Publish ▾  Bills ▾  Timeline */}
            <div className="flex flex-wrap items-center gap-2 justify-end" data-testid="boq-edit-actions-toolbar">
              {/* Save — always visible, primary CTA */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white h-8"
                size="sm"
                data-testid="save-edit-btn"
              >
                <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save'}
              </Button>

              {/* iter-372 — Excel-style Find & Replace bar (Ctrl+F opens it too) */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => planGate('can_find_replace_boq', 'Find & Replace is a Pro-Intelligence feature.') && setFindReplaceOpen(true)}
                className="h-8"
                data-testid="open-find-replace-btn"
                title="Find & Replace (Ctrl+F)"
              >
                <Search size={13} className="mr-1" /> Find
              </Button>

              {/* Back to Estimator — only visible for BOQ imported from Kitchen/Wardrobe estimator */}
              {worksheet.source === 'estimate' && worksheet.estimate_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    const kind = (worksheet.source_type === 'kitchen') ? 'kitchen' : 'wardrobe';
                    navigate(`/estimators/${kind}/${worksheet.estimate_id}`);
                  }}
                  data-testid="back-to-estimator-btn"
                  title="Open the source Kitchen/Storage Estimator to edit modules, materials, etc."
                >
                  <ArrowLeft size={14} className="mr-1" /> Back to {worksheet.source_type === 'kitchen' ? 'Kitchen' : 'Storage'} Estimator
                </Button>
              )}

              {/* EDIT ▾ dropdown — Add Item / Add Header / Add from Estimate / Rebuild */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8 border-stone-300" data-testid="boq-edit-menu-btn">
                    <Edit3 size={13} className="mr-1" />Edit
                    <ChevronDown size={12} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Insert</DropdownMenuLabel>
                  <DropdownMenuItem onClick={handleAddItem} data-testid="add-item-btn">
                    <Plus size={13} className="mr-2 text-stone-600" /> Add Item
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleAddHeaderRow} data-testid="add-header-btn">
                    <LayoutDashboard size={13} className="mr-2 text-amber-700" /> Add Section Header
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={sortByHeader} data-testid="sort-by-header-btn">
                    <ArrowDownAZ size={13} className="mr-2 text-blue-700" /> Sort by Header (A→Z)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openMergeModal} data-testid="add-from-estimate-btn">
                    <Layers size={13} className="mr-2 text-emerald-700" /> Add from Estimate
                  </DropdownMenuItem>
                  {worksheet.source === 'estimate' && worksheet.estimate_id && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Sync</DropdownMenuLabel>
                      <DropdownMenuItem
                        disabled={rebuilding || worksheet.is_locked}
                        onClick={async () => {
                          const ok = window.confirm(
                            'Rebuild this BOQ from the linked estimate?\n\n' +
                            'This will REPLACE all line items with freshly-converted ones (kitchen lines + separate overhead rows), ' +
                            'using the current pricing logic — so the BOQ Basic Total matches the Kitchen Estimator Client Base exactly.\n\n' +
                            'A snapshot of the current items will be saved (visible in the Diff Sheet).\n\n' +
                            'Continue?'
                          );
                          if (!ok) return;
                          setRebuilding(true);
                          try {
                            const r = await axios.post(`${API}/boq-worksheets/${worksheetId}/rebuild-from-estimate`, {}, { headers: authHeaders });
                            toast.success(r.data.message || 'BOQ rebuilt from estimate');
                            const fresh = await axios.get(`${API}/boq-worksheets/${worksheetId}`, { headers: authHeaders });
                            setWorksheet(fresh.data);
                            setEditItems((fresh.data.items || []).map((item, idx) => ({ ...item, _editId: item._editId || `item-${idx}-${Date.now()}` })));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to rebuild BOQ');
                          } finally {
                            setRebuilding(false);
                          }
                        }}
                        data-testid="rebuild-from-estimate-btn"
                      >
                        <RefreshCw size={13} className={`mr-2 text-blue-600 ${rebuilding ? 'animate-spin' : ''}`} />
                        {rebuilding ? 'Rebuilding…' : 'Rebuild from Estimate'}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* EXPORT ▾ dropdown — Downloads (client/vendor × pdf/excel) + Email */}
              {(() => {
                const downloadFile = async (kind, role) => {
                  try {
                    const ext = kind === 'excel' ? 'xlsx' : 'pdf';
                    const res = await axios.get(
                      `${API}/boq-worksheets/${worksheetId}/export/${kind}?export_role=${role}`,
                      { headers: authHeaders, responseType: 'blob' },
                    );
                    const blob = new Blob([res.data], {
                      type: ext === 'xlsx'
                        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        : 'application/pdf',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `BOQ_${role.toUpperCase()}_${worksheetId.slice(0, 8)}.${ext}`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`${role[0].toUpperCase()}${role.slice(1)} ${ext.toUpperCase()} downloaded`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || `Download failed`);
                  }
                };
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm" variant="outline"
                        className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid="downloads-dropdown-btn"
                      >
                        <Download size={13} className="mr-1" />Export
                        <ChevronDown size={12} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Client (with markup)</DropdownMenuLabel>
                      <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(downloadFile)('pdf', 'client')} data-testid="download-client-pdf-btn">
                        <FileText size={13} className="mr-2 text-blue-600" /> Client PDF
                      {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                      <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(downloadFile)('excel', 'client')} data-testid="download-client-excel-btn">
                        <FileSpreadsheet size={13} className="mr-2 text-blue-600" /> Client Excel
                      {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Vendor (raw rates)</DropdownMenuLabel>
                      <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(downloadFile)('pdf', 'vendor')} data-testid="download-vendor-pdf-btn">
                        <FileText size={13} className="mr-2 text-purple-600" /> Vendor PDF
                      {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                      <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(downloadFile)('excel', 'vendor')} data-testid="download-vendor-excel-btn">
                        <FileSpreadsheet size={13} className="mr-2 text-purple-600" /> Vendor Excel
                      {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={openEmailPage} data-testid="edit-send-email-btn">
                        <Mail size={13} className="mr-2 text-emerald-600" /> Send via Email…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}

              {/* PUBLISH ▾ dropdown — Client publish, Vendor publish, Copy links, Vendor Quote Link */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm" variant="outline"
                    className={`h-8 ${(worksheet?.client_visible || worksheet?.vendor_visible) ? 'border-emerald-300 text-emerald-700 bg-emerald-50/40 hover:bg-emerald-50' : 'border-stone-300'}`}
                    data-testid="publish-dropdown-btn"
                  >
                    <Share2 size={13} className="mr-1" />
                    Publish
                    {(worksheet?.client_visible || worksheet?.vendor_visible) && (
                      <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" title="Currently published" />
                    )}
                    <ChevronDown size={12} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Client</DropdownMenuLabel>
                  {worksheet?.client_visible ? (
                    <>
                      <DropdownMenuItem onClick={() => copyPublicLink(worksheet.public_token)} data-testid="edit-published-btn">
                        <LinkIcon size={13} className="mr-2 text-blue-600" /> Copy Client BOQ Link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleUnpublishFromClient} data-testid="edit-unpublish-client-btn" className="text-red-600 focus:text-red-700">
                        <X size={13} className="mr-2" /> Unpublish from Client
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem onClick={() => openPublishDialog('client')} disabled={publishing} data-testid="edit-publish-client-btn">
                      <Share2 size={13} className="mr-2 text-blue-600" /> {publishing ? 'Publishing…' : 'Publish to Client'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Vendor</DropdownMenuLabel>
                  {worksheet?.vendor_visible ? (
                    <>
                      <DropdownMenuItem onClick={() => copyPublicLink(worksheet.vendor_public_token)} data-testid="edit-vendor-published-btn">
                        <LinkIcon size={13} className="mr-2 text-purple-600" /> Copy Vendor BOQ Link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleUnpublishFromVendor} data-testid="edit-unpublish-vendor-btn" className="text-red-600 focus:text-red-700">
                        <X size={13} className="mr-2" /> Unpublish from Vendor
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem onClick={() => openPublishDialog('vendor')} disabled={publishingVendor} data-testid="edit-publish-vendor-btn">
                      <Users size={13} className="mr-2 text-purple-600" /> {publishingVendor ? 'Publishing…' : 'Publish to Vendor'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openVendorQuoteDialog} data-testid="edit-vendor-quote-link-btn">
                    <LinkIcon size={13} className="mr-2 text-green-700" /> Generate Vendor Quote Link
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Access Security</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setOtpConfigOpen(true)} data-testid="edit-otp-config-btn">
                    <Lock size={13} className={`mr-2 ${worksheet?.otp_gate_enabled ? 'text-emerald-600' : 'text-stone-500'}`} />
                    {worksheet?.otp_gate_enabled ? `OTP Gate • ON${worksheet?.otp_view_limit ? ` (max ${worksheet.otp_view_limit})` : ''}` : 'OTP Gate & View Limit'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* iter-365 — Admin Tools dropdown (parity with BOQ Worksheets page).
                  Brings Clone BOQ / View Quote Requests into Edit BOQ so admins
                  don't have to switch tabs. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm" variant="outline"
                    className="h-8 border-stone-300"
                    data-testid="edit-admin-tools-btn"
                  >
                    <Settings2 size={13} className="mr-1" />
                    Admin Tools
                    <ChevronDown size={12} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem
                    onClick={() => window.open(`/boq-worksheets?admin=clone-boq&source=${worksheet?.project_id || ''}`, '_blank')}
                    data-testid="edit-admin-clone-boq"
                  >
                    <Copy size={13} className="mr-2 text-blue-600" /> Clone BOQ to Project
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openVendorQuoteDialog} data-testid="edit-admin-vendor-quote">
                    <LinkIcon size={13} className="mr-2 text-green-600" /> Create Vendor Quote Link
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.open(`/boq-worksheets?admin=quote-requests&project=${worksheet?.project_id || ''}`, '_blank')}
                    data-testid="edit-admin-view-quotes"
                  >
                    <ClipboardCheck size={13} className="mr-2 text-amber-600" /> View Quote Requests
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* BILLS ▾ dropdown — RA Bill confirm/revert, Running Bill, Lock & Snapshot */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm" variant="outline"
                    className={`h-8 ${worksheet?.ra_bill_confirmed_at ? 'border-emerald-300 text-emerald-700 bg-emerald-50/40' : 'border-stone-300'}`}
                    data-testid="bills-dropdown-btn"
                  >
                    <Check size={13} className="mr-1" />Bills
                    <ChevronDown size={12} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">RA Bill</DropdownMenuLabel>
                  {!worksheet?.ra_bill_confirmed_at ? (
                    <DropdownMenuItem
                      disabled={raBillBusy}
                      onClick={handleConfirmRaBill}
                      data-testid="edit-confirm-ra-bill-btn"
                    >
                      <Check size={13} className="mr-2 text-emerald-700" />{raBillBusy ? '…' : 'Confirm as RA Bill'}
                    </DropdownMenuItem>
                  ) : (
                    user?.role === 'owner' && (
                      <DropdownMenuItem
                        disabled={raBillBusy}
                        onClick={handleUnconfirmRaBill}
                        data-testid="edit-unconfirm-ra-bill-btn"
                        className="text-stone-600"
                      >
                        <X size={13} className="mr-2" />Revert RA Bill
                      </DropdownMenuItem>
                    )
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Running Bill</DropdownMenuLabel>
                  <DropdownMenuItem
                    disabled={runningBillBusy}
                    onClick={handleGenerateRunningBill}
                    data-testid="generate-running-bill-btn"
                  >
                    <Check size={13} className="mr-2 text-emerald-700" />{runningBillBusy ? 'Generating…' : 'Generate Running Bill'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">Snapshot</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => { setSnapshotLabel(`Snapshot ${snapshots.length + 1}`); setSnapshotNotes(''); setSnapshotDialogOpen(true); }}
                    data-testid="lock-snapshot-btn"
                  >
                    <Pin size={13} className="mr-2 text-violet-700" />Lock &amp; Snapshot
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* TIMELINE popover — audit trail */}
              <Popover open={timelineOpen} onOpenChange={onTimelineOpenChange}>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-stone-300 text-stone-600 hover:bg-stone-100"
                    data-testid="edit-activity-timeline-btn"
                    title="Activity Timeline (publish, unpublish, vendor-quote, etc.)"
                  >
                    <History size={14} className="mr-1" />Timeline
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-[420px] p-0 max-h-[500px] overflow-hidden flex flex-col"
                  data-testid="edit-activity-timeline-popover"
                >
                  <div className="px-4 py-3 border-b bg-stone-50 flex items-center justify-between sticky top-0">
                    <div className="flex items-center gap-2">
                      <History size={14} className="text-stone-500" />
                      <span className="font-bold text-sm text-stone-900">Activity Timeline</span>
                    </div>
                    <span className="text-[11px] text-stone-500">{timelineEvents.length} events</span>
                  </div>
                  <div className="overflow-y-auto flex-1 px-3 py-2">
                    {timelineLoading && (
                      <div className="py-8 text-center text-sm text-stone-500" data-testid="timeline-loading">Loading…</div>
                    )}
                    {!timelineLoading && timelineEvents.length === 0 && (
                      <div className="py-8 text-center text-sm text-stone-500" data-testid="timeline-empty">
                        No activity yet. Publish, unpublish, or vendor-quote events will appear here.
                      </div>
                    )}
                    <ol className="relative border-l-2 border-stone-200 ml-2">
                      {timelineEvents.map((ev, i) => {
                        const { dateStr, timeStr } = formatTimelineEvent(ev);
                        const label = ACTION_LABEL[ev.action] || (ev.action || '').replace(/_/g, ' ');
                        const color = ACTION_COLOR[ev.action] || 'text-stone-700 bg-stone-50 border-stone-200';
                        return (
                          <li
                            key={ev.id || `${ev.at}-${i}`}
                            className="ml-3 mb-3 relative"
                            data-testid={`timeline-event-${i}`}
                          >
                            <span className={`absolute -left-[18px] top-1.5 w-2 h-2 rounded-full ring-2 ring-white ${color.includes('emerald') ? 'bg-emerald-500' : color.includes('green') ? 'bg-green-500' : color.includes('red') ? 'bg-red-500' : color.includes('purple') ? 'bg-purple-500' : color.includes('amber') ? 'bg-amber-500' : color.includes('indigo') ? 'bg-indigo-500' : 'bg-stone-400'}`} />
                            <div className="flex items-center justify-between">
                              <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${color}`}>{label}</span>
                              <span className="text-[10px] text-stone-400 font-mono">{dateStr}, {timeStr}</span>
                            </div>
                            <div className="text-xs text-stone-700 mt-0.5">{ev.message}</div>
                            {ev.user_name && (
                              <div className="text-[10px] text-stone-500 mt-0.5">by {ev.user_name}</div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                </PopoverContent>
              </Popover>
            </div>{/* /consolidated-toolbar */}

            {/* iter-359: Small state badge strip — shows "last published" timestamps
                that used to live inline next to each button. */}
            {(worksheet?.client_published_at || worksheet?.vendor_published_at || worksheet?.ra_bill_confirmed_at) && (
              <div className="flex flex-wrap items-center gap-3 justify-end text-[10px] text-stone-500 mt-1" data-testid="boq-status-strip">
                {worksheet?.client_published_at && (
                  <span className="inline-flex items-center gap-1" data-testid="edit-last-published-client">
                    <Clock size={9} />
                    Client published: {formatLastPublished(worksheet.client_published_at, worksheet.client_published_by_name)}
                  </span>
                )}
                {worksheet?.vendor_published_at && (
                  <span className="inline-flex items-center gap-1" data-testid="edit-last-published-vendor">
                    <Clock size={9} />
                    Vendor published: {formatLastPublished(worksheet.vendor_published_at, worksheet.vendor_published_by_name)}
                  </span>
                )}
                {worksheet?.ra_bill_confirmed_at && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="edit-ra-bill-confirmed">
                    <Check size={9} />
                    RA Bill confirmed
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        </div>{/* /auto-hide controls panel */}
      </div>

      {/* Edit Table — 100% width */}
      <div className="px-4 py-2">
        {/* iter-364 — Sponsored placement (zone=boq_edit). Renders above the
            legend so admins editing a BOQ see targeted brand-partner ads. */}
        <div className="mb-3"><SponsorBadge zone="boq_edit" limit={1} variant="premium-minimal" /></div>
        {/* Category Color Legend — only applicable categories, clickable to filter */}
        {boqCategories.length > 0 && (() => {
          // iter-338z15: derive a main-category code for every item, even if
          // `main_category_code` isn't populated — fall back to the first
          // letter of `category_code` / `sub_category_code` / `boq_header_code`.
          // Historic items may only have `category_code` (e.g. 'B01') so the
          // Legend used to silently exclude them, making the filter appear
          // broken. Same helper is reused by the row-filter below.
          const codeOf = (i) => (
            i.main_category_code
            || (i.category_code || '').toString().charAt(0)
            || (i.sub_category_code || '').toString().charAt(0)
            || (i.boq_header_code || '').toString().charAt(0)
            || null
          );
          const usedCatCodes = new Set();
          editItems.forEach(i => {
            if (i.item_type === 'header') return;
            const c = codeOf(i);
            if (c) usedCatCodes.add(c);
          });
          const applicableCats = boqCategories.filter(c => c.color && usedCatCodes.has(c.code || c.group));
          if (applicableCats.length === 0) return null;
          return (
            <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-white border border-stone-200 rounded-lg" data-testid="category-color-legend">
              <span className="text-xs font-semibold text-stone-600 mr-1">Legend:</span>
              {applicableCats.map(cat => {
                const catCode = cat.code || cat.group;
                const isActive = activeLegendFilter === catCode;
                return (
                  <button key={catCode}
                    onClick={() => setActiveLegendFilter(prev => prev === catCode ? null : catCode)}
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-all cursor-pointer ${isActive ? 'ring-2 ring-offset-1 shadow-sm font-semibold' : 'hover:shadow-sm'}`}
                    style={{ borderColor: cat.color, backgroundColor: isActive ? cat.color + '30' : cat.color + '10', ringColor: cat.color }}
                    data-testid={`legend-filter-${catCode}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                    {cat.group_name || cat.group || catCode}
                  </button>
                );
              })}
              {activeLegendFilter && (
                <button onClick={() => setActiveLegendFilter(null)} className="text-xs text-stone-500 hover:text-stone-700 underline ml-1" data-testid="legend-clear-filter">
                  Clear filter
                </button>
              )}
            </div>
          );
        })()}
        {/* Expand All / Collapse All / Sort toolbar */}
        <div className="flex items-center gap-3 mb-3 px-1">
          <button onClick={() => { const all = new Set(editItems.map(i => i._editId).filter(Boolean)); setExpandedDescs(all); setExpandedEditCats({}); }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg bg-white hover:bg-stone-50 transition-colors" data-testid="edit-expand-all">
            <ChevronDown size={12} /> Expand All
          </button>
          <button onClick={() => { setExpandedDescs(new Set()); const collapsed = {}; editItems.forEach(i => { if (i.main_category_id) collapsed[i.main_category_id] = true; }); setExpandedEditCats(collapsed); }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg bg-white hover:bg-stone-50 transition-colors" data-testid="edit-collapse-all">
            <ChevronUp size={12} /> Collapse All
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-stone-500">Sort:</span>
            <Select value={editSort.field + '_' + editSort.direction} onValueChange={(v) => {
              const [f, d] = v.split('_');
              setEditSort({ field: f, direction: d });
            }}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="boq-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none_asc">Default Order</SelectItem>
                <SelectItem value="title_asc">Title A → Z</SelectItem>
                <SelectItem value="title_desc">Title Z → A</SelectItem>
                <SelectItem value="category_asc">Category A → Z</SelectItem>
                <SelectItem value="category_desc">Category Z → A</SelectItem>
                <SelectItem value="start_date_asc">Date earliest → latest</SelectItem>
                <SelectItem value="start_date_desc">Date latest → earliest</SelectItem>
              </SelectContent>
            </Select>
            {/* iter-338m + z16 + 359b: AI Arrange dialog remembers last-used dates */}
            {['owner','admin','staff','project_manager'].includes((user?.role || '').toLowerCase()) && (
              <Button
                size="sm"
                variant="outline"
                disabled={aiArranging || !worksheet?.project_id}
                onClick={() => {
                  if (!worksheet?.project_id) return;
                  // iter-359b: prefer the LAST-USED dates saved on the worksheet
                  // (falls back to project dates on the very first run).
                  setAiArrangeStart(
                    (worksheet?.ai_arrange_last_start || worksheet?.project_start_date || '').slice(0, 10)
                  );
                  setAiArrangeEnd(
                    (worksheet?.ai_arrange_last_end || worksheet?.project_end_date || '').slice(0, 10)
                  );
                  setAiArrangeDialogOpen(true);
                }}
                data-testid="boq-ai-arrange-btn"
                className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                title="AI-schedule all tasks within a start/end window"
              >
                <Sparkles size={12} className="mr-1" />{aiArranging ? 'Arranging…' : 'AI Arrange'}
                <CreditBadge toolKey="ai_arrange" className="ml-1.5" />
              </Button>
            )}
          </div>
        </div>
        {/* iter-386u — Bulk Quality Tier action bar (visible only when
            rows are selected in the main BOQ table). Uses the same
            backend endpoint as the Annexure section — AI rewrites
            Material Spec + Installation + unit rate; Scope, Inclusions,
            Exclusions are preserved verbatim. */}
        {tierSelectedIds.size > 0 && (
          <div
            className="flex items-center gap-3 mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg"
            data-testid="boq-tier-action-bar"
          >
            <span className="text-xs font-semibold text-purple-800" data-testid="boq-tier-selected-count">
              {tierSelectedIds.size} selected
            </span>
            <span className="text-xs text-stone-600">Change Quality Tier →</span>
            <select
              onChange={(e) => {
                const t = e.target.value;
                if (!t) return;
                e.target.value = '';
                runBoqTierUpdate(t);
              }}
              disabled={tierRewriting}
              data-testid="boq-tier-select"
              className="h-7 text-xs border border-amber-300 text-amber-800 rounded px-2 bg-white hover:bg-amber-50 disabled:opacity-50"
              title="Bulk update Quality Tier for the selected rows"
            >
              <option value="">{tierRewriting ? 'Applying…' : 'Select tier ▾'}</option>
              <option value="budget">Budget</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="luxury">Luxury</option>
            </select>
            <CreditBadge toolKey="ai_bulk_tier" className="ml-1" />
            <button
              type="button"
              onClick={clearTierSelection}
              className="ml-auto text-xs text-stone-500 hover:text-stone-700 underline"
              data-testid="boq-tier-clear-selection"
            >
              Clear selection
            </button>
          </div>
        )}
        {/* iter-312t: table now scrolls VERTICALLY inside a capped container so
            the sticky thead actually pins to the top of the BOQ as user scrolls. */}
        {/* iter-313d: removed the max-height cap on the BOQ table so the page itself
            scrolls naturally — the Summary section now appears immediately AFTER the
            last BOQ row (instead of being pinned below a fixed-height scrolling box).
            Thead remains sticky relative to the page; offset by ~44px so it doesn't
            visually clash with the slim sticky bar above. */}
        <div className="bg-white rounded-lg shadow-sm border border-stone-200 overflow-visible">
          <table className="w-full border-collapse" style={{ minWidth: FIXED_NUM_W + resizableTotal + FIXED_ACT_W }} data-testid="boq-edit-table">
            <colgroup>
              <col style={{ width: FIXED_NUM_W }} />
              {RESIZABLE_COLS.map(k => <col key={k} style={{ width: colWidths[k] }} />)}
              <col style={{ width: FIXED_ACT_W }} />
            </colgroup>
            <thead className="text-stone-600 bg-stone-50 sticky top-[44px] z-20 shadow-sm">
              {/* Group header row */}
              <tr className="bg-stone-50 border-b-2 border-stone-200">
                <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide border-r border-stone-200" style={{width: FIXED_NUM_W}}>SR</th>
                <th rowSpan={2} className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide relative select-none border-r border-stone-200">
                  Description
                  <ResizeHandle colKey="title" />
                </th>
                <th colSpan={4} className="px-2 py-2.5 text-center text-xs font-bold bg-blue-50 text-blue-800 border-r border-stone-200 border-b border-blue-100">
                  Estimated (Design)
                </th>
                <th colSpan={3} className="px-2 py-2.5 text-center text-xs font-bold bg-green-50 text-green-800 border-r border-stone-200 border-b border-green-100">
                  Actual (Site)
                </th>
                <th rowSpan={2} className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wide relative select-none border-r border-stone-200 bg-emerald-100 text-emerald-800" data-testid="edit-boq-th-billable">
                  Billable So Far
                  <div className="text-[9px] font-normal text-emerald-700">(Rs)</div>
                  <ResizeHandle colKey="billableSoFar" />
                </th>
                <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide relative select-none border-r border-stone-200 bg-amber-50 text-amber-700">
                  Variance
                  <ResizeHandle colKey="variance" />
                </th>
                <th rowSpan={2} className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide relative select-none border-r border-stone-200">
                  GST%
                  <ResizeHandle colKey="gst" />
                </th>
                <th rowSpan={2} className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide relative select-none border-r border-stone-200">
                  Remarks
                  <ResizeHandle colKey="remarks" />
                </th>
                <th rowSpan={2} className="px-2 py-3 text-center text-xs"><Trash2 size={11} /></th>
              </tr>
              {/* Sub-header row */}
              <tr className="bg-stone-50">
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 relative select-none uppercase tracking-wide">
                  Qty <ResizeHandle colKey="estQty" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 relative select-none uppercase tracking-wide">
                  Unit <ResizeHandle colKey="unit" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-blue-100 text-blue-800 relative select-none uppercase tracking-wide sticky-primary-rate" data-sticky-rate="true">
                  {isCtV ? 'C.Rate' : 'V.Rate'} <span className="text-amber-600">📌</span><ResizeHandle colKey="vendorRate" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 relative select-none border-r border-stone-200 uppercase tracking-wide">
                  Amt <ResizeHandle colKey="estAmt" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 relative select-none uppercase tracking-wide">
                  Qty <ResizeHandle colKey="actQty" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 relative select-none uppercase tracking-wide">
                  Rate <ResizeHandle colKey="actRate" />
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 relative select-none border-r border-stone-200 uppercase tracking-wide">
                  Amt <ResizeHandle colKey="actAmt" />
                </th>
              </tr>
            </thead>
            <>
              {sortedItems.slice(0, visibleRows).map((item, displayIndex) => (
                <VirtualRowGroup
                  key={item._editId || `vrow_${displayIndex}`}
                  eager={displayIndex < 25 || !!findQuery || findReplaceOpen}
                  colSpan={RESIZABLE_COLS.length + 2}
                  estimatedHeight={item.item_type === 'header' ? 56 : 140}
                >{((item, displayIndex) => {
                const index = editIndexById.has(item._editId) ? editIndexById.get(item._editId) : -1;
                const actualIndex = index !== -1 ? index : displayIndex;

                const fullPos = fullRowPosById.get(item._editId) || (displayIndex + 1);

                // ── HEADER ROW (amber separator) ──
                if (item.item_type === 'header') {
                  return (
                    <tr key={item._editId || `header_${displayIndex}`} data-row-id={item._editId} className={`bg-amber-100 border-y-2 border-amber-400 ${touchOverIdx === displayIndex ? 'border-t-4 border-t-blue-500' : ''}`} data-testid={`boq-header-row-${displayIndex}`}
                      draggable onDragStart={(e) => handleDragStart(e, displayIndex)} onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, displayIndex)} onDrop={(e) => handleDrop(e, displayIndex)}
                      {...touchHandlers(displayIndex)}>
                      <td className="px-2 py-3 text-center cursor-grab active:cursor-grabbing text-stone-400" data-drag-grip="true">
                        <svg width="8" height="14" viewBox="0 0 8 14" className="text-stone-400 mx-auto"><circle cx="2" cy="2" r="1.2" fill="currentColor"/><circle cx="6" cy="2" r="1.2" fill="currentColor"/><circle cx="2" cy="7" r="1.2" fill="currentColor"/><circle cx="6" cy="7" r="1.2" fill="currentColor"/><circle cx="2" cy="12" r="1.2" fill="currentColor"/><circle cx="6" cy="12" r="1.2" fill="currentColor"/></svg>
                      </td>
                      <td colSpan={RESIZABLE_COLS.length} className="px-3 py-3">
                        <div className="flex items-start gap-2">
                          <LayoutDashboard size={16} className="text-amber-800 flex-shrink-0 mt-1" />
                          {/* iter-386u-am — editable Header Code with SMART
                              re-sequencing. Typing "C" into what was "F"
                              inserts this header at slot C and shifts C, D, E
                              down to D, E, F. Tasks travel with their header.
                              We use a data attribute on the DOM node to
                              remember the code the user started editing so we
                              can diff against it on blur. */}
                          <input
                            defaultValue={String(item.header_code || item.boq_header_code || '')}
                            key={`hcode-${item._editId}-${item.header_code || item.boq_header_code || ''}`}
                            onFocus={(e) => {
                              e.currentTarget.dataset.originalCode = String(item.header_code || item.boq_header_code || '');
                            }}
                            onChange={(e) => {
                              // Sanitize live but do NOT touch state until commit,
                              // otherwise every keystroke would fire a resequence.
                              e.target.value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
                            }}
                            onBlur={(e) => {
                              const original = e.currentTarget.dataset.originalCode || '';
                              const next = String(e.currentTarget.value || '').toUpperCase().replace(/[^A-Z]/g, '');
                              if (!next) {
                                // User cleared — snap back to original so we
                                // don't lose the code silently.
                                e.currentTarget.value = original;
                                return;
                              }
                              if (next === original) return;
                              resequenceHeaderCode(item._editId, original, next);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') {
                                e.currentTarget.value = e.currentTarget.dataset.originalCode || '';
                                e.currentTarget.blur();
                              }
                            }}
                            readOnly={isItemFrozen(item)}
                            placeholder="A"
                            maxLength={3}
                            className={`w-10 text-center font-bold text-sm text-amber-900 bg-white/70 border border-amber-300 rounded uppercase flex-shrink-0 mt-0.5 ${isItemFrozen(item) ? 'cursor-not-allowed opacity-70' : ''}`}
                            data-testid={`boq-header-code-input-${displayIndex}`}
                          />
                          {/* iter-386u-an — Section total pill: sum of all
                              non-header rows between this header and the next.
                              Updates instantly on re-sequence because
                              sectionTotalsByEditId keys off the header's
                              _editId (which travels with the row). */}
                          {(() => {
                            const total = sectionTotalsByEditId[item._editId] || 0;
                            if (!total) return null;
                            return (
                              <span
                                className="ml-1 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-200/70 text-amber-900 text-[11px] font-semibold border border-amber-300 flex-shrink-0"
                                data-testid={`boq-header-total-${displayIndex}`}
                                title="Sum of all items under this header"
                              >
                                ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </span>
                            );
                          })()}
                          {/* iter-312n: auto-growing textarea so long section headers wrap instead of being clipped */}
                          <textarea
                            value={item.task_title || ''}
                            onChange={(e) => handleItemChange(actualIndex, 'task_title', e.target.value)}
                            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            rows={1}
                            readOnly={isItemFrozen(item)}
                            disabled={isItemFrozen(item)}
                            className={`font-bold text-sm text-amber-900 uppercase tracking-wide bg-transparent border-none outline-none flex-1 py-1 resize-none whitespace-pre-wrap break-words leading-snug ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`}
                            placeholder="Section Header..."
                            data-testid={`boq-header-input-${displayIndex}`}
                          />
                        </div>
                      </td>
                      <td className="p-1 text-center">
                        <button onClick={() => handleDeleteItem(actualIndex)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-header-${displayIndex}`}><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  );
                }

                // ── CATEGORY GROUP HEADER ──
                // Show category header before this item if it's the first item or category changed
                const prevItem = displayIndex > 0 ? sortedItems[displayIndex - 1] : null;
                const showCategoryHeader = !prevItem || prevItem.item_type === 'header' ||
                  (item.main_category_id && item.main_category_id !== prevItem.main_category_id) ||
                  (!item.main_category_id && prevItem.main_category_id);
                
                const catColor = categoryColorMap[item.main_category_code] || categoryColorMap[item.main_category_id] || '#78716c';
                const catCode = item.main_category_code || '';
                const catName = item.main_category_name || 'Uncategorized';
                const subCatName = item.sub_category_name || '';
                const subCatCode = item.sub_category_code || '';
                
                const br = isCtV ? (item.client_rate ?? item.rate ?? 0) : (item.vendor_rate ?? item.rate ?? 0);
                const qty = item.quantity || 0;
                // iter-386u-bh — Include lump-sum bucket (unit-mismatched add-ons) in the visible amount.
                const lumpForAmt = Number(isCtV ? item.lump_sum_client : item.lump_sum_vendor) || 0;
                const estAmount = qty * br + lumpForAmt;
                const gstMul = 1 + ((item.gst_percentage || 0) / 100);
                const actQty = item.actual_quantity ?? qty;
                // iter-306b: pick the pricing-model-specific actual tier so
                // switching V↔C re-renders without "value drift" on save.
                const actRate = isCtV
                  ? (item.actual_client_rate ?? item.actual_rate ?? br)
                  : (item.actual_vendor_rate ?? item.actual_rate ?? br);
                const actAmount = actQty * actRate;
                const subOpts = getSubCategoryOptions(item.main_category_id);
                const isExpanded = expandedDescs.has(item._editId);
                const descText = item.description || '';
                const isLongDesc = descText.length > 60;

                // Calculate category actual subtotal for header
                let catActSubtotal = 0;
                let catItemCount = 0;
                if (showCategoryHeader) {
                  for (const si of sortedItems) {
                    if (si.item_type === 'header' || si.is_discount) continue;
                    if (si.main_category_id === item.main_category_id || (!si.main_category_id && !item.main_category_id)) {
                      const siBr = isCtV ? (si.client_rate ?? si.rate ?? 0) : (si.vendor_rate ?? si.rate ?? 0);
                      const siActQty = si.actual_quantity ?? si.quantity ?? 0;
                      const siActRate = isCtV
                        ? (si.actual_client_rate ?? si.actual_rate ?? siBr)
                        : (si.actual_vendor_rate ?? si.actual_rate ?? siBr);
                      catActSubtotal += siActQty * siActRate;
                      catItemCount++;
                    }
                  }
                }

                const rows = [];

                // Category header row (matching Published BOQ style)
                if (showCategoryHeader) {
                  rows.push(
                    <tr key={`cat-header-${displayIndex}`} className="bg-stone-100 border-b border-stone-200" style={{ borderLeft: `6px solid ${catColor}` }}>
                      <td colSpan={RESIZABLE_COLS.length + 2} className="px-3 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="w-7 h-7 rounded-full text-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: catColor }}>
                              {catCode || '?'}
                            </span>
                            <div>
                              <span className="font-bold text-stone-900 text-sm">{catCode ? `${catCode} – ${catName}` : catName}</span>
                              {subCatName && <span className="text-stone-500 text-xs ml-2">{subCatCode ? `${subCatCode} – ` : ''}{subCatName}</span>}
                              <span className="text-stone-400 text-xs ml-3">{catItemCount} item{catItemCount !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          <span className="font-semibold text-stone-800 text-sm">₹{Math.round(catActSubtotal).toLocaleString()}</span>
                        </div>
                      </td>
                    </tr>
                  );
                }

                // ── ITEM ROW ──
                rows.push(
                  <tr
                    key={item._editId || actualIndex}
                    data-row-id={item._editId}
                    data-item-id={item.id || ''}
                    data-task-title={item.task_title || ''}
                    className={`hover:bg-stone-50/80 border-b border-stone-200 transition-colors group h-auto [&>td]:overflow-visible [&>td]:h-auto ${(dragOverIdx === displayIndex || touchOverIdx === displayIndex) ? 'border-t-4 border-t-blue-500' : ''}`}
                    style={catColor !== '#78716c' ? { borderLeft: `6px solid ${catColor}` } : undefined}
                    data-testid={`boq-row-${displayIndex}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, displayIndex)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, displayIndex)}
                    onDrop={(e) => handleDrop(e, displayIndex)}
                    {...touchHandlers(displayIndex)}
                  >
                    <td className="px-2 py-3 text-center text-stone-500 text-sm select-none align-top" title="Drag row · type a number + Enter to jump to that row" data-drag-grip="true">
                      {/* iter-386u — Bulk Quality Tier row select. Skipped on discount / header rows. */}
                      {!item.is_discount && item.item_type !== 'header' && (() => {
                        const _rowId = rowIdFor(item);
                        const _checked = _rowId && tierSelectedIds.has(_rowId);
                        return (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center justify-center mb-1"
                          >
                            <input
                              type="checkbox"
                              checked={!!_checked}
                              onChange={() => toggleTierSelect(_rowId)}
                              className="h-3.5 w-3.5 accent-purple-600 cursor-pointer"
                              data-testid={`boq-tier-row-select-${displayIndex}`}
                              disabled={!_rowId}
                              title={_rowId ? 'Select for Bulk Quality Tier update' : 'Save the BOQ first to enable selection'}
                            />
                          </div>
                        );
                      })()}
                      <div className="flex items-center justify-center gap-0.5 cursor-grab active:cursor-grabbing">
                        <svg width="8" height="14" viewBox="0 0 8 14" className="text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"><circle cx="2" cy="2" r="1.2" fill="currentColor"/><circle cx="6" cy="2" r="1.2" fill="currentColor"/><circle cx="2" cy="7" r="1.2" fill="currentColor"/><circle cx="6" cy="7" r="1.2" fill="currentColor"/><circle cx="2" cy="12" r="1.2" fill="currentColor"/><circle cx="6" cy="12" r="1.2" fill="currentColor"/></svg>
                        <input
                          type="number"
                          min={1}
                          defaultValue={fullPos}
                          key={`pos-${item._editId}-${fullPos}`}
                          onClick={(e) => e.currentTarget.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const target = parseInt(e.currentTarget.value, 10);
                              if (Number.isFinite(target) && target !== fullPos && target > 0) {
                                // Mark this input as already-handled so the
                                // subsequent onBlur (fired by blur() below)
                                // does NOT re-run handleInsertAtPosition
                                // with a stale closure and move a second row.
                                e.currentTarget.dataset.applied = '1';
                                handleInsertAtPosition(actualIndex, target);
                              }
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              e.currentTarget.dataset.applied = '1';
                              e.currentTarget.value = String(fullPos);
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => {
                            // iter-372-fix: guard against double-move when
                            // Enter/Escape already dispatched the change.
                            if (e.currentTarget.dataset.applied === '1') {
                              e.currentTarget.dataset.applied = '';
                              return;
                            }
                            const raw = e.currentTarget.value;
                            const target = parseInt(raw, 10);
                            if (Number.isFinite(target) && target !== fullPos && target > 0) {
                              handleInsertAtPosition(actualIndex, target);
                            } else {
                              e.currentTarget.value = String(fullPos);
                            }
                          }}
                          className="w-10 text-center text-xs text-stone-500 bg-transparent border border-transparent hover:border-stone-200 focus:border-blue-400 focus:bg-white focus:ring-1 focus:ring-blue-200 rounded px-0.5 py-0.5 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`row-position-input-${displayIndex}`}
                          title="Type a row number and press Enter (or click away) to move this item"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-3 align-top" data-testid={`desc-cell-${displayIndex}`}>
                      <div className="space-y-1">
                        {/* Title + sub-category code (like Published BOQ) */}
                        <div className="flex items-start gap-1.5">
                          {/* iter-312n: auto-growing textarea so long titles wrap instead of being clipped */}
                          <textarea
                            value={item.task_title || ''}
                            onChange={(e) => handleItemChange(actualIndex, 'task_title', e.target.value)}
                            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            rows={1}
                            readOnly={isItemFrozen(item)}
                            disabled={isItemFrozen(item)}
                            className={`text-sm font-semibold text-stone-900 border-0 bg-transparent py-0.5 px-1 flex-1 outline-none resize-none whitespace-pre-wrap break-words leading-snug focus:bg-white focus:ring-1 focus:ring-blue-200 rounded ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`}
                            placeholder="Item title"
                            data-testid={`item-title-input-${displayIndex}`}
                          />
                          {subCatCode && <span className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded flex-shrink-0 mt-1">{subCatCode}</span>}
                          {/* iter-386u-b — Quality Tier badge (only for non-Standard tiers to reduce clutter). */}
                          {item.quality_tier && item.quality_tier !== 'standard' && (
                            <span className="flex-shrink-0 mt-1">
                              <QualityTierBadge tier={item.quality_tier} size="xs" />
                            </span>
                          )}
                        </div>
                        {/* Category/Sub edit — visually distinct selector row */}
                        <div className="flex items-center gap-2 bg-stone-100 rounded-md px-2 py-1.5 border border-stone-300">
                          <CategoryCombobox
                            value={item.main_category_id || ''}
                            options={mainCategoryOptions}
                            onChange={(v) => handleMainCategoryChange(actualIndex, v)}
                            placeholder="+ Category"
                          />
                          {item.main_category_id && subOpts.length > 0 && (
                            <>
                              <span className="text-stone-400 font-bold">/</span>
                              <CategoryCombobox
                                value={item.sub_category_id || ''}
                                options={subOpts}
                                onChange={(v) => handleSubCategoryChange(actualIndex, v)}
                                placeholder="Sub"
                              />
                            </>
                          )}
                        </div>
                        {/* Description below title (like Published BOQ) */}
                        {isExpanded ? (
                          <div className="relative">
                            <Textarea value={descText} onChange={(e) => handleItemChange(actualIndex, 'description', e.target.value)}
                              readOnly={isItemFrozen(item)}
                              disabled={isItemFrozen(item)}
                              className={`text-sm text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`} style={{ minHeight: '80px' }} placeholder="Description"
                              data-testid={`item-description-textarea-${displayIndex}`} />
                            <button onClick={() => toggleDescExpanded(item._editId)} className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500"
                              data-testid={`collapse-desc-${displayIndex}`}><ChevronUp size={12} /></button>
                          </div>
                        ) : (
                          <div className="relative group/desc cursor-pointer" onClick={() => toggleDescExpanded(item._editId)} title={isLongDesc ? 'Click to expand' : undefined}>
                            {/* iter-380 — highlight Find matches inside the collapsed description snippet */}
                            {descText ? (
                              <div
                                className="text-sm text-stone-500 leading-relaxed line-clamp-2 pr-4"
                                dangerouslySetInnerHTML={findQuery ? highlightHtml(descText, findQuery, { caseSensitive: findCaseSensitive }) : { __html: descText.replace(/</g, '&lt;').replace(/\n/g, ' ') }}
                              />
                            ) : (
                              <div className="text-sm text-stone-500 leading-relaxed line-clamp-2 pr-4">
                                <span className="text-stone-300 italic text-xs">Click to add description</span>
                              </div>
                            )}
                            {isLongDesc && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400 group-hover/desc:text-blue-500" />}
                          </div>
                        )}

                        {/* iter-321: Task Progress slider (admin path) */}
                        <div className="mt-2 pt-2 border-t border-stone-100">
                          {deferredReady && <TaskProgressSlider
                            itemId={item.id || item._editId}
                            progress={item.task_progress || 0}
                            status={item.task_progress_status || 'not_started'}
                            source={item.task_progress_source}
                            updatedBy={item.task_progress_updated_by}
                            updatedAt={item.task_progress_updated_at}
                            compact
                            onChange={(next) => handleItemProgressUpdate(item.id, actualIndex, next)}
                          />}
                          {/* iter-338k: Start / End date pickers — Gantt SSOT sync */}
                          <BOQTaskDateInputs
                            worksheetId={worksheetId}
                            item={item}
                            taskDates={taskDatesMap}
                            readOnly={isItemFrozen(item)}
                            onSaved={async (taskId, dates) => {
                              // iter-338m (1a): cascade — when ANY task date changes,
                              // compute the delta vs the previous value on that task
                              // and apply the SAME delta to every OTHER linked task.
                              const prev = taskDatesMap[taskId] || {};
                              const computeDelta = (oldDate, newDate) => {
                                if (!oldDate || !newDate) return 0;
                                try {
                                  const dOld = new Date(oldDate).getTime();
                                  const dNew = new Date(newDate).getTime();
                                  if (Number.isFinite(dOld) && Number.isFinite(dNew)) {
                                    return Math.round((dNew - dOld) / 86400000);
                                  }
                                } catch { /* ignore */ }
                                return 0;
                              };
                              const startDelta = computeDelta(prev.start_date, dates.start_date);
                              const endDelta = computeDelta(prev.end_date, dates.end_date);
                              const delta = startDelta || endDelta; // prefer start, fall back to end
                              // Update local map for the saved task first
                              setTaskDatesMap((prev2) => ({
                                ...prev2,
                                [taskId]: { start_date: dates.start_date, end_date: dates.end_date, title: item.task_title },
                              }));
                              if (!item.task_id && !item.source_task_id) {
                                setWorksheet((p) => p ? {
                                  ...p,
                                  items: p.items.map((it, i) => i === actualIndex ? { ...it, task_id: taskId, source_task_id: taskId } : it),
                                } : p);
                              }
                              // iter-338z: dependency-aware cascade — server does the
                              // BFS through depends_on graph + applies user's rules
                              // (LATER = always shift dependents; EARLIER = only on
                              // conflict). Returns undo_snapshot for one-click revert.
                              if (delta) {
                                try {
                                  const cascadeRes = await axios.post(
                                    `${API}/boq-worksheets/${worksheetId}/cascade-dates`,
                                    {
                                      item_id: item.id,
                                      new_start_date: dates.start_date,
                                      new_deadline: dates.end_date,
                                    },
                                    { headers: authHeaders },
                                  );
                                  const cascadedIds = cascadeRes.data?.cascaded_ids || [];
                                  const undoSnap = cascadeRes.data?.undo_snapshot || [];
                                  if (cascadedIds.length > 0) {
                                    // Refresh worksheet so the UI reflects the new dates
                                    fetchWorksheet();
                                    const tid = `cascade-${Date.now()}`;
                                    toast.success(
                                      `${cascadedIds.length} dependent task${cascadedIds.length !== 1 ? 's' : ''} auto-shifted by ${delta > 0 ? '+' : ''}${delta} day${Math.abs(delta) !== 1 ? 's' : ''}`,
                                      {
                                        id: tid,
                                        duration: 10000,
                                        action: {
                                          label: 'Undo',
                                          onClick: async () => {
                                            try {
                                              await axios.post(
                                                `${API}/boq-worksheets/${worksheetId}/cascade-dates`,
                                                { item_id: item.id, undo_snapshot: undoSnap },
                                                { headers: authHeaders },
                                              );
                                              toast.success('Reverted');
                                              fetchWorksheet();
                                            } catch (err) {
                                              toast.error('Undo failed');
                                            }
                                          },
                                        },
                                      },
                                    );
                                  }
                                } catch (err) {
                                  console.warn('cascade-dates failed', err);
                                }
                              }
                            }}
                          />
                          {/* iter-386u-ai — Move-to-Header pill lives here (under the date
                              inputs, inside the wide Description column) so the narrow
                              actions column stops overflowing. Behaves exactly like the
                              old right-side dropdown. */}
                          {(() => {
                            const headers = (editItems || []).filter((it) => it && (it.item_type === 'header' || it.is_header));
                            if (headers.length === 0) return null;
                            const currentCode = String(item.boq_header_code || item.header_code || '').toUpperCase();
                            return (
                              <div className="mt-1 flex items-center gap-1 text-[10px] text-stone-500">
                                <span className="uppercase tracking-wider">Header</span>
                                <select
                                  className="h-6 text-[10px] border border-stone-200 rounded bg-white text-stone-700 px-1"
                                  value={currentCode}
                                  data-testid={`row-move-header-${displayIndex}`}
                                  onChange={(e) => {
                                    const target = e.target.value;
                                    if (target === currentCode) return;
                                    if (target === '__NONE__') {
                                      assignItemToHeader(item._editId, '__none__');
                                    } else {
                                      const h = headers.find((hh) => String(hh.header_code || hh.boq_header_code || '').toUpperCase() === target);
                                      if (h) assignItemToHeader(item._editId, h._editId);
                                    }
                                  }}
                                >
                                  <option value="">— none —</option>
                                  {headers.map((h) => {
                                    const code = String(h.header_code || h.boq_header_code || '').toUpperCase();
                                    return (
                                      <option key={h._editId} value={code}>
                                        {code || '·'} · {(h.task_title || 'Untitled').slice(0, 24)}
                                      </option>
                                    );
                                  })}
                                  <option value="__NONE__">— no header (end) —</option>
                                </select>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    {/* Estimated columns (blue) */}
                    <td className="px-1 py-1 bg-blue-50/20 align-top border-l border-blue-100">
                      <Input type="number" min="0" value={qty} readOnly={isItemFrozen(item)} disabled={isItemFrozen(item)} onChange={(e) => handleItemChange(actualIndex, 'quantity', parseFloat(e.target.value) || 0)} className={`h-9 text-center text-sm text-blue-700 border-0 bg-transparent ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`} />
                    </td>
                    <td className="px-1 py-1 bg-blue-50/20 align-top">
                      <Select value={item.unit || 'No.s'} disabled={isItemFrozen(item)} onValueChange={(v) => handleItemChange(actualIndex, 'unit', v)}>
                        <SelectTrigger className={`h-9 text-sm text-stone-600 border-0 bg-transparent ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1 bg-blue-50/20 align-top sticky-primary-rate" data-sticky-rate="true">
                      <Input type="number" min="0" value={br} readOnly={isItemFrozen(item)} disabled={isItemFrozen(item)} onChange={(e) => handleVendorRateChange(actualIndex, parseFloat(e.target.value) || 0)} className={`h-9 text-right text-sm text-stone-700 border-0 bg-transparent font-semibold ${isItemFrozen(item) ? 'cursor-not-allowed opacity-80' : ''}`} data-testid={`primary-rate-input-${displayIndex}`} />
                      {/* iter-386u-bw — 3-step Rate History (Ghost Trail) */}
                      {(() => {
                        const eid = item._editId || item.id;
                        const saved = Array.isArray(item.rate_history) ? item.rate_history : [];
                        if (!deferredReady || !eid || dismissedRateGhosts[eid] || saved.length < 2) return null;
                        const chain = saved.slice(-3).map((h) => Math.round(Number(isCtV ? h.client_rate : h.vendor_rate) || 0));
                        // Filter out no-op adjacent duplicates
                        const compact = chain.filter((v, idx) => idx === 0 || v !== chain[idx - 1]);
                        if (compact.length < 2) return null;
                        const labels = ['Was', 'Then', 'Now'].slice(-compact.length);
                        return (
                          <div
                            className="mt-0.5 flex items-center justify-end gap-1 whitespace-nowrap text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                            title={`Rolling 3-step ${isCtV ? 'Client' : 'Vendor'} rate history for this row`}
                            data-testid={`rate-ghost-${displayIndex}`}
                          >
                            {compact.map((v, i) => (
                              <span key={i} className="inline-flex items-center gap-1">
                                {i > 0 && <span className="text-amber-400">→</span>}
                                <span><span className="uppercase text-[8px] font-bold mr-0.5">{labels[i]}</span>₹{v.toLocaleString()}</span>
                              </span>
                            ))}
                            <button
                              onClick={(e) => { e.stopPropagation(); dismissRateGhost(eid); }}
                              className="ml-0.5 text-amber-500 hover:text-amber-700"
                              data-testid={`rate-ghost-dismiss-${displayIndex}`}
                              title="Dismiss"
                            >×</button>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-blue-700 bg-blue-50/30 text-sm whitespace-nowrap align-top">
                      ₹{Math.round(estAmount).toLocaleString()}
                      {/* iter-386u-bh — Show lump-sum sub-breakdown for units-mismatched items */}
                      {(() => {
                        const lump = Number(isCtV ? item.lump_sum_client : item.lump_sum_vendor) || 0;
                        if (!lump) return null;
                        const base = Number(estAmount) - lump;
                        return (
                          <div className="text-[10px] font-normal text-stone-500 mt-0.5" data-testid={`item-lump-breakdown-${displayIndex}`} title="Base per-unit total + lump-sum add-on = final amount">
                            ₹{Math.round(base).toLocaleString()} + <span className="text-amber-700">₹{Math.round(lump).toLocaleString()} (Add-on)</span> = ₹{Math.round(estAmount).toLocaleString()}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Actual columns (green) */}
                    <td className="px-1 py-1 bg-green-50/20 align-top border-l border-green-100">
                      <Input type="number" min="0" step="0.1" value={actQty} onChange={(e) => handleItemChange(actualIndex, 'actual_quantity', parseFloat(e.target.value) || 0)} className="h-9 text-center text-sm text-green-700 border-0 bg-transparent" />
                    </td>
                    <td className="px-1 py-1 bg-green-50/20 align-top">
                      <Input type="number" min="0" step="0.1" value={actRate} onChange={(e) => handleItemChange(actualIndex, 'actual_rate', parseFloat(e.target.value) || 0)} className="h-9 text-right text-sm text-stone-700 border-0 bg-transparent" />
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-green-700 bg-green-50/30 text-sm whitespace-nowrap align-top">
                      ₹{Math.round(actAmount).toLocaleString()}
                    </td>
                    {/* iter-326f: Billable So Far cell — actual × task_progress% */}
                    {(() => {
                      const pct = Number(item.task_progress) || 0;
                      const bsf = Math.round(actAmount * pct / 100);
                      return (
                        <td className="px-3 py-3 text-right font-bold text-emerald-800 bg-emerald-50/70 text-sm whitespace-nowrap align-top border-l-2 border-emerald-300" data-testid={`edit-boq-bsf-${displayIndex}`}>
                          {bsf > 0 ? '₹' + bsf.toLocaleString() : '-'}
                        </td>
                      );
                    })()}
                    {/* Variance column */}
                    {(() => {
                      const variance = actAmount - estAmount;
                      return (
                        <td className={`px-2 py-3 text-right text-sm font-medium align-top whitespace-nowrap ${variance > 0 ? 'text-red-600 bg-red-50/30' : variance < 0 ? 'text-green-600 bg-green-50/20' : 'text-stone-400'}`}>
                          {variance !== 0 ? (variance > 0 ? '+' : '') + '₹' + Math.round(variance).toLocaleString() : '-'}
                        </td>
                      );
                    })()}
                    <td className="p-0 align-top">
                      <Input type="number" min="0" max="100" value={item.gst_percentage ?? 0} onChange={(e) => handleItemChange(actualIndex, 'gst_percentage', parseFloat(e.target.value) || 0)} className="h-9 text-center text-sm font-medium text-stone-900 border-0 bg-transparent" />
                    </td>
                    <td className="px-2 py-3 align-top">
                      {(() => {
                        const remarksText = item.remarks || '';
                        const remarksKey = `r_${item._editId}`;
                        const isRemarksExpanded = expandedDescs.has(remarksKey);
                        if (!deferredReady) return <div className="text-sm text-stone-600 leading-relaxed line-clamp-3">{remarksText}</div>;
                        return (
                          <div className="space-y-0.5">
                            {isRemarksExpanded ? (
                              <div className="relative">
                                <Textarea value={remarksText} onChange={(e) => handleItemChange(actualIndex, 'remarks', e.target.value)}
                                  className="text-sm text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y leading-relaxed" style={{ minHeight: '60px' }} placeholder="Add remarks..." />
                                <button onClick={() => setExpandedDescs(prev => { const s = new Set(prev); s.delete(remarksKey); return s; })}
                                  className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500"><ChevronUp size={12} /></button>
                              </div>
                            ) : (
                              <div className="relative group/remarks cursor-pointer" onClick={() => setExpandedDescs(prev => { const s = new Set(prev); s.add(remarksKey); return s; })}>
                                <div className="text-sm text-stone-600 leading-relaxed line-clamp-3">
                                  {remarksText || <span className="text-stone-300 italic text-xs">Click to add remarks</span>}
                                </div>
                                {remarksText.length > 60 && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400 group-hover/remarks:text-blue-500" />}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-1 text-center">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() => { setAiRefineTargetIdx(actualIndex); setAiRefineOpen(true); }}
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded relative"
                          data-testid={`ai-refine-item-${displayIndex}`}
                          title={`Refine this line with AI (${creditCostFor('ai_refine_line') || 10} credits)`}
                        >
                          <Sparkles size={12} />
                          {creditCostFor('ai_refine_line') > 0 && (
                            <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-amber-400 text-amber-900 rounded-full px-1 leading-tight" data-testid={`credit-badge-inline-ai_refine_line-${displayIndex}`}>
                              {creditCostFor('ai_refine_line')}
                            </span>
                          )}
                        </button>
                        {/* iter-386u-bs — Clone: deep-copy the row (new _editId, cleared id) */}
                        <button
                          onClick={() => {
                            if (!planGate('can_clone_boq_row', 'Cloning BOQ rows is a Pro-Intelligence feature.')) return;
                            const original = editItems[actualIndex];
                            if (!original) return;
                            const clone = JSON.parse(JSON.stringify(original));
                            clone._editId = `row_${Math.random().toString(36).slice(2)}`;
                            clone.id = undefined;
                            clone.task_title = `${original.task_title || ''} (Copy)`.trim();
                            setEditItems((prev) => {
                              const next = [...prev];
                              next.splice(actualIndex + 1, 0, clone);
                              return next;
                            });
                            toast.success('Row cloned — adjust category or specs as needed');
                            setTimeout(() => {
                              const el = document.querySelector(`[data-row-id="${clone._editId}"]`);
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                el.classList.add('boq-row-focus-flash');
                                setTimeout(() => el.classList.remove('boq-row-focus-flash'), 2000);
                              }
                            }, 200);
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          data-testid={`clone-item-${displayIndex}`}
                          title="Clone this row"
                        >
                          <Plus size={12} />
                        </button>
                        <button onClick={() => handleDeleteItem(actualIndex)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-item-${displayIndex}`}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
                return rows;
              })(item, displayIndex)}</VirtualRowGroup>
              ))}
              {sortedItems.length > visibleRows && (
                <tbody>
                <tr ref={rowSentinelRef} data-testid="boq-lazy-load-sentinel">
                  <td colSpan={RESIZABLE_COLS.length + 2} className="px-3 py-4 text-center text-xs text-stone-500">
                    Showing {visibleRows} of {sortedItems.length} rows ·{' '}
                    <button type="button" className="underline hover:text-stone-900" onClick={() => setVisibleRows(Infinity)} data-testid="boq-show-all-rows-btn">Show all</button>
                  </td>
                </tr>
                </tbody>
              )}
            </>
          </table>
          {/* iter-338f (Msg-680 round-7): mirror "Add Header / Add Item" at the
             BOTTOM of the items table — same UX as the public vendor form. */}
          <div className="bg-stone-50 border-t border-stone-200 px-3 py-2 flex items-center justify-end gap-2 flex-wrap" data-testid="edit-boq-bottom-add-bar">
            <Button onClick={handleAddHeaderRow} variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="add-header-btn-bottom">
              <LayoutDashboard size={14} className="mr-1" /> Add Header
            </Button>
            <Button onClick={handleAddItem} variant="outline" size="sm" data-testid="add-item-btn-bottom">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>
        </div>

        {/* ============ SUMMARY SECTION — Matching Published BOQ style ============ */}
        <div className="bg-white rounded-lg shadow-sm border border-stone-200 mt-4 overflow-hidden" data-testid="edit-boq-summary">
          {/* Summary Header */}
          <div className="grid grid-cols-5 border-b-2 border-stone-200">
            <div className="px-6 py-4 bg-stone-50 border-r border-stone-200">
              <span className="text-sm font-bold text-stone-700 uppercase tracking-wide">Summary</span>
            </div>
            <div className="px-6 py-4 bg-blue-50/50 border-r border-stone-200 text-center">
              <span className="text-sm font-bold text-blue-700 uppercase tracking-wide">Estimated (Design)</span>
            </div>
            <div className="px-6 py-4 bg-green-50/50 border-r border-stone-200 text-center">
              <span className="text-sm font-bold text-green-700 uppercase tracking-wide">Actual (Site)</span>
            </div>
            <div className="px-6 py-4 bg-emerald-100 border-r border-stone-200 text-center" data-testid="edit-boq-summary-billable-header">
              <span className="text-sm font-bold text-emerald-800 uppercase tracking-wide">Billable So Far</span>
            </div>
            <div className="px-6 py-4 bg-amber-50/50 text-center">
              <span className="text-sm font-bold text-amber-700 uppercase tracking-wide">Variance</span>
            </div>
          </div>
          {/* Basic Total */}
          <div className="grid grid-cols-5 border-b border-stone-200">
            <div className="px-6 py-5 text-base font-medium text-stone-800">Basic Total</div>
            <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right">
              <span className="text-base font-bold text-blue-700">₹{Math.round(totals.estBasic).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right">
              <span className="text-base font-bold text-green-700">₹{Math.round(totals.actBasic).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right" data-testid="edit-boq-summary-billable-basic">
              <span className="text-base font-bold text-emerald-800">₹{Math.round(totals.billableBasic).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
              {(() => { const v = Math.round(totals.actBasic - totals.estBasic); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
            </div>
          </div>
          {/* Discount (right after Basic) */}
          {(totals.discount > 0 || totals.actDiscount > 0) && (
            <div className="grid grid-cols-5 border-b border-stone-200">
              <div className="px-6 py-5 text-base font-medium text-green-700">
                Discount{discountType === 'percentage' && discountValue > 0 ? ` (${discountValue}%)` : ''}
              </div>
              <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right">
                <span className="text-base font-bold text-blue-700">-₹{Math.round(totals.discount).toLocaleString()}</span>
              </div>
              <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right">
                <span className="text-base font-bold text-green-700">-₹{Math.round(totals.actDiscount).toLocaleString()}</span>
              </div>
              <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right">
                <span className="text-base font-bold text-emerald-800">{totals.billableDiscount > 0 ? '-₹' + Math.round(totals.billableDiscount).toLocaleString() : '-'}</span>
              </div>
              <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
                {(() => { const v = Math.round(totals.actDiscount - totals.discount); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
              </div>
            </div>
          )}
          {/* Freight */}
          {(totals.freight > 0 || totals.actFreight > 0) && (
            <div className="grid grid-cols-5 border-b border-stone-200">
              <div className="px-6 py-5 text-base font-medium text-stone-800">Freight ({freightPct}%)</div>
              <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.freight).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.actFreight).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right"><span className="text-base font-medium text-emerald-800">{totals.billableFreight > 0 ? '+₹' + Math.round(totals.billableFreight).toLocaleString() : '-'}</span></div>
              <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
                {(() => { const v = Math.round(totals.actFreight - totals.freight); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
              </div>
            </div>
          )}
          {/* Labour Handling */}
          {(totals.labourHandling > 0 || totals.actLabour > 0) && (
            <div className="grid grid-cols-5 border-b border-stone-200">
              <div className="px-6 py-5 text-base font-medium text-stone-800">Labour Handling ({labourHandlingPct}%)</div>
              <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.labourHandling).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.actLabour).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right"><span className="text-base font-medium text-emerald-800">{totals.billableLabour > 0 ? '+₹' + Math.round(totals.billableLabour).toLocaleString() : '-'}</span></div>
              <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
                {(() => { const v = Math.round(totals.actLabour - totals.labourHandling); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
              </div>
            </div>
          )}
          {/* Consultancy (Fixed or %) */}
          {!totals.vendorScope && (totals.consultancy > 0 || totals.actConsultancy > 0) && (
            <div className="grid grid-cols-5 border-b border-stone-200" data-testid="summary-consultancy-row">
              <div className="px-6 py-5 text-base font-medium text-stone-800">
                Consultancy {consultancyType === 'amount' || consultancyType === 'fixed' ? '(Fixed)' : `(${consultancyPct}%)`}
              </div>
              <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.consultancy).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right"><span className="text-base font-medium text-stone-700">+₹{Math.round(totals.actConsultancy).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right"><span className={`text-base font-medium ${totals.milestone?.est > totals.billableConsultancy ? 'text-stone-400 line-through' : 'text-emerald-800'}`} title={totals.milestone?.est > totals.billableConsultancy ? 'Superseded by Consultancy Milestone — only the higher of the two is billed' : undefined} data-testid="edit-boq-billable-consultancy">{totals.billableConsultancy > 0 ? '+₹' + Math.round(totals.billableConsultancy).toLocaleString() : '-'}</span></div>
              <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
                {(() => { const v = Math.round(totals.actConsultancy - totals.consultancy); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
              </div>
            </div>
          )}
          {/* iter-386u-bx — Commercial Snapshot 5-stage Consultancy Milestone toggle.
              Placed BELOW Consultancy and ABOVE GST. Owner/Admin/Staff only.
              Silent-save to the PROJECT record — the project is now the single
              source of truth so every worksheet + PDF + Public link inherits.
              Renders whenever admin is viewing OR consultancy is active OR
              a stage is already locked (so it can be edited). */}
          {!totals.vendorScope && ['owner','admin','staff'].includes((user?.role || '').toLowerCase()) && (
            <div className="grid grid-cols-5 border-b border-stone-200 bg-emerald-50/20" data-testid="commercial-stage-toggle-row">
              <div className="px-6 py-4 text-base font-medium text-stone-800">
                <div className="flex items-center gap-2">
                  <span>Consultancy Milestone</span>
                  {worksheet?.consultancy_stage && (
                    <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid="commercial-stage-locked-badge">
                      Stage {worksheet.consultancy_stage} · {({1:10,2:25,3:50,4:80,5:100})[worksheet.consultancy_stage]}%
                    </span>
                  )}
                </div>
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-1.5 py-1" data-testid="commercial-stage-pills">
                  <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold pl-1">Stage:</span>
                  {[0, 1, 2, 3, 4, 5].map((s) => {
                    const active = (Number(worksheet?.consultancy_stage) || 0) === s;
                    const pct = { 0: 0, 1: 10, 2: 25, 3: 50, 4: 80, 5: 100 }[s];
                    return (
                      <button
                        key={s}
                        type="button"
                        title={s === 0 ? 'Stage 0 · Reset milestones (billable consultancy = ₹0)' : `Stage ${s} · ${pct}% locked (silent-save · public + PDF sync)`}
                        onClick={async () => {
                          // iter-386u-bx — Optimistic + silent save to PROJECT record so ALL
                          // worksheets, PDFs, email templates and the Public link inherit
                          // the same milestone. Falls back to worksheet-scoped save if
                          // project_id is missing (defensive).
                          setWorksheet((prev) => (prev ? { ...prev, consultancy_stage: s } : prev));
                          try {
                            if (worksheet?.project_id) {
                              await axios.patch(`${API}/projects/${worksheet.project_id}`,
                                { consultancy_stage: s },
                                { headers: authHeaders });
                            }
                            await axios.put(`${API}/boq-worksheets/${worksheetId}`,
                              { consultancy_stage: s },
                              { headers: authHeaders });
                            toast.success(s === 0 ? 'Milestones reset · Stage 0 · Billable consultancy ₹0' : `Stage ${s} · ${pct}% locked — Public link + PDFs updated`);
                          } catch (err) {
                            setWorksheet((prev) => (prev ? { ...prev, consultancy_stage: worksheet?.consultancy_stage || null } : prev));
                            toast.error(err.response?.data?.detail || err.message || 'Failed to save stage');
                          }
                        }}
                        className={`w-7 h-7 rounded-full text-[11px] font-extrabold transition ${
                          active
                            ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300'
                            : 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
                        }`}
                        data-testid={`commercial-stage-btn-${s}`}
                      >{s}</button>
                    );
                  })}
                </div>
                {Number(worksheet?.consultancy_stage) > 0 && (
                  <button
                    type="button"
                    onClick={() => setConsultancyInvoiceOpen(true)}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1 rounded-full lg-btn-glass text-emerald-800 hover:text-emerald-900"
                    data-testid="consultancy-invoice-open-btn"
                  >
                    <Receipt size={12} /> Invoice this stage
                  </button>
                )}
              </div>
              {(() => {
                const { stage, milestonePct, sitePct, effectivePct } = totals.milestone;
                if (!stage) return (
                  <>
                    <div className="px-6 py-4 bg-blue-50/20 border-l border-blue-100 text-right"><span className="text-base font-bold text-stone-400" data-testid="commercial-stage-est-billable">₹0</span></div>
                    <div className="px-6 py-4 bg-green-50/20 border-l border-green-100 text-right"><span className="text-base font-bold text-stone-400" data-testid="commercial-stage-act-billable">₹0</span></div>
                    <div className="px-6 py-4 bg-emerald-50 border-l-2 border-emerald-300 text-right"><span className="text-base font-bold text-stone-400" data-testid="commercial-stage-billable-so-far">₹0</span></div>
                    <div className="px-6 py-4 bg-amber-50/20 border-l border-amber-100 text-right"><span className="text-[10px] text-stone-400 font-mono">S0·0%</span></div>
                  </>
                );
                const estBillable = Math.round(totals.milestone.est);
                const actBillable = Math.round(totals.milestone.act);
                return (
                  <>
                    <div className="px-6 py-4 bg-blue-50/20 border-l border-blue-100 text-right">
                      <span className="text-base font-bold text-emerald-800" data-testid="commercial-stage-est-billable">₹{estBillable.toLocaleString()}</span>
                      <div className="text-[9px] text-stone-500 mt-0.5">{effectivePct.toFixed(0)}% of ₹{Math.round(totals.consultancy).toLocaleString()}</div>
                    </div>
                    <div className="px-6 py-4 bg-green-50/20 border-l border-green-100 text-right">
                      <span className="text-base font-bold text-emerald-800" data-testid="commercial-stage-act-billable">₹{actBillable.toLocaleString()}</span>
                      {stage >= 4 && sitePct > milestonePct && (
                        <div className="text-[9px] text-emerald-700 mt-0.5">site {sitePct.toFixed(0)}% wins</div>
                      )}
                    </div>
                    <div className="px-6 py-4 bg-emerald-50 border-l-2 border-emerald-300 text-right">
                      <span className="text-base font-bold text-emerald-900" data-testid="commercial-stage-billable-so-far">₹{estBillable.toLocaleString()}</span>
                    </div>
                    <div className="px-6 py-4 bg-amber-50/20 border-l border-amber-100 text-right">
                      <span className="text-[10px] text-emerald-700 font-mono">S{stage}·{effectivePct.toFixed(0)}%</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {/* GST Amount (placed after overheads, just above Grand Total) */}
          <div className="grid grid-cols-5 border-b border-stone-200">
            <div className="px-6 py-5 text-base font-medium text-stone-800">GST Amount</div>
            <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right">
              <span className="text-base font-bold text-blue-700">₹{Math.round(totals.gst).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right">
              <span className="text-base font-bold text-green-700">₹{Math.round(totals.actGst).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right">
              <span className="text-base font-bold text-emerald-800">₹{Math.round(totals.billableGst).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right">
              {(() => { const v = Math.round(totals.actGst - totals.gst); return <span className={`text-base font-semibold ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
            </div>
          </div>
          {/* iter-312r: deduction rows moved below Grand Total. See further down. */}
          {/* 🎁 PACKAGE DEAL DISCOUNT (driven by Discount Rules Engine) */}
          {totals.packageEligible && totals.packageDiscount > 0 && (
            <div className="grid grid-cols-5 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="package-savings-row">
              <div className="px-6 py-5 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">🎁 Package Savings</span>
                {packageEditOpen ? (
                  <div className="flex items-center gap-2 bg-white border border-emerald-300 rounded-md px-2 py-1" data-testid="package-edit-inline">
                    <input
                      type="text"
                      value={packageEditName}
                      onChange={(e) => setPackageEditName(e.target.value)}
                      placeholder="e.g. Commercial Combo"
                      className="text-sm border border-stone-200 rounded px-2 py-1 w-44"
                      data-testid="package-edit-name"
                    />
                    <input
                      type="number"
                      min="0" step="0.5" max="100"
                      value={packageEditPct}
                      onChange={(e) => setPackageEditPct(parseFloat(e.target.value) || 0)}
                      className="text-sm border border-stone-200 rounded px-2 py-1 w-16"
                      data-testid="package-edit-pct"
                    />
                    <span className="text-xs text-stone-500">%</span>
                    <button
                      onClick={() => { setPackageOverride({ name: packageEditName.trim() || 'Package Savings', pct: +packageEditPct || 0 }); setPackageEditOpen(false); }}
                      className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid="package-edit-save"
                    >Save</button>
                    <button
                      onClick={() => setPackageEditOpen(false)}
                      className="text-xs px-2 py-1 rounded text-stone-500 hover:bg-stone-100"
                      data-testid="package-edit-cancel"
                    >Cancel</button>
                  </div>
                ) : (
                  <>
                    <span className="text-base font-semibold text-emerald-800" data-testid="package-rule-label">
                      {totals.packageRuleName} · {totals.packageDiscountPct}% off
                    </span>
                    {discountRule?.valid_till && !packageOverride && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid="package-savings-expiry">
                        ⏳ Valid till {new Date(discountRule.valid_till).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <button
                      onClick={() => { setPackageEditName(totals.packageRuleName); setPackageEditPct(totals.packageDiscountPct); setPackageEditOpen(true); }}
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      title="Override label and percentage for this BOQ only"
                      data-testid="package-edit-btn"
                    >Edit</button>
                    <button
                      onClick={() => { setPackageDismissed(true); setPackageOverride(null); }}
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-stone-300 text-stone-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                      title="Don't apply Package Savings on this BOQ"
                      data-testid="package-dismiss-btn"
                    >Dismiss</button>
                  </>
                )}
              </div>
              <div className="px-6 py-5 bg-blue-50/20 border-l border-blue-100 text-right"><span className="text-base font-bold text-emerald-700">-₹{Math.round(totals.packageDiscount).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-green-50/20 border-l border-green-100 text-right"><span className="text-base font-bold text-emerald-700">-₹{Math.round(totals.actPackageDiscount).toLocaleString()}</span></div>
              <div className="px-6 py-5 bg-emerald-50 border-l-2 border-emerald-300 text-right"><span className="text-sm text-stone-400">-</span></div>
              <div className="px-6 py-5 bg-amber-50/20 border-l border-amber-100 text-right"><span className="text-sm text-stone-400">-</span></div>
            </div>
          )}
          {/* When dismissed, show a small subtle "re-enable" hint */}
          {packageDismissed && discountRule && (
            <div className="grid grid-cols-5 border-b border-stone-200 bg-stone-50/50" data-testid="package-dismissed-row">
              <div className="px-6 py-2 col-span-5 text-xs text-stone-500">
                Package savings ({discountRule.name} · {discountRule.discount_pct}%) dismissed for this BOQ.
                <button onClick={() => setPackageDismissed(false)} className="ml-2 text-emerald-700 font-medium hover:underline" data-testid="package-restore-btn">Restore</button>
              </div>
            </div>
          )}
          {/* GRAND TOTAL */}
          <div className="grid grid-cols-5 bg-stone-900" data-testid="summary-grand-total-row">
            <div className="px-6 py-5">
              <span className="text-lg font-black text-white uppercase tracking-wide">Grand Total</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              <span className="text-lg font-black text-white" data-testid="summary-grand-total-est">₹{Math.round(totals.estGrand).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              <span className="text-lg font-black text-white" data-testid="summary-grand-total-act">₹{Math.round(totals.actGrand).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 border-l-2 border-emerald-400 text-right bg-emerald-900/40">
              <span className="text-lg font-black text-emerald-300" data-testid="summary-grand-total-billable">₹{Math.round(totals.billableGrand).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              {(() => { const v = Math.round(totals.actGrand - totals.estGrand); return <span className={`text-lg font-black ${v > 0 ? 'text-red-400' : v < 0 ? 'text-green-400' : 'text-white'}`}>{v !== 0 ? (v > 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString() : '-'}</span>; })()}
            </div>
          </div>
          {/* iter-312p/q/t: Client Payments Applied — Actual-side only.
              No auto-from-ledger anymore. Show a hint of available cash. */}
          {!totals.vendorScope && (<>
          <div className="grid grid-cols-5 border-b border-stone-200" data-testid="summary-client-payments-row">
            <div className="px-6 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-stone-700">Client Payments Applied <span className="text-stone-400">(−)</span></span>
                {totals.usingAppliedList && (
                  <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" title={`${totals.appliedPaymentsCount} payment(s) applied`} data-testid="applied-payments-pill">
                    {totals.appliedPaymentsCount} payment{totals.appliedPaymentsCount === 1 ? '' : 's'} applied
                  </span>
                )}
                {!totals.usingAppliedList && totals.clientPaymentsOverridden && (
                  <button onClick={() => setClientPaymentsOverride(null)} className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200" title="Click to clear override" data-testid="reset-client-payments-auto">Override · clear</button>
                )}
                <button onClick={() => setApplyPaymentsDialogOpen(true)} className="text-[10px] text-blue-600 hover:underline font-medium" data-testid="manage-applied-payments-btn">
                  {totals.usingAppliedList ? 'Manage applied payments' : 'Apply payments ↗'}
                </button>
                {/* iter-312t: inline record-payment shortcuts */}
                <button onClick={() => { setRecordPaymentMode('client'); setRecordPaymentOpen(true); }} className="text-[10px] text-emerald-700 hover:underline font-medium" data-testid="inline-record-client-payment-btn" title="Record a new client payment (writes to Project Accounts → Transactions in real time)">
                  + Record client payment
                </button>
                <button onClick={() => { setRecordPaymentMode('vendor'); setRecordPaymentOpen(true); }} className="text-[10px] text-blue-700 hover:underline font-medium" data-testid="inline-record-vendor-payment-btn" title="Record a new vendor payment made (writes to Project Accounts in real time)">
                  + Record vendor payment
                </button>
                {worksheet?.project_id && (
                  <a href={`/project-accounts?projectId=${worksheet.project_id}&tab=transactions`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline" data-testid="open-project-ledger-link">Open ledger ↗</a>
                )}
              </div>
              {/* iter-312t: hint subtitle showing what's available in the project ledger */}
              {totals.clientPaymentsAuto > 0 && !totals.usingAppliedList && (
                <div className="text-[11px] text-stone-500 mt-1" data-testid="payments-available-hint">
                  <span className="font-semibold text-emerald-700">₹{Math.round(totals.clientPaymentsAuto).toLocaleString()}</span> available from this project's ledger — click <em>Apply payments</em> to use.
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-blue-50/20 border-l border-blue-100 text-right">
              <span className="text-sm text-stone-300">—</span>
            </div>
            <div className="px-6 py-4 bg-green-50/20 border-l border-green-100 text-right" title={totals.usingAppliedList ? 'Sum of selected payments. Click "Manage applied payments" to change.' : 'Click to override manually, or use Apply payments to pick from ledger.'}>
              {totals.usingAppliedList ? (
                <span className="text-sm font-semibold text-red-600" data-testid="client-payments-applied-display">−₹{Math.round(totals.clientPaymentsApplied).toLocaleString()}</span>
              ) : (
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={totals.clientPaymentsOverridden ? totals.clientPaymentsApplied : 0}
                  onChange={(e) => setClientPaymentsOverride(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                  className="w-32 inline-block text-right text-sm font-semibold text-red-600 bg-transparent border-0 outline-none focus:bg-white focus:ring-1 focus:ring-emerald-300 rounded px-1"
                  data-testid="client-payments-applied-input"
                />
              )}
            </div>
            <div className="px-6 py-4 bg-emerald-50 border-l-2 border-emerald-300 text-right" title="Same client payments — they reduce the Billable Payment Receivable below.">
              <span className="text-sm font-semibold text-red-600" data-testid="client-payments-applied-billable">{totals.clientPaymentsApplied > 0 ? '−₹' + Math.round(totals.clientPaymentsApplied).toLocaleString() : '-'}</span>
            </div>
            <div className="px-6 py-4 bg-amber-50/20 border-l border-amber-100 text-right">
              <span className="text-sm text-stone-400">-</span>
            </div>
          </div>
          {/* iter-312p/t: Credit Notes Applied — Actual-side only, no auto-from-ledger */}
          <div className="grid grid-cols-5 border-b border-stone-200" data-testid="summary-credit-notes-row">
            <div className="px-6 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-stone-700">Credit Notes Applied <span className="text-stone-400">(−)</span></span>
                {totals.creditNotesOverridden && (
                  <button onClick={() => setCreditNotesOverride(null)} className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200" title="Click to clear override" data-testid="reset-credit-notes-auto">Override · clear</button>
                )}
                <button onClick={() => setCreditNotesDialogOpen(true)} className="text-[10px] text-blue-600 hover:underline" data-testid="manage-credit-notes-btn">Manage credit notes</button>
              </div>
              {totals.creditNotesAuto > 0 && !totals.creditNotesOverridden && (
                <div className="text-[11px] text-stone-500 mt-1" data-testid="credit-notes-available-hint">
                  <span className="font-semibold text-emerald-700">₹{Math.round(totals.creditNotesAuto).toLocaleString()}</span> in credit notes available for this project.
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-blue-50/20 border-l border-blue-100 text-right">
              <span className="text-sm text-stone-300">—</span>
            </div>
            <div className="px-6 py-4 bg-green-50/20 border-l border-green-100 text-right">
              <input
                type="number"
                min="0"
                step="1"
                value={totals.creditNotesOverridden ? totals.creditNotesApplied : 0}
                onChange={(e) => setCreditNotesOverride(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                className="w-32 inline-block text-right text-sm font-semibold text-red-600 bg-transparent border-0 outline-none focus:bg-white focus:ring-1 focus:ring-emerald-300 rounded px-1"
                data-testid="credit-notes-applied-input"
              />
            </div>
            <div className="px-6 py-4 bg-emerald-50 border-l-2 border-emerald-300 text-right" title="Same credit notes — they reduce the Billable Payment Receivable below.">
              <span className="text-sm font-semibold text-red-600" data-testid="credit-notes-applied-billable">{totals.creditNotesApplied > 0 ? '−₹' + Math.round(totals.creditNotesApplied).toLocaleString() : '-'}</span>
            </div>
            <div className="px-6 py-4 bg-amber-50/20 border-l border-amber-100 text-right">
              <span className="text-sm text-stone-400">-</span>
            </div>
          </div>
          </>)}
          {/* iter-312t: PAYMENT RECEIVABLE — same band as Grand Total. Per user spec
              the value renders under Actual (Site) AND Billable (iter-329); the
              Estimated + Variance columns show an em-dash because deductions are
              cash-basis adjustments. */}
          <div className="grid grid-cols-5 bg-stone-900" data-testid="summary-payment-receivable-row">
            <div className="px-6 py-5">
              <span className="text-lg font-black text-emerald-300 uppercase tracking-wide">Payment Receivable</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              <span className="text-lg font-black text-stone-500">—</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              <span className="text-lg font-black text-emerald-300">₹{Math.round(totals.actNetReceivable).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 border-l-2 border-emerald-400 text-right bg-emerald-900/40">
              <span className="text-lg font-black text-emerald-200" data-testid="summary-billable-payment-receivable">₹{Math.round(totals.billableNetReceivable).toLocaleString()}</span>
            </div>
            <div className="px-6 py-5 border-l border-stone-700 text-right">
              <span className="text-lg font-black text-stone-500">—</span>
            </div>
          </div>
        </div>

        {/* Discount + Bulk GST Section */}
        <div className="mt-4 bg-white rounded-lg border border-stone-200 p-4">
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <h3 className="font-semibold text-stone-700 mb-2">Discount</h3>
              <div className="flex items-center gap-3 flex-wrap">
                <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="h-9 w-40 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-stone-400" data-testid="discount-type-select">
                  <option value="value">Fixed Amount (₹)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
                <Input type="number" min="0" value={discountValue} onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)} className="w-28" placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 5000'} data-testid="discount-value-input" />
                {discountType === 'percentage' && discountValue > 0 && (
                  <span className="text-sm text-stone-500">= ₹{Math.round(totals.estBasic * discountValue / 100).toLocaleString()}</span>
                )}
                {/* iter-315e: Before-tax / After-tax toggle (per-BOQ override) */}
                <div className="inline-flex items-center rounded-md border border-stone-300 overflow-hidden text-xs" data-testid="discount-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setDiscountApplyMode('before_tax')}
                    className={`px-2.5 py-1.5 font-medium transition-colors ${discountApplyMode === 'before_tax' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                    data-testid="discount-mode-before-tax"
                    title="Discount applied on subtotal BEFORE GST"
                  >
                    Before Tax
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountApplyMode('after_tax')}
                    className={`px-2.5 py-1.5 font-medium transition-colors ${discountApplyMode === 'after_tax' ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                    data-testid="discount-mode-after-tax"
                    title="Discount applied on GST-inclusive total AFTER tax"
                  >
                    After Tax
                  </button>
                </div>
              </div>
            </div>
            <div className="border-l border-stone-200 pl-6">
              <h3 className="font-semibold text-stone-700 mb-2">GST</h3>
              <div className="flex items-center gap-3">
                <select value={bulkGstType || 'percentage'} onChange={(e) => setBulkGstType(e.target.value)} className="h-9 w-40 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-stone-400" data-testid="bulk-gst-type-select">
                  <option value="value">Fixed Amount (₹)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
                <Input type="number" min="0" placeholder={bulkGstType === 'value' ? 'e.g. 5000' : 'e.g. 18'} value={bulkGst} onChange={(e) => setBulkGst(e.target.value)} className="w-28" data-testid="bulk-gst-input" />
                <Button size="sm" onClick={applyBulkGst} data-testid="bulk-gst-apply">Apply</Button>
                {bulkGstType === 'percentage' && parseFloat(bulkGst) > 0 && (
                  <span className="text-sm text-stone-500">= ₹{Math.round(totals.estBasic * parseFloat(bulkGst) / 100).toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Overhead Costs Section */}
        <div className="mt-4 bg-white rounded-lg border border-stone-200 p-4">
          <h3 className="font-semibold text-stone-700 mb-2">Overhead Costs (added to Grand Total)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-stone-600 mb-1 block">Freight (%)</label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="0.5" value={freightPct} onChange={(e) => setFreightPct(parseFloat(e.target.value) || 0)} className="w-24" data-testid="freight-pct" />
                {freightPct > 0 && <span className="text-xs text-stone-500">= ₹{Math.round(totals.freight).toLocaleString()}</span>}
              </div>
            </div>
            <div>
              <label className="text-sm text-stone-600 mb-1 block">Labour Handling (%)</label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="0.5" value={labourHandlingPct} onChange={(e) => setLabourHandlingPct(parseFloat(e.target.value) || 0)} className="w-24" data-testid="labour-handling-pct" />
                {labourHandlingPct > 0 && <span className="text-xs text-stone-500">= ₹{Math.round(totals.labourHandling).toLocaleString()}</span>}
              </div>
            </div>
            <div>
              <label className="text-sm text-stone-600 mb-1 block">Consultancy</label>
              <div className="flex items-center gap-2">
                <select
                  value={consultancyType}
                  onChange={(e) => setConsultancyType(e.target.value)}
                  className="px-2 py-2 text-sm border border-stone-300 rounded-md bg-white"
                  data-testid="consultancy-type"
                  title="Choose how Consultancy is computed: Percentage of post-discount basic, or a Fixed lump sum"
                >
                  <option value="percentage">Percentage</option>
                  <option value="amount">Fixed</option>
                </select>
                {consultancyType === 'percentage' ? (
                  <>
                    <Input type="number" min="0" step="0.5" value={consultancyPct} onChange={(e) => setConsultancyPct(parseFloat(e.target.value) || 0)} className="w-20" data-testid="consultancy-pct" />
                    <span className="text-xs text-stone-500">%</span>
                    {consultancyPct > 0 && <span className="text-xs text-stone-500">{totals.vendorScope ? '· client view only' : `= ₹${Math.round(totals.consultancy).toLocaleString()}`}</span>}
                  </>
                ) : (
                  <>
                    <span className="text-xs text-stone-500">₹</span>
                    <Input type="number" min="0" step="100" value={consultancyValue} onChange={(e) => setConsultancyValue(parseFloat(e.target.value) || 0)} className="w-28" data-testid="consultancy-value" />
                    {consultancyValue > 0 && <span className="text-xs text-stone-500">{totals.vendorScope ? '· client view only' : `= ₹${Math.round(totals.consultancy).toLocaleString()}`}</span>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* iter-286: Admin Visibility Toggles */}
        <div className="mt-4 bg-white rounded-lg border border-stone-200 p-4" data-testid="admin-visibility-toggles">
          <h3 className="font-semibold text-stone-700 mb-3">Visibility Controls (Admin)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* iter-303: Package Savings feature removed — toggle hidden. */}
            <label className="flex items-start gap-3 p-3 rounded-md border border-stone-200 hover:bg-stone-50 cursor-pointer">
              <input
                type="checkbox"
                checked={showConsultancyToVendor}
                onChange={(e) => setShowConsultancyToVendor(e.target.checked)}
                className="mt-1"
                data-testid="show-consultancy-to-vendor-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-stone-800">Show Consultancy in Vendor Published BOQ</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  By default Consultancy charges are <span className="font-semibold">hidden</span> from the vendor public view. Tick this only if the vendor needs to see the consultancy line.
                </div>
              </div>
            </label>
            {/* iter-304: Client PDF Actual/Variance visibility toggle */}
            <label className="flex items-start gap-3 p-3 rounded-md border border-stone-200 hover:bg-stone-50 cursor-pointer">
              <input
                type="checkbox"
                checked={showActualVarianceInClientPdf}
                onChange={(e) => setShowActualVarianceInClientPdf(e.target.checked)}
                className="mt-1"
                data-testid="show-actual-variance-in-client-pdf-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-stone-800">Show Actual / Variance in Client PDF</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  By default the Client PDF/Excel shows <span className="font-semibold">Estimated only</span>. Enable this to reveal Actual + Variance columns and the full Summary block (Basic, Overheads, GST, Grand Total — estimated vs actual).
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* iter-329: Locked Snapshots — immutable BOQ versions */}
        <div className="mt-4 bg-white rounded-lg border border-stone-200 p-4" data-testid="locked-snapshots-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-stone-700 flex items-center gap-2">
                <Pin size={16} className="text-violet-600" />
                Locked Snapshots
                {snapshots.length > 0 && (
                  <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200" data-testid="snapshots-count">
                    {snapshots.length}
                  </span>
                )}
              </h3>
              <p className="text-xs text-stone-500 mt-0.5">
                Each snapshot freezes the current BOQ state into an immutable PDF + Excel pair stored in MongoDB. Use these as audit-grade reference points for client sign-offs and dispute resolution.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-violet-700 border-violet-300 bg-violet-50/40 hover:bg-violet-100 font-semibold"
              onClick={() => { setSnapshotLabel(`Snapshot ${snapshots.length + 1}`); setSnapshotNotes(''); setSnapshotDialogOpen(true); }}
              data-testid="snapshots-card-create-btn"
            >
              <Pin size={14} className="mr-1" />New Snapshot
            </Button>
          </div>
          {snapshotsLoading ? (
            <div className="text-sm text-stone-400 py-4 text-center">Loading snapshots…</div>
          ) : snapshots.length === 0 ? (
            <div className="text-sm text-stone-400 py-6 text-center border border-dashed border-stone-200 rounded-md" data-testid="snapshots-empty-state">
              No snapshots yet. Click <span className="font-semibold text-violet-700">Lock &amp; Snapshot</span> in the toolbar (or above) to capture the current state.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="snapshots-table">
                <thead className="bg-stone-50 text-xs text-stone-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">Version</th>
                    <th className="px-3 py-2 text-left">Created</th>
                    <th className="px-3 py-2 text-left">By</th>
                    <th className="px-3 py-2 text-right">Grand Total</th>
                    <th className="px-3 py-2 text-right">Billable So Far</th>
                    <th className="px-3 py-2 text-center">Files</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => (
                    <tr key={s.id} className="border-t border-stone-100 hover:bg-violet-50/30" data-testid={`snapshot-row-${s.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-stone-800 flex items-center gap-2">
                          <Lock size={12} className="text-violet-600" />
                          <span data-testid={`snapshot-label-${s.id}`}>{s.version_label}</span>
                        </div>
                        {s.notes && <div className="text-[11px] text-stone-500 mt-0.5 max-w-[280px] truncate" title={s.notes}>{s.notes}</div>}
                      </td>
                      <td className="px-3 py-2 text-stone-600 text-xs">
                        {(() => {
                          try {
                            const d = new Date(s.created_at);
                            return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                          } catch { return s.created_at; }
                        })()}
                      </td>
                      <td className="px-3 py-2 text-stone-600 text-xs">{s.created_by_name || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-stone-800" data-testid={`snapshot-grand-${s.id}`}>
                        ₹{Math.round(s.grand_total || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700" data-testid={`snapshot-billable-${s.id}`}>
                        ₹{Math.round(s.billable_total || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <FeatureLocked capability="can_export_pdf" label="PDF export is Essential+">
                            <button
                              onClick={() => guardExport(handleDownloadSnapshot)(s, 'pdf')}
                              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                              data-testid={`snapshot-download-pdf-${s.id}`}
                              title="Download PDF"
                            >
                              <FileText size={12} />PDF
                            </button>
                          </FeatureLocked>
                          <FeatureLocked capability="can_export_excel" label="Excel export is Essential+">
                            <button
                              onClick={() => guardExport(handleDownloadSnapshot)(s, 'xlsx')}
                              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                              data-testid={`snapshot-download-xlsx-${s.id}`}
                              title="Download Excel"
                            >
                              <FileSpreadsheet size={12} />Excel
                            </button>
                          </FeatureLocked>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          {/* iter-386u-ak — Clone snapshot into a fresh, editable BOQ */}
                          <button
                            onClick={() => handleCloneSnapshot(s)}
                            className="inline-flex items-center text-xs text-sky-700 hover:bg-sky-50 rounded p-1"
                            data-testid={`snapshot-clone-${s.id}`}
                            title="Edit in new tab — clones this snapshot into a fresh BOQ"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteSnapshot(s)}
                            className="inline-flex items-center text-xs text-red-600 hover:bg-red-50 rounded p-1"
                            data-testid={`snapshot-delete-${s.id}`}
                            title="Delete (admin) — reason required"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Terms & Conditions Section (AI-generated, amendable) */}
        <div className="mt-4 bg-white rounded-lg border border-stone-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-stone-700">Terms & Conditions</h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTermsExpandedOpen(true)}
                data-testid="expand-terms-btn"
                title="Open in a larger editor"
              >
                <Expand size={14} className="mr-1" /> Expand
              </Button>
              <FeatureLocked capability="can_use_ai_tnc" label="AI T&C is Essential+">
                <Button variant="outline" size="sm" disabled={generatingTerms} onClick={async () => {
                  setGeneratingTerms(true);
                  try {
                    const resp = await axios.post(`${API}/boq-worksheets/${worksheetId}/generate-terms`, {}, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (resp.data.terms) { setTermsConditions(resp.data.terms); toast.success('T&C generated — combines the 43-clause skeleton with AI enrichment from this BOQ'); }
                    else toast.error(resp.data.error || 'Failed to generate terms');
                  } catch { toast.error('Failed to generate terms'); }
                  finally { setGeneratingTerms(false); }
                }} data-testid="generate-terms-btn">
                  <Sparkles size={14} className="mr-1" />
                  {generatingTerms ? 'Generating…' : 'Generate T&C (AI + 43-Clause)'}
                  <CreditBadge toolKey="ai_tnc" className="ml-1.5" />
                </Button>
              </FeatureLocked>
              {/* iter-383 — Standard 43-Clause button removed. The single
                  "Generate T&C" button above now renders the 43-clause
                  skeleton via `generate-terms` and enriches each clause
                  with BOQ-specific context (brands, categories, payment
                  %s). Users get one deterministic + specific document. */}
            </div>
          </div>
          <Textarea
            value={termsConditions}
            onChange={(e) => setTermsConditions(e.target.value)}
            placeholder="Add terms & conditions for this BOQ. Click 'Generate with AI' to auto-generate based on line items and payment schedule..."
            className="min-h-[150px] text-sm"
            data-testid="terms-conditions-textarea"
          />
          <p className="text-xs text-stone-400 mt-1">These terms will be included in the published BOQ and email.</p>
        </div>

        {/* iter-370: Client & Vendor Documents replicated inside the Edit BOQ page */}
        {worksheet?.project_id && (
          <ProjectDocsPanel projectId={worksheet.project_id} />
        )}

        {/* iter-372: Annexure — Material / Brand / Installation grid
            with two-way sync to each line item's description.
            iter-377: Legend filter chips (shared with the parent BOQ table
            via `categoryFilter` / `onCategoryFilterChange`), plus two
            independent public-visibility toggles and idempotent
            AI-rewrite tracking. */}
        <AnnexureTable
          items={editItems}
          worksheetId={worksheetId}
          projectSegment={worksheet?.project_segment || 'residential_apartment'}
          onItemPatch={(idx, patch) => {
            setEditItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
          }}
          categoryFilter={activeLegendFilter || ''}
          onCategoryFilterChange={(code) => setActiveLegendFilter(code || null)}
          showToClient={annexureShowToClient}
          onShowToClientChange={setAnnexureShowToClient}
          showToVendor={annexureShowToVendor}
          onShowToVendorChange={setAnnexureShowToVendor}
          aiRewrittenIds={aiRewrittenIds}
          onMarkRewritten={(ids) => setAiRewrittenIds((prev) => {
            const next = new Set(prev);
            (ids || []).forEach((id) => id && next.add(id));
            return next;
          })}
          findQuery={findQuery}
          findCaseSensitive={findCaseSensitive}
        />
      </div>

      {/* === iter-307: Pricing-Model Preview Dialog === */}
      <PricingModelPreviewDialog
        open={pricingPreviewOpen}
        onOpenChange={setPricingPreviewOpen}
        items={editItems}
        worksheet={worksheet}
      />

      {/* iter-338z: AI Task Creator dialog (also used for per-line AI refine
          via `refineTargetItem` — user gets the full toolkit: preset,
          project segment, image/PDF context, chat-to-edit, editable card,
          rate suggestions — scoped to that single line). */}
      {(aiTaskCreatorOpen || aiRefineOpen) && (
      <Suspense fallback={null}>
      <AITaskCreatorDialog
        open={aiTaskCreatorOpen || aiRefineOpen}
        onOpenChange={(v) => {
          if (aiRefineOpen) setAiRefineOpen(v);
          else setAiTaskCreatorOpen(v);
          if (!v) setAiRefineTargetIdx(null);
        }}
        worksheetId={worksheetId}
        /* iter-383 — pre-fill the dialog's project segment from the
           worksheet's `project_segment`. Editable — admin can still switch. */
        initialProjectSegment={(project?.project_type || worksheet?.project_type) ? segmentFromProjectType(project?.project_type || worksheet?.project_type) : (worksheet?.project_segment || 'residential_apartment')}
        refineTargetItem={aiRefineOpen && aiRefineTargetIdx != null ? editItems[aiRefineTargetIdx] : null}
        onRefineApplied={(patch) => {
          if (aiRefineTargetIdx == null) return;
          setEditItems((prev) => prev.map((it, i) => (i === aiRefineTargetIdx ? { ...it, ...patch } : it)));
        }}
        onItemsCommitted={(newItems) => {
          // iter-386u-bs — Zero-Refresh Save: append server-returned rows
          // in-place and scroll the FIRST new row into view. Skip fetchWorksheet
          // so the studio's scroll position + focus state don't blink.
          if (!Array.isArray(newItems) || newItems.length === 0) return;
          const enriched = newItems.map((it) => ({ ...it, _editId: it.id || `row_${Math.random()}` }));
          setEditItems((prev) => [...prev, ...enriched]);
          setWorksheet((w) => (w ? { ...w, items: [...(w.items || []), ...enriched] } : w));
          setTimeout(() => {
            const firstNew = enriched[0];
            if (firstNew?._editId) {
              const el = document.querySelector(`[data-row-id="${firstNew._editId}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('boq-row-focus-flash');
                setTimeout(() => el.classList.remove('boq-row-focus-flash'), 2400);
              }
            }
          }, 200);
        }}
      />
      </Suspense>
      )}

      {/* iter-371: OTP gate + view-limit admin config */}
      <OTPConfigDialog
        open={otpConfigOpen}
        onOpenChange={setOtpConfigOpen}
        worksheet={worksheet}
        onSaved={(patch) => setWorksheet((w) => (w ? { ...w, ...patch } : w))}
      />

      {/* iter-372: Expanded T&C editor — big canvas for reading & editing
          the AI-generated Terms & Conditions comfortably. */}
      <ConsultancyInvoiceDialog
        open={consultancyInvoiceOpen}
        onOpenChange={setConsultancyInvoiceOpen}
        worksheetId={worksheetId}
        onPaymentRecorded={() => fetchWorksheet?.()}
      />
      <FindReplaceBar
        open={findReplaceOpen}
        onOpenChange={setFindReplaceOpen}
        anchorSelector='[data-testid="boq-edit-sticky-header"]'
        items={editItems}
        undoAvailable={!!findReplaceUndoSnapshot}
        onQueryChange={handleFindQueryChange}
        onUndo={() => {
          if (!findReplaceUndoSnapshot) return;
          setEditItems(findReplaceUndoSnapshot);
          setFindReplaceUndoSnapshot(null);
          toast.success('Reverted the last Replace All');
        }}
        onReplaceAll={(patches) => {
          // Snapshot BEFORE mutating so Undo restores the exact prior state.
          setFindReplaceUndoSnapshot(editItems.map((it) => ({ ...it })));
          setEditItems((prev) => {
            const next = [...prev];
            patches.forEach(({ actualIndex, patch }) => {
              if (next[actualIndex]) next[actualIndex] = { ...next[actualIndex], ...patch };
            });
            return next;
          });
        }}
      />

      <Dialog open={termsExpandedOpen} onOpenChange={setTermsExpandedOpen}>
        <DialogContent
          className="p-6 flex flex-col gap-3 sm:max-w-none"
          style={{ width: '95vw', maxWidth: '95vw', height: '90vh', maxHeight: '90vh' }}
          data-testid="terms-expanded-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} className="text-amber-700" />
              Terms & Conditions — Expanded Editor
            </DialogTitle>
            <p className="text-xs text-stone-500 mt-0.5" id="terms-expanded-desc">
              Edit generated terms in a larger canvas. Changes sync back to the small editor automatically.
            </p>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="outline"
              size="sm"
              disabled={generatingTerms}
              onClick={async () => {
                setGeneratingTerms(true);
                try {
                  const resp = await axios.post(`${API}/boq-worksheets/${worksheetId}/generate-terms`, {}, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                  });
                  if (resp.data.terms) { setTermsConditions(resp.data.terms); toast.success('Terms generated!'); }
                  else toast.error(resp.data.error || 'Failed to generate terms');
                } catch { toast.error('Failed to generate terms'); }
                finally { setGeneratingTerms(false); }
              }}
              data-testid="expanded-generate-terms-btn"
            >
              <Sparkles size={14} className="mr-1" />
              {generatingTerms ? 'Generating...' : 'Regenerate with AI'}
            </Button>
            <span className="text-xs text-stone-500">
              Edits sync back to the main T&C field automatically. Close when done.
            </span>
          </div>
          <Textarea
            value={termsConditions}
            onChange={(e) => setTermsConditions(e.target.value)}
            placeholder="Terms and conditions…"
            className="flex-1 min-h-0 text-sm font-mono resize-none"
            data-testid="terms-expanded-textarea"
          />
          <div className="flex justify-end pt-2">
            <Button onClick={() => setTermsExpandedOpen(false)} data-testid="terms-expanded-close-btn">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Merge Estimate Modal === */}
      <MergeItemsDialog
        open={mergeModalOpen}
        onOpenChange={setMergeModalOpen}
        merging={merging}
        estimateId={mergeEstimateId}
        onEstimateIdChange={setMergeEstimateId}
        createTasks={mergeCreateTasks}
        onCreateTasksChange={setMergeCreateTasks}
        availableEstimates={availableEstimates}
        onConfirm={performMerge}
      />
      {/* iter-329: Lock & Snapshot Dialog */}
      <SnapshotDialog
        open={snapshotDialogOpen}
        onOpenChange={setSnapshotDialogOpen}
        label={snapshotLabel}
        onLabelChange={setSnapshotLabel}
        notes={snapshotNotes}
        onNotesChange={setSnapshotNotes}
        busy={snapshotBusy}
        snapshotsCount={snapshots.length}
        project={project}
        worksheet={worksheet}
        onConfirm={handleCreateSnapshot}
      />

      {/* iter-292: Vendor Quote Link Dialog — replicated UX from BOQ Worksheets list */}
      <VendorQuoteDialog
        open={vendorQuoteDialogOpen}
        onOpenChange={setVendorQuoteDialogOpen}
        data={vendorQuoteData}
        onDataChange={setVendorQuoteData}
        loading={vendorQuoteLoading}
        onSubmit={handleCreateVendorQuoteLink}
        project={project}
        worksheet={worksheet}
        result={vendorQuoteResult}
        onCopy={copyGeneratedVendorQuoteUrl}
        onReset={() => { setVendorQuoteResult(null); setVendorQuoteData({ vendor_name: '', vendor_email: '', instructions: '', expiry_days: 14, category_codes: [] }); }}
        categories={boqCategories || []}
      />
      {/* iter-312p: Credit Notes management modal triggered from Edit BOQ summary */}
      <CreditNotesDialog
        open={creditNotesDialogOpen}
        onOpenChange={setCreditNotesDialogOpen}
        projectId={worksheet?.project_id}
        projectName={project?.name || worksheet?.name}
        onChange={fetchWorksheet}
      />
      {/* iter-312q: Apply Payments to BOQ modal */}
      <ApplyPaymentsDialog
        open={applyPaymentsDialogOpen}
        onOpenChange={setApplyPaymentsDialogOpen}
        projectId={worksheet?.project_id}
        worksheetId={worksheetId}
        initialApplied={appliedPayments}
        onApply={(list) => {
          setAppliedPayments(list);
          toast.success(`Applied ${list.length} payment${list.length === 1 ? '' : 's'} — click Save Changes to persist`);
        }}
      />
      {/* iter-312t: Inline Record Payment (Client OR Vendor). Writes to db.payments
          via the existing Project Accounts endpoints, then refreshes the worksheet
          so the new payment shows up immediately in Apply Payments. */}
      <RecordPaymentDialog
        open={recordPaymentOpen}
        onOpenChange={setRecordPaymentOpen}
        projectId={worksheet?.project_id}
        mode={recordPaymentMode}
        onRecorded={fetchWorksheet}
        worksheetId={worksheetId}
        legendCategories={boqCategories.map(c => ({
          code: c.group || c.code,
          group: c.group,
          name: c.group_name || c.name,
          group_name: c.group_name,
        }))}
      />
      {/* iter-338i: Profitability tab */}
      <ProfitabilityDialog
        open={profitabilityOpen}
        onOpenChange={setProfitabilityOpen}
        worksheetId={worksheetId}
        worksheetNumber={worksheet?.worksheet_number}
        legendCategories={boqCategories.map(c => ({
          code: c.group || c.code,
          group: c.group,
          name: c.group_name || c.name,
          group_name: c.group_name,
        }))}
      />
      {/* iter-313e: Publish-to-{Client|Vendor} validity picker dialog with 3/7/14/20/30/60/90 day quick-picks */}
      <PublishValidityDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        target={publishDialogTarget}
        validityDays={publishValidityDays}
        onValidityChange={setPublishValidityDays}
        publishing={publishing}
        publishingVendor={publishingVendor}
        onPublishClient={handlePublishToClient}
        onPublishVendor={handlePublishToVendor}
        worksheet={worksheet}
        displayInDashboards={publishDisplayInDashboards}
        onDisplayInDashboardsChange={setPublishDisplayInDashboards}
      />
      {/* iter-338o: AI Assistant available on BOQ Edit (page doesn't use Layout).
          iter-338t: pass page-scope so email/report intents auto-resolve
          to the currently-open worksheet + project + client. */}
      <AIChatWidget
        scope={worksheet ? {
          worksheet_id: worksheet.id,
          worksheet_number: worksheet.worksheet_number || worksheet.name,
          project_id: worksheet.project_id,
          project_name: worksheet.project_name || worksheet.name,
          client_id: worksheet.client_id || null,
          client_name: worksheet.client_name || null,
          client_email: worksheet.client_email || null,
        } : null}
      />
      {/* iter-338z2: Floating "AI Create Task" launcher — sits above the AI
          chat launcher in the bottom-right so the two AI features are
          co-located in a discoverable "AI dock". */}
      <button
        type="button"
        onClick={() => planGate('can_use_ai_task_creator', 'AI Task Creator is available on Essential and above.') && setAiTaskCreatorOpen(true)}
        className="fixed bottom-24 right-5 z-40 w-12 h-12 rounded-full shadow-lg bg-gradient-to-br from-violet-500 to-violet-700 text-white flex items-center justify-center hover:scale-110 transition-transform"
        title="AI Create Task (Ctrl+Shift+A)"
        data-testid="ai-create-task-btn"
      >
        <Sparkles size={20} />
        <span className="absolute -top-1 -right-1"><CreditBadge toolKey="ai_task_creator" variant="onDark" /></span>
      </button>

      {/* iter-338z16 — AI Arrange date-range dialog */}
      <Dialog open={aiArrangeDialogOpen} onOpenChange={(v) => { if (!aiArranging) setAiArrangeDialogOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="ai-arrange-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <Sparkles size={16} className="text-amber-700" />
              AI Arrange schedule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900">
              Pick the project window and the AI will schedule every linked task inside it, respecting trade-order dependencies. Leave either date blank to use the current anchor / no hard deadline. Dependent tasks propagate automatically.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Project Start</Label>
                <Input
                  type="date"
                  value={aiArrangeStart}
                  onChange={(e) => setAiArrangeStart(e.target.value)}
                  className="bg-white text-stone-900 mt-0.5"
                  data-testid="ai-arrange-start-date"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Project End (optional)</Label>
                <Input
                  type="date"
                  value={aiArrangeEnd}
                  onChange={(e) => setAiArrangeEnd(e.target.value)}
                  className="bg-white text-stone-900 mt-0.5"
                  data-testid="ai-arrange-end-date"
                />
              </div>
            </div>
            {/* iter-386u-bw — Timeline Anchors (First / Last task pins) */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-stone-200">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">First Task <span className="text-amber-600">(anchor start)</span></Label>
                <select
                  value={aiArrangeFirstTaskId}
                  onChange={(e) => setAiArrangeFirstTaskId(e.target.value)}
                  className="w-full h-9 text-sm bg-white border border-stone-300 rounded px-2 mt-0.5"
                  data-testid="ai-arrange-first-task"
                >
                  <option value="">— auto (earliest phase) —</option>
                  {(editItems || [])
                    .filter((it) => (it.task_id || it.source_task_id) && !(it.item_type === 'header' || it.is_header))
                    .map((it) => (
                      <option key={it.task_id || it.source_task_id} value={it.task_id || it.source_task_id}>
                        {(it.task_title || 'Untitled').slice(0, 40)}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Last Task <span className="text-amber-600">(anchor end)</span></Label>
                <select
                  value={aiArrangeLastTaskId}
                  onChange={(e) => setAiArrangeLastTaskId(e.target.value)}
                  className="w-full h-9 text-sm bg-white border border-stone-300 rounded px-2 mt-0.5"
                  data-testid="ai-arrange-last-task"
                >
                  <option value="">— auto (deepest phase) —</option>
                  {(editItems || [])
                    .filter((it) => (it.task_id || it.source_task_id) && !(it.item_type === 'header' || it.is_header))
                    .map((it) => (
                      <option key={it.task_id || it.source_task_id} value={it.task_id || it.source_task_id}>
                        {(it.task_title || 'Untitled').slice(0, 40)}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <p className="text-[10px] text-stone-500">
              Anchors override phase inference — the pinned tasks are hard-locked to the start / end dates you set above.
            </p>
            {aiArrangeStart && aiArrangeEnd && new Date(aiArrangeEnd) <= new Date(aiArrangeStart) && (
              <div className="text-xs text-red-600">End date must be after start date.</div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setAiArrangeDialogOpen(false)} disabled={aiArranging}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!worksheet?.project_id) return;
                if (aiArrangeStart && aiArrangeEnd && new Date(aiArrangeEnd) <= new Date(aiArrangeStart)) return;
                setAiArranging(true);
                try {
                  const params = new URLSearchParams({
                    project_id: worksheet.project_id,
                    worksheet_id: worksheetId,
                  });
                  if (aiArrangeStart) params.append('start_date', aiArrangeStart);
                  if (aiArrangeEnd) params.append('end_date', aiArrangeEnd);
                  if (aiArrangeFirstTaskId) params.append('first_task_id', aiArrangeFirstTaskId);
                  if (aiArrangeLastTaskId) params.append('last_task_id', aiArrangeLastTaskId);
                  const r = await axios.post(
                    `${API}/tasks/ai-arrange-and-apply?${params.toString()}`,
                    {}, { headers: authHeaders },
                  );
                  toast.success(r.data?.message || 'AI Arrange complete');
                  setAiArrangeDialogOpen(false);
                  // iter-359b: refresh BOTH the worksheet (so last-used dates
                  // are cached client-side) AND task-dates (so every row's
                  // start/end labels reflect the new AI-arranged schedule).
                  await Promise.all([fetchWorksheet(), fetchTaskDates()]);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'AI Arrange failed');
                } finally {
                  setAiArranging(false);
                }
              }}
              disabled={aiArranging}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="ai-arrange-run-btn"
            >
              <Sparkles size={14} className="mr-1" />
              {aiArranging ? 'Arranging…' : 'Run AI Arrange'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// iter-386u-al — Revision Chain chip.
// Renders a tiny "Rev N ← Rev N-1 ← … ← Original" pill next to the BOQ
// title. Only shows when the current worksheet has at least one ancestor.
function RevisionChainChip({ worksheetId }) {
  const [chain, setChain] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/revision-chain`, { headers: authHeaders });
        if (!cancelled) setChain(r.data?.chain || []);
      } catch { /* silent — endpoint may 403 for non-privileged users */ }
    })();
    return () => { cancelled = true; };
  }, [worksheetId]);

  if (!chain || chain.length < 2) return null;
  const depth = chain.length;
  const rootName = chain[chain.length - 1]?.name || 'Original';
  return (
    <div className="relative inline-block" data-testid="revision-chain-chip">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-200 uppercase tracking-wider"
        title="Revision lineage — click to expand"
      >
        <History size={10} />
        Rev · depth {depth - 1}
      </button>
      {open && (
        <div className="absolute z-[60] top-full left-0 mt-1 w-80 bg-white border border-stone-200 rounded-lg shadow-lg p-3 text-xs text-stone-700" data-testid="revision-chain-panel">
          <div className="font-semibold text-stone-900 mb-2">Revision lineage</div>
          <div className="text-[11px] text-stone-500 mb-2">Traced back to <span className="font-medium text-stone-800">{rootName}</span>.</div>
          <ol className="space-y-1">
            {chain.map((n, idx) => (
              <li key={n.id} className={`flex items-start gap-2 ${n.is_current ? 'bg-emerald-50 border border-emerald-200 rounded px-2 py-1' : 'px-2 py-1'}`}>
                <span className="text-[10px] font-semibold text-stone-400 w-4">{idx === 0 ? '★' : idx}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-stone-800">{n.name}</div>
                  <div className="text-[10px] text-stone-500 truncate">
                    {n.is_current ? 'This BOQ · ' : ''}
                    {n.cloned_at ? `Cloned by ${n.cloned_by_name || '—'} on ${new Date(n.cloned_at).toLocaleDateString()}` : (n.created_at ? `Created ${new Date(n.created_at).toLocaleDateString()}` : '')}
                  </div>
                </div>
                {!n.is_current && (
                  <a
                    href={`/boq-worksheets/${n.id}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-sky-700 hover:underline flex-shrink-0"
                    data-testid={`revision-chain-open-${idx}`}
                  >Open ↗</a>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

